import { useEffect, useState } from 'react';
import { Plus, Search, FileText, Calendar, User, Truck, ChevronRight, ChevronLeft, X, Eye, Download, ChevronDown, ChevronUp, Trash2, Send, Mail, Settings, CheckSquare, Square, Pencil } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { releaseStatementLoads } from '../../lib/releaseStatementLoads';
import { useToast } from '../../contexts/ToastContext';
import { fetchStoredBytes } from '../../utils/fileUrls';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Format a stored date without applying the viewer's timezone. For values that
// look like YYYY-MM-DD (with or without a trailing time component), parse the
// year/month/day from the string directly so the rendered date matches what's
// in the database. Falls back to UTC components for other inputs.
function fmtDate(
  value: string | Date | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (value === null || value === undefined || value === '') return '';
  let y: number | undefined;
  let mo: number | undefined;
  let d: number | undefined;
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) {
      y = parseInt(m[1], 10);
      mo = parseInt(m[2], 10);
      d = parseInt(m[3], 10);
    }
  }
  if (y === undefined) {
    const dt = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dt.getTime())) return '';
    y = dt.getUTCFullYear();
    mo = dt.getUTCMonth() + 1;
    d = dt.getUTCDate();
  }
  if (mo === undefined || d === undefined) return '';
  if (!opts) return `${mo}/${d}/${y}`;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}

// Statements are labeled with the Monday the work is paid "through": the first
// Monday on/after the period end (a Mon–Sun week labels the following Monday).
// UTC arithmetic keeps the day-of-week stable across viewer timezones.
function mondayThrough(weekEnd: string | Date): Date {
  const end = weekEnd instanceof Date ? new Date(weekEnd) : new Date(weekEnd);
  const daysUntilMonday = (8 - end.getUTCDay()) % 7;
  const monday = new Date(end);
  monday.setUTCDate(end.getUTCDate() + daysUntilMonday);
  return monday;
}

// Fixed display order for the Deductions table (preview + PDF), per accounting:
// cargo, truck rent, ELD equipment, ELD weekly, escrow, remaining scheduled
// types, company service fee, then manually added deductions in their original
// order. Matched by description keywords since that's all saved line items have.
function deductionSortRank(description: string | null | undefined): number {
  const d = (description || '').toLowerCase();
  if (d.includes('cargo')) return 0;
  if (d.includes('physical') || d.includes('phys.dem')) return 8; // before 'truck': legacy "Physical for Truck …"
  if (d.includes('truck')) return 1;
  if (d.includes('eld weekly')) return 3;
  if (d.includes('eld')) return 2;
  if (d.includes('escrow')) return 4;
  if (d.includes('trailer')) return 7;
  if (d.includes('prepass')) return 9;
  if (d.includes('occupational')) return 10;
  if (d.includes('highway')) return 11;
  if (d.includes('fuel card')) return 12;
  if (d.includes('service fee')) return 20;
  return 100;
}

// Cap on a hand-edited load description. The PDF's ORIGIN - DESTINATION column
// is the flexible one: ~80mm wide on company-driver statements (182mm table
// minus the fixed date/miles/amount columns), which fits roughly 38 ALL-CAPS
// characters per line at 9pt. 80 leaves room for about twice the length of a
// typical "CITY, ST - CITY, ST" default while still wrapping to at most a
// couple of lines, so the statement keeps fitting on one page.
const LOAD_DESCRIPTION_MAX_CHARS = 80;

interface Statement {
  id: string;
  driver_name: string;
  truck_number: string;
  week_start_date: string;
  week_end_date: string;
  total_revenue: number;
  total_deductions: number;
  total_credits: number;
  total_fuel: number;
  net_pay: number;
  contractor_name?: string | null;
  contractor_address?: string | null;
  created_at: string;
}

interface Load {
  id: string;
  load_number: string;
  pickup_date: string;
  delivery_date: string;
  pickup_location?: string;
  delivery_location?: string;
  pickup_city?: string;
  pickup_state?: string;
  delivery_city?: string;
  delivery_state?: string;
  total_miles?: number;
  empty_miles?: number;
  rate?: number;
  total_rate?: number;
  driver_id: string;
  status: string;
  sent_to_accounting?: boolean;
  billing_note?: string;
  invoice_sent?: boolean;
  trucks?: { unit_number: string };
  drivers?: { first_name: string; last_name: string };
  dispatches?: { driver_pay: number }[];
  included_in_statement?: boolean;
}

interface Deduction {
  id: string;
  driver_id: string;
  driver_name?: string;
  deduction_type: string;
  description: string;
  amount: number;
  charged_amount?: number;
  remaining_amount?: number;
  deduction_date: string;
  status: string;
  paid_date?: string;
  statement_id?: string;
  drivers?: { first_name: string; last_name: string };
}

interface ScheduledDeduction {
  id: string;
  driver_name: string;
  lease_or_rent: string;
  contractor_name?: string;
  contractor_address?: string;
  commission_cpm?: string;
  unit_number?: string;
  company?: string;
  truck_weekly: number;
  truck_monthly: number;
  truck_monthly_charge_day?: string;
  physical_weekly: number;
  physical_monthly: number;
  physical_monthly_charge_day?: string;
  trailer: number;
  cargo: number;
  escrow: number;
  fuel_card_fee: number;
  eld_equipment_fee: number;
  eld_weekly: number;
  eld_monthly: number;
  eld_monthly_charge_day?: string;
  prepass_weekly: number;
  prepass_monthly: number;
  prepass_monthly_charge_day?: string;
  occupational_weekly: number;
  occupational_monthly: number;
  occupational_monthly_charge_day?: string;
  highway_registration_weekly: number;
}

interface Credit {
  id: string;
  driver_name: string;
  amount: number;
  reason: string;
  date: string;
  status: string;
}

interface FuelTransaction {
  id: string;
  driver_name: string;
  location: string;
  gallons: number;
  amount: number;
  invoice_amount?: number;
  transaction_date: string;
  product?: string;
  city?: string;
  state?: string;
  date?: string;
}

// Derive how a statement should be rendered (preview modal + PDF) from its
// saved line items. Newly generated statements explicitly mark percentage
// commissions with a `company_service_fee` deduction line and a
// `commission_type` flag on each load. Older statements were saved with
// commission already baked into each trip's `amount` and no service-fee row,
// so we recover the percentage shape with a heuristic: if every load's
// (amount/freight) ratio matches within a tight tolerance, it was percentage
// and we synthesize the missing service-fee row at render time.
type LineItem = {
  id?: string;
  item_type: string;
  description?: string;
  date?: string;
  amount: number;
  quantity?: number | null;
  metadata?: any;
};

type ServiceFeeRow = {
  id: string;
  item_type: 'deduction';
  description: string;
  date: string;
  amount: number;
  metadata: { kind: 'company_service_fee'; commission_percent: number; synthesized: true };
};

// Per-mile rate for company-driver (CPM) statements: the commission_value
// stamped on load line items at creation (from the scheduled deduction), else
// the amount/miles ratio when it's consistent across every load (legacy
// statements without the stamp). Null when neither yields a rate.
function cpmRateFromLoads(loads: LineItem[]): number | null {
  for (const item of loads) {
    const v = Number(item.metadata?.commission_value);
    if (item.metadata?.commission_type === 'cpm' && Number.isFinite(v) && v > 0) {
      return v >= 10 ? v / 100 : v;
    }
  }
  const ratios = loads
    .map(item => ((item.quantity || 0) > 0 ? item.amount / (item.quantity as number) : null))
    .filter((r): r is number => r !== null && r > 0);
  if (ratios.length === loads.length && ratios.length > 0) {
    const min = Math.min(...ratios);
    const max = Math.max(...ratios);
    if (max - min < 0.005) return Math.round(((min + max) / 2) * 100) / 100;
  }
  return null;
}

function deriveStatementView(
  statement: { week_end_date: string; total_revenue: number; total_deductions: number },
  lineItems: LineItem[],
) {
  const loads = lineItems.filter(i => i.item_type === 'load');
  const deductions = lineItems.filter(i => i.item_type === 'deduction');

  const explicitFee = deductions.find(d => d.metadata?.kind === 'company_service_fee');
  const hasExplicitMarker = explicitFee !== undefined
    || loads.some(l => l.metadata?.commission_type === 'percentage');
  // Loads stamped 'cpm' were paid per-mile — their amount/freight ratio is
  // coincidental, so the percentage heuristic below must not run on them.
  const hasExplicitCpmMarker = loads.some(l => l.metadata?.commission_type === 'cpm');

  // Heuristic for legacy statements: consistent (amount/freight) ratio across
  // all loads with freight > amount → percentage commission was applied.
  let inferredFee: ServiceFeeRow | null = null;
  if (!hasExplicitMarker && !hasExplicitCpmMarker && loads.length > 0) {
    const ratios = loads
      .map(l => {
        const freight = Number(l.metadata?.freight_amount) || 0;
        return freight > 0 ? l.amount / freight : null;
      })
      .filter((r): r is number => r !== null && r > 0 && r < 1);
    if (ratios.length === loads.length && ratios.length > 0) {
      const min = Math.min(...ratios);
      const max = Math.max(...ratios);
      if (max - min < 0.01) {
        const avgRatio = ratios.reduce((s, r) => s + r, 0) / ratios.length;
        const commissionPercent = Math.round((1 - avgRatio) * 100 * 100) / 100;
        const totalFreight = loads.reduce((s, l) => s + (Number(l.metadata?.freight_amount) || 0), 0);
        const totalDriver = loads.reduce((s, l) => s + l.amount, 0);
        const fee = totalFreight - totalDriver;
        if (fee > 0) {
          inferredFee = {
            id: '__synth_company_service_fee',
            item_type: 'deduction',
            description: `Company service fee ${commissionPercent}%`,
            date: statement.week_end_date,
            amount: -fee,
            metadata: { kind: 'company_service_fee', commission_percent: commissionPercent, synthesized: true },
          };
        }
      }
    }
  }

  const isPercentageStmt = explicitFee !== undefined
    || loads.some(l => l.metadata?.commission_type === 'percentage')
    || inferredFee !== null;

  // The last column always carries the load's rate amount — the freight amount on
  // percentage statements, the calculated driver amount otherwise — so it reads
  // "Amount" for every statement type.
  const lastColLabel = 'Amount';
  const lastColValue = (item: LineItem): number =>
    isPercentageStmt ? Number(item.metadata?.freight_amount ?? item.amount) : item.amount;

  // Effective deductions: existing rows plus any synthesized fee.
  const effectiveDeductions: LineItem[] = inferredFee ? [...deductions, inferredFee] : deductions;

  // Display totals: for legacy percentage statements the saved total_revenue
  // is driver-net; we re-roll it to match the new shape so the totals card
  // reads naturally alongside the synthesized fee.
  const displayTotalRevenue = isPercentageStmt
    ? loads.reduce((s, l) => s + lastColValue(l), 0)
    : statement.total_revenue;
  const displayTotalDeductions = isPercentageStmt && inferredFee
    ? statement.total_deductions + Math.abs(inferredFee.amount)
    : statement.total_deductions;

  return {
    isPercentageStmt,
    lastColLabel,
    lastColValue,
    effectiveDeductions,
    displayTotalRevenue,
    displayTotalDeductions,
    inferredFee,
  };
}

export function StatementsView() {
  const toast = useToast();
  const [statements, setStatements] = useState<Statement[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statementToDelete, setStatementToDelete] = useState<Statement | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewStatement, setPreviewStatement] = useState<Statement | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewLineItems, setPreviewLineItems] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [driverTypeFilter, setDriverTypeFilter] = useState<'all' | 'company_driver' | 'contractor' | 'owner'>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'by_driver' | 'by_week'>('by_driver');
  // Weeks default to expanded; storing the collapsed keys keeps the toggle stateless
  // for never-clicked weeks.
  const [collapsedWeeks, setCollapsedWeeks] = useState<Set<string>>(new Set());
  const [selectedStatements, setSelectedStatements] = useState<Set<string>>(new Set());
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  // Persisted in localStorage for now — review recipient address used by "Send to Review".
  const [reviewEmail, setReviewEmail] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('tms_review_email') || '';
  });
  const [draftReviewEmail, setDraftReviewEmail] = useState('');
  // driver_name (lowercased) → latest scheduled-deduction info used to bucket statements
  // and surface contractor details on pre-migration statements (which have no snapshot).
  const [driverLeaseTypeMap, setDriverLeaseTypeMap] = useState<Record<string, {
    lease_or_rent: string;
    contractor_name?: string;
    contractor_address?: string;
  }>>({});
  // statement_id → lease_or_rent of the scheduled deduction actually used on that statement.
  // Authoritative for filter buckets — beats the per-driver fallback when a driver has
  // multiple scheduled deductions (e.g. a Contractor + a Company Driver record).
  const [statementLeaseMap, setStatementLeaseMap] = useState<Record<string, string>>({});
  // statement_id → load commission_type ('cpm' | 'percentage'). Lets a company-driver
  // statement (cpm) be identified even when it recorded zero scheduled deductions.
  const [statementCommissionTypeMap, setStatementCommissionTypeMap] = useState<Record<string, string>>({});
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  // In-flight guard: generation inserts the statement then renders/uploads a
  // PDF (several seconds) — a second click during that window would save an
  // identical duplicate statement.
  const [isGeneratingStatement, setIsGeneratingStatement] = useState(false);
  const [showPastStatementModal, setShowPastStatementModal] = useState(false);
  const [customWeekStart, setCustomWeekStart] = useState('');
  const [customWeekEnd, setCustomWeekEnd] = useState('');
  // Past-statement flow: the statement's truck is derived from the trucks that
  // actually drove the selected loads (all distinct units, comma-separated)
  // instead of the profile/current-assignment unit used for regular statements.
  const [isPastStatementFlow, setIsPastStatementFlow] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [driverSearchTerm, setDriverSearchTerm] = useState('');
  const [deductionSearchTerm, setDeductionSearchTerm] = useState('');

  // Form data for statement generation
  const [selectedDriver, setSelectedDriver] = useState('');
  // Team statements: when a team is picked, selectedTeamId/Name are set and
  // selectedDriver holds the combined team label (which also matches the team's
  // scheduled_deduction.driver_name so the name-keyed loaders keep working).
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedTeamName, setSelectedTeamName] = useState('');
  const [teams, setTeams] = useState<Array<{ id: string; label: string; memberIds: string[]; truckNumber: string | null }>>([]);
  const [selectedTruck, setSelectedTruck] = useState('');
  // Unit typed by the user at Step 8. null = no override, i.e. show the
  // driver's current truck assignment. Applies to one statement only.
  const [truckNumberOverride, setTruckNumberOverride] = useState<string | null>(null);
  const [weekStartDate, setWeekStartDate] = useState('');
  const [weekEndDate, setWeekEndDate] = useState('');
  const [selectedLoads, setSelectedLoads] = useState<string[]>([]);
  const [selectedDeductions, setSelectedDeductions] = useState<string[]>([]);
  const [selectedMaintenanceDeductions, setSelectedMaintenanceDeductions] = useState<string[]>([]);
  const [selectedScheduledDeductions, setSelectedScheduledDeductions] = useState<string[]>([]);
  const [selectedCredits, setSelectedCredits] = useState<string[]>([]);
  const [selectedFuel, setSelectedFuel] = useState<string[]>([]);
  const [editedMileage, setEditedMileage] = useState<Record<string, number>>({});
  const [editedLoadedMiles, setEditedLoadedMiles] = useState<Record<string, number>>({});
  const [editedEmptyMiles, setEditedEmptyMiles] = useState<Record<string, number>>({});
  const [editedFreightAmounts, setEditedFreightAmounts] = useState<Record<string, number>>({});
  const [editedDescription, setEditedDescription] = useState<Record<string, string>>({});
  const [editedDeductionDescriptions, setEditedDeductionDescriptions] = useState<Record<string, string>>({});
  const [editedScheduledDescriptions, setEditedScheduledDescriptions] = useState<Record<string, string>>({});
  const [editedCreditDescriptions, setEditedCreditDescriptions] = useState<Record<string, string>>({});
  const [driverCommission, setDriverCommission] = useState<string>('');
  const [maintenanceChargeAmounts, setMaintenanceChargeAmounts] = useState<Record<string, number>>({});
  const [deductionChargeAmounts, setDeductionChargeAmounts] = useState<Record<string, number>>({});
  
  // Add Deduction Modal
  const [showAddDeductionModal, setShowAddDeductionModal] = useState(false);
  const [newDeductionType, setNewDeductionType] = useState('');
  const [newDeductionAmount, setNewDeductionAmount] = useState('');
  const [newDeductionDate, setNewDeductionDate] = useState(new Date().toISOString().split('T')[0]);
  const [addingDeduction, setAddingDeduction] = useState(false);
  // Raw driver_deductions.id being edited through the Add Deduction form, or
  // null when the form is adding a new one.
  const [editingDeductionId, setEditingDeductionId] = useState<string | null>(null);
  
  // Add Credit Modal
  const [showAddCreditModal, setShowAddCreditModal] = useState(false);
  const [newCreditReason, setNewCreditReason] = useState('');
  const [newCreditAmount, setNewCreditAmount] = useState('');
  const [newCreditDate, setNewCreditDate] = useState(new Date().toISOString().split('T')[0]);
  const [newCreditStatus, setNewCreditStatus] = useState('pending');
  const [addingCredit, setAddingCredit] = useState(false);
  
  // Data for each step
  const [drivers, setDrivers] = useState<any[]>([]);
  // All companies (with logo/address/MC/DOT) so a statement's PDF header can be
  // rendered for the company on the driver's scheduled-deduction profile, which
  // may differ from the company the driver record itself is assigned to.
  const [companies, setCompanies] = useState<any[]>([]);
  const [availableLoads, setAvailableLoads] = useState<Load[]>([]);
  const [availableDeductions, setAvailableDeductions] = useState<Deduction[]>([]);
  const [availableMaintenanceDeductions, setAvailableMaintenanceDeductions] = useState<any[]>([]);
  const [availableScheduledDeductions, setAvailableScheduledDeductions] = useState<ScheduledDeduction[]>([]);
  const [availableCredits, setAvailableCredits] = useState<Credit[]>([]);
  const [fuelData, setFuelData] = useState<FuelTransaction[]>([]);

  // Step 3 labelling for dual-profile drivers. A driver with both a company-driver
  // and an owner/contractor scheduled deduction gets ONE statement per profile, so
  // their loads deliberately stay visible after the first statement is generated.
  // This map exists purely to LABEL those loads — it never hides anything.
  // load id -> the statements already billing that load. Statement numbers are
  // generated at PDF time (Math.random) and never persisted, so a statement is
  // identified here by its week plus the pay mode it used.
  // Step 3 reveal toggle for loads hidden by the fully-billed rule.
  const [showBilledLoads, setShowBilledLoads] = useState(false);
  const [loadStatementRefs, setLoadStatementRefs] = useState<
    Record<string, { statementId: string | null; payMode: string | null; weekEnd: string | null }[]>
  >({});

  // Get the correct fuel amount based on the pay profile selected in Step 2:
  // - Company drivers: invoice_amount (lower discounted price)
  // - Owner / lease / rent / contractor: retail amount — EXCEPT trucks running
  //   under a fuel-discount company (Tara Express, Beetle Express LLC), whose
  //   owners are passed the discounted invoice price. The profile's Company
  //   field decides this.
  // The driver record's driver_type is only a fallback for statements
  // generated without a profile selected.
  const FUEL_DISCOUNT_COMPANIES = ['tara express', 'beetle express'];
  // Decide whether the selected Step-2 profile is entitled to the discounted
  // (invoice) fuel price. Returns the reason so the UI can show it.
  const getFuelPricingMode = (): { discounted: boolean; reason: string } => {
    const selectedSd = availableScheduledDeductions.find(sd => selectedScheduledDeductions.includes(sd.id));
    const profileType = (selectedSd?.lease_or_rent || '').toLowerCase();
    // The discount company can be recorded either as the profile's Company or
    // as its Pay-To Company Name (contractor_name) — e.g. owners paid to
    // "Tara Express" / "Beetle Express LLC" have it in the pay-to field, not the
    // Company dropdown.
    const profileCompany = `${selectedSd?.company || ''} ${selectedSd?.contractor_name || ''}`.toLowerCase();
    const matchedCompany = FUEL_DISCOUNT_COMPANIES.find(c => profileCompany.includes(c));

    if (profileType === 'company_driver') {
      return { discounted: true, reason: 'Company driver profile' };
    }
    if (['owner', 'lease', 'rent', 'contractor'].includes(profileType)) {
      return matchedCompany
        ? { discounted: true, reason: `Profile pays to ${selectedSd?.contractor_name || selectedSd?.company}` }
        : { discounted: false, reason: `${profileType} profile with no fuel-discount company` };
    }
    const driver = drivers.find(
      d => `${d.first_name} ${d.last_name}`.toLowerCase() === (selectedDriver || '').toLowerCase()
    );
    const isCompanyDriver = (driver?.driver_type || 'company_driver') === 'company_driver';
    return { discounted: isCompanyDriver, reason: 'No profile selected — using driver type' };
  };

  // A fuel row imported without a discounted (invoice) amount. When the
  // discounted price applies, these rows silently fall back to retail — the
  // Step 3 UI flags them so a broken import is visible before the statement
  // is generated.
  const fuelMissingInvoice = (fuel: FuelTransaction): boolean =>
    !fuel.invoice_amount || Number(fuel.invoice_amount) === 0;

  const getFuelAmount = (fuel: FuelTransaction): number => {
    const { discounted } = getFuelPricingMode();
    return discounted ? (fuel.invoice_amount || fuel.amount) : fuel.amount;
  };

  // Calculate total for a scheduled deduction (weekly + applicable monthly charges)
  const getScheduledDeductionTotal = (sd: ScheduledDeduction): number => {
    let total = sd.truck_weekly + sd.physical_weekly + sd.trailer + sd.cargo + (sd.escrow || 0) + sd.fuel_card_fee +
      (sd.eld_equipment_fee || 0) + sd.eld_weekly + sd.prepass_weekly + sd.occupational_weekly + sd.highway_registration_weekly;

    // Check which week of month for monthly charges
    const checkMonthly = (chargeDay: string | undefined) => {
      if (!chargeDay) return false;
      const today = new Date();
      const dayOfWeek = today.getDay();
      let fridayThisWeek: Date;
      if (dayOfWeek === 6) fridayThisWeek = new Date(today.getTime() - 86400000);
      else if (dayOfWeek === 5) fridayThisWeek = today;
      else if (dayOfWeek < 5) fridayThisWeek = new Date(today.getTime() + (5 - dayOfWeek) * 86400000);
      else fridayThisWeek = new Date(today.getTime() + 5 * 86400000);
      const fridayDate = fridayThisWeek.getDate();
      const weekOfMonth = Math.ceil(fridayDate / 7);
      const chargeWeek = chargeDay === '1st Friday' ? 1 : chargeDay === '2nd Friday' ? 2 :
        chargeDay === '3rd Friday' ? 3 : chargeDay === '4th Friday' ? 4 : 0;
      return weekOfMonth === chargeWeek;
    };

    if (sd.truck_monthly > 0 && checkMonthly(sd.truck_monthly_charge_day)) total += sd.truck_monthly;
    if (sd.physical_monthly > 0 && checkMonthly(sd.physical_monthly_charge_day)) total += sd.physical_monthly;
    if (sd.occupational_monthly > 0 && checkMonthly(sd.occupational_monthly_charge_day)) total += sd.occupational_monthly;
    if (sd.eld_monthly > 0 && checkMonthly(sd.eld_monthly_charge_day)) total += sd.eld_monthly;
    if (sd.prepass_monthly > 0 && checkMonthly(sd.prepass_monthly_charge_day)) total += sd.prepass_monthly;
    return total;
  };

  useEffect(() => {
    loadStatements();
    loadDrivers();
    loadCompanies();
    loadDriverLeaseTypes();
    loadTeams();
  }, []);

  async function loadCompanies() {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, code, logo_url, address, city, state, zip_code, phone, email, mc_number, dot_number')
        .order('name');
      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  // Which company a statement is issued under. The scheduled-deduction profile
  // selected at Step 2 carries a Company (e.g. "CS National LLC"); that wins over
  // the company on the driver record, which can differ (a driver assigned to DN
  // Expedite but paid under a CS National profile). Profile ids come from the
  // saved scheduled_deduction line items (item_id), so past statements resolve the
  // same way as freshly generated ones; `profileIds` covers a new statement whose
  // profile produced no deduction rows. Falls back to the driver's company.
  const resolveStatementCompany = async (statement: Statement, lineItems: any[], profileIds: string[] = []): Promise<any> => {
    const driver = drivers.find(d => `${d.first_name} ${d.last_name}` === statement.driver_name);
    const driverCompany = driver?.companies || {};

    const sdIds = Array.from(new Set([
      ...profileIds,
      ...lineItems.filter(li => li.item_type === 'scheduled_deduction' && li.item_id).map(li => li.item_id as string),
    ]));
    if (sdIds.length === 0 || companies.length === 0) return driverCompany;

    let profileCompanyName: string | undefined =
      availableScheduledDeductions.find(sd => sdIds.includes(sd.id) && sd.company)?.company;
    if (!profileCompanyName) {
      try {
        const { data } = await supabase
          .from('scheduled_deductions')
          .select('id, company')
          .in('id', sdIds);
        profileCompanyName = (data || []).find((sd: any) => sd.company)?.company;
      } catch (e) {
        console.warn('Could not resolve statement profile company:', e);
      }
    }
    if (!profileCompanyName) return driverCompany;

    const key = profileCompanyName.trim().toLowerCase();
    const match = companies.find(c =>
      (c.name || '').trim().toLowerCase() === key || (c.code || '').trim().toLowerCase() === key
    );
    return match || driverCompany;
  };

  async function loadTeams() {
    try {
      // Active teams + members + the team's current truck (from either member's
      // current equipment assignment). Fails gracefully if team tables are absent.
      const { data, error } = await supabase
        .from('driver_teams')
        .select('id, name, driver_team_members(driver_id, created_at, drivers(first_name, last_name, equipment_assignments(is_current, trucks(unit_number))))')
        .eq('is_active', true)
        .order('name');
      if (error) { setTeams([]); return; }
      const mapped = (data || []).map((t: any) => {
        const members = [...(t.driver_team_members || [])].sort(
          (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        const label = members
          .map((m: any) => m.drivers ? `${m.drivers.first_name} ${m.drivers.last_name}` : '')
          .filter(Boolean)
          .join(' / ');
        let truckNumber: string | null = null;
        for (const m of members) {
          const ca = (m.drivers?.equipment_assignments || []).find((a: any) => a.is_current);
          if (ca?.trucks?.unit_number) { truckNumber = ca.trucks.unit_number; break; }
        }
        return { id: t.id, label, memberIds: members.map((m: any) => m.driver_id), truckNumber };
      });
      setTeams(mapped);
    } catch (e) {
      console.error('Error loading teams:', e);
      setTeams([]);
    }
  }

  // Build a driver_name → lease_or_rent map from scheduled_deductions so we can
  // bucket existing (pre-migration) statements as Contractor/Company/Owner.
  const loadDriverLeaseTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('scheduled_deductions')
        .select('driver_name, lease_or_rent, contractor_name, contractor_address, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const map: Record<string, { lease_or_rent: string; contractor_name?: string; contractor_address?: string }> = {};
      (data || []).forEach((row: any) => {
        const key = (row.driver_name || '').trim().toLowerCase();
        if (!key || !row.lease_or_rent) return;
        // Most recent row wins (data is ordered desc).
        if (!map[key]) {
          map[key] = {
            lease_or_rent: row.lease_or_rent,
            contractor_name: row.contractor_name || undefined,
            contractor_address: row.contractor_address || undefined,
          };
        }
      });
      setDriverLeaseTypeMap(map);
    } catch (err) {
      console.error('Failed to load driver lease types:', err);
    }
  };

  // Toggle a statement's selection by id.
  const toggleStatementSelection = (id: string) => {
    setSelectedStatements(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Toggle every statement in a group (week or driver). When all are already
  // selected, deselect them; otherwise add them all.
  const toggleGroupSelection = (ids: string[]) => {
    setSelectedStatements(prev => {
      const next = new Set(prev);
      const allSelected = ids.every(id => next.has(id));
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  // Send-to-Review: ships the selected PDFs to the configured review address.
  // The actual delivery requires a backend (Supabase Edge Function or similar)
  // — until that's wired, this surfaces what would be sent so the UI is testable.
  const handleSendToReview = async () => {
    if (selectedStatements.size === 0) return;
    if (!reviewEmail) {
      toast.error('Set a review email first (gear icon → Email Settings).');
      return;
    }
    const count = selectedStatements.size;
    toast.info(`Queuing ${count} statement${count === 1 ? '' : 's'} for review at ${reviewEmail} (backend wiring pending).`);
    // TODO: invoke an Edge Function that fetches each statement's PDF and emails it.
  };

  // Send-to-Drivers: emails each selected statement to its driver. Requires
  // drivers.email to be populated; same backend caveat as Send to Review.
  const handleSendToDrivers = async () => {
    if (selectedStatements.size === 0) return;
    const count = selectedStatements.size;
    toast.info(`Queuing ${count} statement${count === 1 ? '' : 's'} to drivers (backend wiring pending).`);
    // TODO: for each selected statement_id → look up driver.email → email PDF.
  };

  const saveReviewEmail = () => {
    const email = draftReviewEmail.trim();
    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Enter a valid email address.');
      return;
    }
    setReviewEmail(email);
    if (typeof window !== 'undefined') {
      if (email) localStorage.setItem('tms_review_email', email);
      else localStorage.removeItem('tms_review_email');
    }
    setShowEmailSettings(false);
    toast.success('Email settings saved.');
  };

  // For each statement, find the scheduled_deduction it actually used (via line items)
  // and record that scheduled_deduction's lease_or_rent. This lets the bucket filter
  // work correctly when a driver has multiple scheduled deductions of different types.
  const loadStatementLeaseMap = async () => {
    try {
      // Paged: both line-item queries span every statement ever generated and
      // passed PostgREST's 1000-row cap. Truncated, the newest statements went
      // missing from these maps and fell back to the driver's most recent
      // profile — a company-driver statement got bucketed (and printed) as Owner.
      const items = await fetchAllRows(() =>
        supabase
          .from('statement_line_items')
          .select('id, statement_id, item_id')
          .eq('item_type', 'scheduled_deduction')
      );
      const sdIds = [...new Set(items.map((i: any) => i.item_id).filter(Boolean))];
      const sdLeaseMap: Record<string, string> = {};
      for (let i = 0; i < sdIds.length; i += 100) {
        const { data: sds, error: sdError } = await supabase
          .from('scheduled_deductions')
          .select('id, lease_or_rent')
          .in('id', sdIds.slice(i, i + 100));
        if (sdError) throw sdError;
        (sds || []).forEach((sd: any) => {
          if (sd.id && sd.lease_or_rent) sdLeaseMap[sd.id] = sd.lease_or_rent;
        });
      }
      const map: Record<string, string> = {};
      items.forEach((item: any) => {
        if (!item.statement_id || !item.item_id || map[item.statement_id]) return;
        const lease = sdLeaseMap[item.item_id];
        if (lease) map[item.statement_id] = lease;
      });
      setStatementLeaseMap(map);

      // Also record each statement's load commission_type ('cpm' | 'percentage').
      // A company-driver statement is generated by selecting the company profile,
      // which stamps loads as 'cpm' — but that profile often has $0 deductions and
      // so saves no scheduled_deduction row. commission_type recovers the selection.
      // Only the commission_type key is pulled out of metadata — this query
      // pages through every load row ever put on a statement.
      const loadItems = await fetchAllRows(() =>
        supabase
          .from('statement_line_items')
          .select('id, statement_id, commission_type:metadata->>commission_type')
          .eq('item_type', 'load')
      );
      const ctMap: Record<string, string> = {};
      loadItems.forEach((item: any) => {
        const ct = item?.commission_type;
        if (!item.statement_id || !ct || ctMap[item.statement_id]) return;
        ctMap[item.statement_id] = ct;
      });
      setStatementCommissionTypeMap(ctMap);
    } catch (err) {
      console.error('Failed to load statement lease map:', err);
    }
  };

  const handleDeleteStatement = async () => {
    if (!statementToDelete) return;

    try {
      // First, fetch the statement to get document_id
      const { data: statementDetails, error: statementFetchError } = await supabase
        .from('driver_statements')
        .select('document_id')
        .eq('id', statementToDelete.id)
        .single();

      if (statementFetchError) throw statementFetchError;

      // If there's a linked document, delete it from storage and database
      if (statementDetails?.document_id) {
        try {
          // Get document details
          const { data: documentData, error: docFetchError } = await supabase
            .from('documents')
            .select('file_path')
            .eq('id', statementDetails.document_id)
            .single();

          if (docFetchError) {
            console.error('Error fetching document:', docFetchError);
          } else if (documentData?.file_path) {
            // Delete from storage
            const { error: storageError } = await supabase.storage
              .from('company-documents')
              .remove([documentData.file_path]);

            if (storageError) {
              console.error('Error deleting file from storage:', storageError);
            }

            // Delete document record
            const { error: docDeleteError } = await supabase
              .from('documents')
              .delete()
              .eq('id', statementDetails.document_id);

            if (docDeleteError) {
              console.error('Error deleting document record:', docDeleteError);
            }
          }
        } catch (docError) {
          console.error('Error during document deletion:', docError);
          // Continue with statement deletion even if document deletion fails
        }
      }

      // Fetch the line items — still needed below to reverse deductions, credits
      // and fuel markers.
      const { data: lineItems, error: fetchError } = await supabase
        .from('statement_line_items')
        .select('*')
        .eq('statement_id', statementToDelete.id);

      if (fetchError) throw fetchError;

      // Release this statement's loads for re-billing. Shared with the 1099 delete
      // screens so all three paths behave identically. Runs BEFORE the delete
      // because statement_line_items cascades away with the statement, and it
      // deliberately keeps a load marked when the driver's OTHER profile statement
      // still bills it (dual-profile drivers legitimately have a load on both).
      const releasedCount = await releaseStatementLoads([statementToDelete.id]);
      console.log('Released', releasedCount, 'loads for re-billing');

      // Clear statement_id from fuel transactions
      const { error: fuelError } = await supabase
        .from('pilot_fuel_imports')
        .update({ statement_id: null })
        .eq('statement_id', statementToDelete.id);
      
      if (fuelError) {
        console.error('Error clearing fuel transaction markers:', fuelError);
        // Don't throw - this is not critical
      }

      // Restore custom deductions (reverse charged amounts)
      const deductionLineItems = lineItems
        ?.filter(item => item.item_type === 'deduction' && item.item_id?.length === 36) || []; // UUIDs are 36 chars
      
      if (deductionLineItems.length > 0) {
        // Process each deduction individually to restore amounts
        for (const lineItem of deductionLineItems) {
          // Get current deduction state
          const { data: deduction, error: fetchError } = await supabase
            .from('driver_deductions')
            .select('amount, charged_amount, remaining_amount')
            .eq('id', lineItem.item_id)
            .single();
          
          if (fetchError) {
            console.error('Error fetching deduction:', fetchError);
            continue;
          }
          
          if (deduction) {
            // Calculate the amount that was charged in this statement
            const chargedInThisStatement = Math.abs(lineItem.amount || 0);
            const newChargedAmount = Math.max(0, (deduction.charged_amount || 0) - chargedInThisStatement);
            const newRemainingAmount = deduction.amount - newChargedAmount;
            
            // Update deduction with restored amounts
            const updateData: any = {
              charged_amount: newChargedAmount,
              remaining_amount: newRemainingAmount,
              statement_id: null
            };
            
            // If there's still a remaining amount, mark as pending
            if (newRemainingAmount > 0) {
              updateData.status = 'pending';
              updateData.paid_date = null;
            }
            
            const { error: updateError } = await supabase
              .from('driver_deductions')
              .update(updateData)
              .eq('id', lineItem.item_id);
            
            if (updateError) {
              console.error('Error restoring deduction:', updateError);
            }
          }
        }
      }

      // Unmark hotel reimbursements
      const hotelIds = lineItems
        ?.filter(item => item.item_type === 'deduction')
        .map(item => item.item_id) || [];
      
      if (hotelIds.length > 0) {
        const { error: hotelError } = await supabase
          .from('hotel_reimbursements')
          .update({ driver_paid: false })
          .in('id', hotelIds);
        
        // Ignore error if no records found (might be custom deductions)
        if (hotelError && hotelError.code !== 'PGRST116') {
          console.log('Hotel reimbursements update (may be expected):', hotelError);
        }
      }

      // Unmark lyft reimbursements
      if (hotelIds.length > 0) {
        const { error: lyftError } = await supabase
          .from('lyft_reimbursements')
          .update({ driver_paid: false })
          .in('id', hotelIds);
        
        if (lyftError && lyftError.code !== 'PGRST116') {
          console.log('Lyft reimbursements update (may be expected):', lyftError);
        }
      }

      // Unmark airplane reimbursements
      if (hotelIds.length > 0) {
        const { error: airplaneError } = await supabase
          .from('airplane_reimbursements')
          .update({ driver_paid: false })
          .in('id', hotelIds);
        
        if (airplaneError && airplaneError.code !== 'PGRST116') {
          console.log('Airplane reimbursements update (may be expected):', airplaneError);
        }
      }

      // Unmark credits
      const creditIds = lineItems
        ?.filter(item => item.item_type === 'credit' && item.item_id)
        .map(item => item.item_id)
        .filter(id => id !== null && id !== undefined) || [];
      
      if (creditIds.length > 0) {
        const { error: creditsError } = await supabase
          .from('driver_credits')
          .update({ status: 'pending', paid_date: null, statement_id: null })
          .in('id', creditIds);
        
        if (creditsError) throw creditsError;
      }

      // Restore maintenance deductions (reverse charged amounts)
      const maintenanceLineItems = lineItems
        ?.filter(item => item.item_type === 'maintenance_deduction' && item.item_id) || [];
      
      if (maintenanceLineItems.length > 0) {
        // Process each maintenance deduction individually to restore amounts
        for (const lineItem of maintenanceLineItems) {
          // Get current maintenance deduction state
          const { data: maintenance, error: fetchError } = await supabase
            .from('maintenance_logs')
            .select('driver_payment_amount, driver_charged_amount, driver_remaining_amount')
            .eq('id', lineItem.item_id)
            .single();
          
          if (fetchError) {
            console.error('Error fetching maintenance deduction:', fetchError);
            continue;
          }
          
          if (maintenance) {
            // Calculate the amount that was charged in this statement
            const chargedInThisStatement = Math.abs(lineItem.amount || 0);
            const newChargedAmount = Math.max(0, (maintenance.driver_charged_amount || 0) - chargedInThisStatement);
            const newRemainingAmount = maintenance.driver_payment_amount - newChargedAmount;
            
            // Update maintenance with restored amounts
            const updateData: any = {
              driver_charged_amount: newChargedAmount,
              driver_remaining_amount: newRemainingAmount
            };
            
            // If there's still a remaining amount, mark as unpaid
            if (newRemainingAmount > 0) {
              updateData.driver_payment_status = 'unpaid';
            }
            
            const { error: updateError } = await supabase
              .from('maintenance_logs')
              .update(updateData)
              .eq('id', lineItem.item_id);
            
            if (updateError) {
              console.error('Error restoring maintenance deduction:', updateError);
            }
          }
        }
      }

      // Delete statement line items
      const { error: lineItemsError } = await supabase
        .from('statement_line_items')
        .delete()
        .eq('statement_id', statementToDelete.id);

      if (lineItemsError) throw lineItemsError;

      // Delete the statement
      const { error: statementError } = await supabase
        .from('driver_statements')
        .delete()
        .eq('id', statementToDelete.id);

      if (statementError) throw statementError;

      toast.success('Statement deleted successfully - loads and deductions are now available again');
      setShowDeleteModal(false);
      setStatementToDelete(null);
      loadStatements();
    } catch (error) {
      console.error('Error deleting statement:', error);
      toast.error('Failed to delete statement');
    }
  };

  // Statement load rows are dated by PICKUP. Statements generated before that
  // rule stored the DELIVERY date, so before any display or PDF we re-resolve
  // each load row's date from the load itself (first shipper stop, else the
  // load's pickup_date). Read-only; the stored row is left as is. On any
  // failure the stored date is shown, so this can never block a statement.
  type StatementLoadRow = { item_type: string; item_id?: string | null; date?: string | null };
  type PickupLookupLoad = {
    id: string;
    pickup_date: string | null;
    load_stops: { stop_type: string | null; stop_order: number | null; date: string | null }[] | null;
  };
  const withLoadPickupDates = async <T extends StatementLoadRow>(lineItems: T[]): Promise<T[]> => {
    const loadIds = Array.from(new Set(
      lineItems.filter(i => i.item_type === 'load' && i.item_id).map(i => i.item_id as string)
    ));
    if (loadIds.length === 0) return lineItems;
    try {
      const pickupById: Record<string, string> = {};
      for (let i = 0; i < loadIds.length; i += 100) {
        const { data, error } = await supabase
          .from('loads')
          .select('id, pickup_date, load_stops!load_stops_load_id_fkey(stop_type, stop_order, date)')
          .in('id', loadIds.slice(i, i + 100));
        if (error) throw error;
        ((data || []) as unknown as PickupLookupLoad[]).forEach((load) => {
          let pickupDate: string | null = load.pickup_date || null;
          const stops = [...(load.load_stops || [])].sort((a, b) => (a.stop_order || 0) - (b.stop_order || 0));
          const pickupStop = stops.find((st) => st.stop_type === 'shipper');
          if (pickupStop?.date) pickupDate = pickupStop.date;
          if (pickupDate) pickupById[load.id] = pickupDate;
        });
      }
      return lineItems.map(item =>
        item.item_type === 'load' && item.item_id && pickupById[item.item_id]
          ? { ...item, date: pickupById[item.item_id] }
          : item
      );
    } catch (err) {
      console.error('Could not resolve load pickup dates for statement; showing stored dates', err);
      return lineItems;
    }
  };

  const handlePreviewStatement = async (statement: Statement) => {
    setPreviewStatement(statement);
    setShowPreviewModal(true);
    setPreviewLoading(true);
    
    try {
      // Fetch statement line items
      const { data: lineItems, error } = await supabase
        .from('statement_line_items')
        .select('*')
        .eq('statement_id', statement.id)
        .order('item_type');

      if (error) throw error;
      
      // Store line items in state (we'll need to add this state)
      setPreviewLineItems(await withLoadPickupDates(lineItems || []));
    } catch (error) {
      console.error('Error loading statement details:', error);
      toast.error('Failed to load statement details');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Helper function to find or create folder structure: Statements 2026 -> Driver Name -> Month
  const findOrCreateStatementFolder = async (driverName: string, weekEndDate: string): Promise<string | null> => {
    try {
      const date = new Date(weekEndDate);
      const year = date.getFullYear();
      const monthName = date.toLocaleString('en-US', { month: 'long' });
      
      // Find or create "Statements {year}" folder
      let { data: yearFolder, error: yearError } = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', `Statements ${year}`)
        .is('parent_folder_id', null)
        .maybeSingle();

      if (yearError) throw yearError;
      
      if (!yearFolder) {
        const { data: newYearFolder, error: createYearError } = await supabase
          .from('document_folders')
          .insert({ name: `Statements ${year}`, parent_folder_id: null })
          .select('id')
          .single();
        
        if (createYearError) throw createYearError;
        yearFolder = newYearFolder;
      }
      
      // Find or create driver folder under year folder
      let { data: driverFolder, error: driverError } = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', driverName)
        .eq('parent_folder_id', yearFolder.id)
        .maybeSingle();

      if (driverError) throw driverError;
      
      if (!driverFolder) {
        const { data: newDriverFolder, error: createDriverError } = await supabase
          .from('document_folders')
          .insert({ name: driverName, parent_folder_id: yearFolder.id })
          .select('id')
          .single();
        
        if (createDriverError) throw createDriverError;
        driverFolder = newDriverFolder;
      }
      
      // Find or create month folder under driver folder
      let { data: monthFolder, error: monthError } = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', monthName)
        .eq('parent_folder_id', driverFolder.id)
        .maybeSingle();

      if (monthError) throw monthError;
      
      if (!monthFolder) {
        const { data: newMonthFolder, error: createMonthError } = await supabase
          .from('document_folders')
          .insert({ name: monthName, parent_folder_id: driverFolder.id })
          .select('id')
          .single();
        
        if (createMonthError) throw createMonthError;
        monthFolder = newMonthFolder;
      }
      
      return monthFolder.id;
    } catch (error) {
      console.error('Error creating folder structure:', error);
      return null;
    }
  };

  const handleDownloadStatement = async (statement: Statement) => {
    try {
      toast.info('Generating PDF...');
      
      // Fetch statement line items
      const { data: lineItems, error } = await supabase
        .from('statement_line_items')
        .select('*')
        .eq('statement_id', statement.id)
        .order('item_type');

      if (error) throw error;

      // Generate PDF
      await generateStatementPDF(statement, await withLoadPickupDates(lineItems || []), true);
      
      toast.success('Statement downloaded successfully!');
    } catch (error) {
      console.error('Error downloading statement:', error);
      toast.error('Failed to download statement');
    }
  };

  const generateStatementPDF = async (statement: Statement, lineItems: any[], download: boolean = false, profileIds: string[] = []): Promise<Blob | null> => {
    const doc = new jsPDF();
    
    // Group line items by type
    const loads = lineItems
      .filter(item => item.item_type === 'load')
      .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
    const deductions = lineItems.filter(item => item.item_type === 'deduction');
    const scheduledDeductions = lineItems.filter(item => item.item_type === 'scheduled_deduction');
    const credits = lineItems.filter(item => item.item_type === 'credit');
    const fuel = lineItems.filter(item => item.item_type === 'fuel');

    // Company the statement is issued under (scheduled-deduction profile's
    // company first, driver's company as fallback).
    const company = await resolveStatementCompany(statement, lineItems, profileIds);
    const companyName = company.name || 'Company Name';

    // Try to fetch company logo as a data URL (so jsPDF can embed it).
    let logo: { data: string; format: 'PNG' | 'JPEG'; w: number; h: number } | null = null;
    if (company.logo_url) {
      try {
        const blob = await fetchStoredBytes(company.logo_url);
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const i = new Image();
          i.onload = () => resolve(i);
          i.onerror = () => reject(new Error('logo load failed'));
          i.src = dataUrl;
        });
        const maxW = 42, maxH = 30;
        const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
        const format: 'PNG' | 'JPEG' =
          blob.type.includes('jpeg') || blob.type.includes('jpg') ? 'JPEG' : 'PNG';
        logo = {
          data: dataUrl,
          format,
          w: img.naturalWidth * ratio,
          h: img.naturalHeight * ratio,
        };
      } catch (e) {
        console.warn('Could not load company logo for PDF:', e);
      }
    }

    let yPos = 18;

    // Company logo + name + address block on left
    let leftHeaderY = yPos;
    if (logo) {
      doc.addImage(logo.data, logo.format, 14, leftHeaderY - 8, logo.w, logo.h);
      leftHeaderY += logo.h - 5;
    }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName, 14, leftHeaderY + 4);
    leftHeaderY += 4;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (company.address) {
      leftHeaderY += 4;
      doc.text(company.address, 14, leftHeaderY);
    }
    const cityLine = [company.city, company.state].filter(Boolean).join(', ');
    const cityStateZip = [cityLine, company.zip_code].filter(Boolean).join(' ');
    if (cityStateZip) {
      leftHeaderY += 4;
      doc.text(cityStateZip, 14, leftHeaderY);
    }
    const idLine = [
      company.mc_number ? `MC# ${company.mc_number}` : null,
      company.dot_number ? `DOT# ${company.dot_number}` : null,
    ].filter(Boolean).join('   ');
    if (idLine) {
      leftHeaderY += 4;
      doc.text(idLine, 14, leftHeaderY);
    }
    const contactLine = [company.phone, company.email].filter(Boolean).join('   ');
    if (contactLine) {
      leftHeaderY += 4;
      doc.text(contactLine, 14, leftHeaderY);
    }

    // Statement #/date moved to bottom-right of the last page (rendered just
    // before save). Generate the number here so the bottom-of-page render uses it.
    const statementNumber = Math.floor(Math.random() * 90000) + 10000;

    // Monday the statement is paid "through" — printed at the bottom-right of
    // the last page in place of an issue date.
    const statementMonday = mondayThrough(statement.week_end_date);

    doc.setTextColor(0, 0, 0);
    const hasContractor = !!(statement.contractor_name || statement.contractor_address);
    // Monday-to-Monday period — printed under the unit/driver block in the
    // top-right corner. Ends on the Monday the statement is paid "through"
    // (the day after the Mon–Sun work week), per accounting.
    const weekRangeLabel = `${fmtDate(statement.week_start_date)} - ${fmtDate(statementMonday)}`;
    // Only Company Driver statements show Mileage columns / per-mile pay.
    // Contractor / Owner / Lease / Rent are paid driver pay directly, not by
    // mile. The rate prints as "PER MILE $0.65" under the driver name.
    const bucket = getStatementBucket(statement);
    const hideMileage = bucket !== 'company_driver';
    const cpmRate = hideMileage ? null : cpmRateFromLoads(loads);
    let rightHeaderBottom = yPos;
    if (hasContractor) {
      // Pay-To Contractor block on right — TRUCK / COMPANY / DRIVER, each
      // line labeled so the reader can tell the contractor from the driver.
      let rightY = yPos;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`TRUCK: ${statement.truck_number}`, 200, rightY, { align: 'right' });
      rightY += 6;
      if (statement.contractor_name) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`COMPANY: ${statement.contractor_name}`, 200, rightY, { align: 'right' });
        rightY += 6;
      }
      if (statement.contractor_address) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const addressLines = doc.splitTextToSize(statement.contractor_address, 90);
        doc.text(addressLines, 200, rightY, { align: 'right' });
        rightY += addressLines.length * 4;
      }
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`DRIVER: ${statement.driver_name}`, 200, rightY, { align: 'right' });
      if (cpmRate !== null) {
        rightY += 5;
        doc.text(`PER MILE $${cpmRate.toFixed(2)}`, 200, rightY, { align: 'right' });
      }
      doc.setFont('helvetica', 'normal');
      rightY += 5;
      doc.setFontSize(9);
      doc.text(weekRangeLabel, 200, rightY, { align: 'right' });
      rightHeaderBottom = rightY;
    } else {
      // Right-aligned driver/unit — top-right where Statement # used to live.
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`TRUCK: ${statement.truck_number}`, 200, yPos, { align: 'right' });
      doc.text(`DRIVER: ${statement.driver_name}`, 200, yPos + 6, { align: 'right' });
      let rightY = yPos + 6;
      if (cpmRate !== null) {
        // Company-driver pay rate, right under the name — e.g. PER MILE $0.65.
        rightY += 6;
        doc.setFontSize(10);
        doc.text(`PER MILE $${cpmRate.toFixed(2)}`, 200, rightY, { align: 'right' });
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      rightY += 5;
      doc.text(weekRangeLabel, 200, rightY, { align: 'right' });
      rightHeaderBottom = rightY;
    }

    // Add separator line — positioned below whichever side (left header block or
    // right contractor/driver info) extends further down the page.
    const separatorY = Math.max(leftHeaderY, rightHeaderBottom) + 4;
    doc.setLineWidth(0.5);
    doc.line(14, separatorY, 196, separatorY);

    yPos = separatorY + 5;

    // Derive how this statement should render (percentage vs CPM/fixed,
    // synthesized service fee for legacy statements, etc.).
    const view = deriveStatementView(statement, lineItems);
    const { lastColLabel, lastColValue, effectiveDeductions, displayTotalRevenue, displayTotalDeductions } = view;

    // Body cells default to gray in autoTable; force them black, and turn any
    // minus-prefixed cell (negative amount) red so deductions/fuel rows pop.
    const NEGATIVE_RED: [number, number, number] = [220, 38, 38];
    const minusToRed = (data: any) => {
      const raw = Array.isArray(data.cell.text) ? data.cell.text.join('') : data.cell.text;
      // Match a "-$" pattern anywhere — covers leading "-$X" in body cells and
      // combined foot strings like "Total:  -$1520.00".
      if (raw && /-\s*\$/.test(raw.toString())) {
        data.cell.styles.textColor = NEGATIVE_RED;
      }
    };
    const bodyStylesBlack = { textColor: [0, 0, 0] as [number, number, number] };
    // Compact table styling — the statement should fit on one page whenever possible.
    const compactStyles = { fontSize: 9, cellPadding: 1 };
    const SECTION_GAP = 4;

    // Loads Table — column order: Date | Load No. | Description | [Mileage] | Amount
    if (loads.length > 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Loads:', 14, yPos);
      yPos += 4;

      // Load No. is shown in the on-screen preview but intentionally omitted
      // from the printed PDF — accounting doesn't need it there and it eats width.
      const loadsData = loads.map(item => {
        // Remove load number prefix from description (e.g., "9345395 - " from "9345395 - Belvidere, IL to New Haven, CT")
        const description = item.description.replace(/^[^\s]+ - /, '').toUpperCase();
        const total = item.quantity || 0;
        const empty = item.metadata?.empty_miles ?? 0;
        const loaded = item.metadata?.loaded_miles ?? Math.max(0, total - empty);
        const row: (string | number)[] = [
          fmtDate(item.date),
          description,
        ];
        if (!hideMileage) {
          row.push(loaded, empty, total);
        }
        row.push(`$${lastColValue(item).toFixed(2)}`);
        return row;
      });

      // Header row prints centered, ALL CAPS.
      const head = hideMileage
        ? [['DATE', 'ORIGIN - DESTINATION', lastColLabel.toUpperCase()]]
        : [['DATE', 'ORIGIN - DESTINATION', 'LOADED', 'EMPTY', 'MILEAGE', lastColLabel.toUpperCase()]];

      const totalAmount = `$${loads.reduce((sum, item) => sum + lastColValue(item), 0).toFixed(2)}`;
      const totalTotal = loads.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const totalEmpty = loads.reduce((sum, item) => sum + (item.metadata?.empty_miles ?? 0), 0);
      const totalLoaded = loads.reduce((sum, item) => {
        const t = item.quantity || 0;
        const e = item.metadata?.empty_miles ?? 0;
        return sum + (item.metadata?.loaded_miles ?? Math.max(0, t - e));
      }, 0);
      // Single right-aligned cell so "Total:" and the value sit on one line.
      const foot = hideMileage
        ? [[
            { content: `Total:  ${totalAmount}`, colSpan: 3, styles: { halign: 'right' as const } },
          ]]
        : [[
            { content: 'Total:', colSpan: 2, styles: { halign: 'right' as const } },
            { content: totalLoaded, styles: { halign: 'right' as const } },
            { content: totalEmpty, styles: { halign: 'right' as const } },
            { content: totalTotal, styles: { halign: 'right' as const } },
            totalAmount,
          ]];

      const columnStyles: Record<number, any> = hideMileage
        ? {
            0: { cellWidth: 22 },
            2: { halign: 'right', cellWidth: 28 },
          }
        : {
            // Wide enough that the ALL-CAPS header labels stay on one line.
            0: { cellWidth: 20 },
            2: { halign: 'right', cellWidth: 18 },
            3: { halign: 'right', cellWidth: 18 },
            4: { halign: 'right', cellWidth: 20 },
            5: { halign: 'right', cellWidth: 26 },
          };

      autoTable(doc, {
        startY: yPos,
        head,
        body: loadsData,
        foot,
        theme: 'grid',
        styles: compactStyles,
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'normal', halign: 'center' },
        bodyStyles: bodyStylesBlack,
        footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
        columnStyles,
        didParseCell: minusToRed,
        showFoot: 'lastPage',
      });

      yPos = (doc as any).lastAutoTable.finalY + SECTION_GAP;
    }

    // Deductions Table — uses the derived effectiveDeductions list, which
    // includes the synthesized "Company service fee" row for legacy
    // percentage statements that didn't save it explicitly.
    // All rows render under a single "Deductions" heading in the fixed
    // deductionSortRank order (cargo → … → service fee → manual add-ons).
    const combinedDeductions = [...scheduledDeductions, ...effectiveDeductions]
      .sort((a, b) => deductionSortRank(a.description) - deductionSortRank(b.description));
    if (combinedDeductions.length > 0) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Deductions:', 14, yPos);
      yPos += 4;

      const deductionsData = combinedDeductions.map(item => [
        fmtDate(item.date),
        item.description ?? '',
        `-$${Math.abs(item.amount).toFixed(2)}`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['DATE', 'DESCRIPTION', 'AMOUNT']],
        body: deductionsData,
        foot: [[
          { content: `Total:  -$${combinedDeductions.reduce((sum, item) => sum + Math.abs(item.amount), 0).toFixed(2)}`, colSpan: 3, styles: { halign: 'right' as const } },
        ]],
        theme: 'grid',
        styles: compactStyles,
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'normal', halign: 'center' },
        bodyStyles: bodyStylesBlack,
        footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', textColor: [0, 0, 0] },
        columnStyles: { 0: { cellWidth: 22 }, 2: { halign: 'right', cellWidth: 26 } },
        didParseCell: minusToRed,
        showFoot: 'lastPage',
      });

      yPos = (doc as any).lastAutoTable.finalY + SECTION_GAP;
    }

    // Maintenance Deductions Table
    const maintenanceDeductions = lineItems.filter(item => item.item_type === 'maintenance_deduction');
    if (maintenanceDeductions.length > 0) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Maintenance Deductions:', 14, yPos);
      yPos += 4;

      const maintenanceData = maintenanceDeductions.map(item => [
        fmtDate(item.date),
        item.description,
        `-$${Math.abs(item.amount).toFixed(2)}`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['DATE', 'DESCRIPTION', 'AMOUNT']],
        body: maintenanceData,
        foot: [[
          { content: `Total:  -$${maintenanceDeductions.reduce((sum, item) => sum + Math.abs(item.amount), 0).toFixed(2)}`, colSpan: 3, styles: { halign: 'right' as const } },
        ]],
        theme: 'grid',
        styles: compactStyles,
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'normal', halign: 'center' },
        bodyStyles: bodyStylesBlack,
        footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', textColor: [0, 0, 0] },
        columnStyles: { 0: { cellWidth: 22 }, 2: { halign: 'right', cellWidth: 26 } },
        didParseCell: minusToRed,
        showFoot: 'lastPage',
      });

      yPos = (doc as any).lastAutoTable.finalY + SECTION_GAP;
    }

    // Credits Table
    if (credits.length > 0) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Credits:', 14, yPos);
      yPos += 4;

      const creditsData = credits.map(item => [
        fmtDate(item.date),
        item.description,
        `$${item.amount.toFixed(2)}`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['DATE', 'DESCRIPTION', 'AMOUNT']],
        body: creditsData,
        foot: [[
          { content: `Total:  +$${credits.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}`, colSpan: 3, styles: { halign: 'right' as const } },
        ]],
        theme: 'grid',
        styles: compactStyles,
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'normal', halign: 'center' },
        bodyStyles: bodyStylesBlack,
        footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', textColor: [0, 0, 0] },
        columnStyles: { 0: { cellWidth: 22 }, 2: { halign: 'right', cellWidth: 26 } },
        didParseCell: minusToRed,
        showFoot: 'lastPage',
      });

      yPos = (doc as any).lastAutoTable.finalY + SECTION_GAP;
    }


    // Fuel detail — printed after the line-item tables and BEFORE the Summary,
    // so the check amount is the final thing on the statement.
    if (fuel.length > 0) {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Fuel:', 14, yPos);
      yPos += 4;

      const fuelData = fuel.map(item => {
        const fuelAmount = Math.abs(item.metadata?.invoice_amount || item.metadata?.amount || item.amount);
        return [
          item.metadata?.product || item.description || 'Fuel',
          item.metadata?.city || '',
          item.metadata?.state || '',
          item.metadata?.gallons?.toFixed(2) || '0.00',
          `$${fuelAmount.toFixed(2)}`,
          '$0.00',
          '$0.00',
          fmtDate(item.date),
          `-$${fuelAmount.toFixed(2)}`
        ];
      });

      autoTable(doc, {
        startY: yPos,
        head: [['DESCRIPTION', 'CITY', 'ST.', 'GALLONS', 'FUEL ($)', 'ADVANCE ($)', 'MISC. ($)', 'DATE', 'AMOUNT']],
        body: fuelData,
        foot: [[
          { content: `Total:  -$${fuel.reduce((sum, item) => sum + Math.abs(item.metadata?.invoice_amount || item.metadata?.amount || item.amount), 0).toFixed(2)}`, colSpan: 9, styles: { halign: 'right' } },
        ]],
        theme: 'grid',
        styles: { cellPadding: 0.9 },
        headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'normal', fontSize: 8, halign: 'center' },
        bodyStyles: { fontSize: 8, textColor: [0, 0, 0] },
        footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', textColor: [0, 0, 0], fontSize: 8 },
        columnStyles: {
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'right' },
          6: { halign: 'right' },
          8: { halign: 'right' }
        },
        didParseCell: minusToRed,
        showFoot: 'lastPage',
      });

      yPos = (doc as any).lastAutoTable.finalY + SECTION_GAP;
    }

    // Summary — the LAST block of the statement, printed under the Fuel detail
    // (or under the last line-item table when there is no fuel). It only spills
    // to a new page when the whole block would collide with the Statement #/date footer.
    const pageHeightForSummary = doc.internal.pageSize.getHeight();
    const SUMMARY_HEIGHT = 32;      // title -> check amount, in mm
    const FOOTER_RESERVE = 18;      // space kept for "Statement #" + date
    yPos += 2;
    if (yPos + SUMMARY_HEIGHT > pageHeightForSummary - FOOTER_RESERVE) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('SUMMARY', 105, yPos, { align: 'center' });
    yPos += 6;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Revenue:', 14, yPos);
    doc.text(`$${displayTotalRevenue.toFixed(2)}`, 196, yPos, { align: 'right' });
    yPos += 5;

    doc.text('Total Deductions:', 14, yPos);
    doc.setTextColor(220, 38, 38);
    doc.text(`-$${displayTotalDeductions.toFixed(2)}`, 196, yPos, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    yPos += 5;

    doc.text('Total Credits:', 14, yPos);
    doc.text(`+$${statement.total_credits.toFixed(2)}`, 196, yPos, { align: 'right' });
    yPos += 5;

    doc.text('Total Fuel:', 14, yPos);
    doc.setTextColor(220, 38, 38);
    doc.text(`-$${statement.total_fuel.toFixed(2)}`, 196, yPos, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    yPos += 3;

    doc.setDrawColor(0, 0, 0);
    doc.line(14, yPos, 196, yPos);
    yPos += 6;

    doc.setFontSize(12);
    // Bold on every statement type — the check amount is the number the driver
    // looks for first, so it has to stand out from the summary rows above it.
    doc.setFont('helvetica', 'bold');
    doc.text('CHECK AMOUNT:', 14, yPos);
    if (statement.net_pay < 0) doc.setTextColor(220, 38, 38);
    doc.text(`$${statement.net_pay.toFixed(2)}`, 196, yPos, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    yPos += SECTION_GAP + 6;

    // Statement # and issue date — printed at the bottom-right of the LAST page
    // (jsPDF's "current page" after rendering everything is the final page).
    doc.setPage(doc.getNumberOfPages());
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Statement #${statementNumber}`, 200, pageHeight - 14, { align: 'right' });
    doc.setFontSize(9);
    doc.text(fmtDate(statementMonday), 200, pageHeight - 8, { align: 'right' });

    // Save or return PDF
    const filename = statementPdfFilename(statement);

    if (download) {
      doc.save(filename);
      return null;
    } else {
      // Return as blob for upload
      return doc.output('blob');
    }
  };

  async function loadStatements() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('driver_statements')
        .select('*')
        .order('week_start_date', { ascending: false });

      if (error) throw error;
      setStatements(data || []);
      loadStatementLeaseMap();
    } catch (error) {
      console.error('Error loading statements:', error);
      toast.error('Failed to load statements');
    } finally {
      setLoading(false);
    }
  }

  async function loadDrivers() {
    try {
      const { data: driversData, error: driversError } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, is_active, company_id, driver_type, companies(id, name, code, logo_url, address, city, state, zip_code, phone, email, mc_number, dot_number)')
        .order('first_name');

      if (driversError) throw driversError;
      
      // Current equipment assignments, newest first and fully paged. Both
      // matter: a driver carrying more than one is_current row must resolve to
      // the same truck for every user (unordered, .find() picked whichever row
      // Postgres happened to return first), and the unpaged query silently
      // truncated at PostgREST's 1000-row cap.
      const assignments = await fetchAllRows<any>(() => supabase
        .from('equipment_assignments')
        .select('truck_id, driver_id, assigned_at, trucks(unit_number)')
        .eq('is_current', true)
        .order('assigned_at', { ascending: false }));

      // First row per driver wins = the most recent current assignment.
      const currentAssignmentByDriver = new Map<string, any>();
      assignments.forEach((a) => {
        if (a.driver_id && !currentAssignmentByDriver.has(a.driver_id)) {
          currentAssignmentByDriver.set(a.driver_id, a);
        }
      });

      // Match drivers with their assigned trucks
      const driversWithTrucks = (driversData || []).map((driver) => {
        const assignment = currentAssignmentByDriver.get(driver.id);
        const truck = Array.isArray(assignment?.trucks) ? assignment.trucks[0] : assignment?.trucks;

        return {
          ...driver,
          trucks: truck || null
        };
      });
      
      setDrivers(driversWithTrucks);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    }
  }

  // Calculate previous week (Monday to Sunday)
  const getPreviousWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? 7 : dayOfWeek; // If Sunday, go back 7 days, else go back to last Monday
    
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - diff);
    
    const lastMonday = new Date(lastSunday);
    lastMonday.setDate(lastSunday.getDate() - 6);
    
    return {
      start: lastMonday.toISOString().split('T')[0],
      end: lastSunday.toISOString().split('T')[0]
    };
  };

  const openGenerateModal = () => {
    const week = getPreviousWeek();
    setWeekStartDate(week.start);
    setWeekEndDate(week.end);
    setCurrentStep(1);
    setSelectedDriver('');
    setSelectedTeamId(null);
    setSelectedTeamName('');
    setIsPastStatementFlow(false);
    setTruckNumberOverride(null);
    setShowGenerateModal(true);
  };

  const openPastStatementModal = () => {
    // Set default to last week (most recent completed week)
    const week = getPreviousWeek();
    setCustomWeekStart(week.start);
    setCustomWeekEnd(week.end);
    setShowPastStatementModal(true);
  };

  // Generate list of past weeks (last 12 weeks)
  const getPastWeeks = () => {
    const weeks = [];
    const today = new Date();
    
    for (let i = 1; i <= 12; i++) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() - (i * 7));
      
      const dayOfWeek = targetDate.getDay();
      const diff = dayOfWeek === 0 ? 7 : dayOfWeek;
      
      const sunday = new Date(targetDate);
      sunday.setDate(targetDate.getDate() - diff);
      
      const monday = new Date(sunday);
      monday.setDate(sunday.getDate() - 6);
      
      weeks.push({
        start: monday.toISOString().split('T')[0],
        end: sunday.toISOString().split('T')[0],
        label: `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      });
    }
    
    return weeks;
  };

  const startPastStatementGeneration = () => {
    if (!customWeekStart || !customWeekEnd) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    setWeekStartDate(customWeekStart);
    setWeekEndDate(customWeekEnd);
    setCurrentStep(1);
    setIsPastStatementFlow(true);
    setTruckNumberOverride(null);
    setShowPastStatementModal(false);
    setShowGenerateModal(true);
  };

  // Shared transform: a dispatch+load row -> the Load shape the statement steps
  // expect (origin/destination from stops, miles, driver_pay).
  const transformDispatchLoadRow = (dispatch: any) => {
    const load = dispatch.loads as any;
    let pickupCity = load.pickup_city;
    let pickupState = load.pickup_state;
    let pickupDate = load.pickup_date;
    let deliveryCity = load.delivery_city;
    let deliveryState = load.delivery_state;
    let deliveryDate = load.delivery_date;

    if (load.load_stops && load.load_stops.length > 0) {
      const sortedStops = [...load.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
      const pickupStop = sortedStops.find((s: any) => s.stop_type === 'shipper');
      if (pickupStop) {
        pickupCity = pickupStop.city;
        pickupState = pickupStop.state;
        if (pickupStop.date) pickupDate = pickupStop.date;
      }
      const deliveryStops = sortedStops.filter((s: any) => s.stop_type === 'consignee');
      if (deliveryStops.length > 0) {
        const lastDelivery = deliveryStops[deliveryStops.length - 1];
        deliveryCity = lastDelivery.city;
        deliveryState = lastDelivery.state;
        if (lastDelivery.date) deliveryDate = lastDelivery.date;
      }
    }

    return {
      ...load,
      pickup_city: pickupCity,
      pickup_state: pickupState,
      pickup_date: pickupDate,
      delivery_city: deliveryCity,
      delivery_state: deliveryState,
      delivery_date: deliveryDate,
      empty_miles: (dispatch as any).empty_miles || 0,
      dispatches: [{ driver_pay: dispatch.driver_pay }],
    };
  };

  const handleDriverSelect = async (driverId: string) => {
    const driver = drivers.find(d => d.id === driverId);
    if (!driver) return;

    // Selecting an individual driver clears any team selection.
    setSelectedTeamId(null);
    setSelectedTeamName('');

    const driverFullName = `${driver.first_name} ${driver.last_name}`;
    setSelectedDriver(driverFullName);
    setTruckNumberOverride(null);
    
    // Load all DELIVERED loads for this driver through dispatches table
    try {
      console.log('Loading loads for driver ID:', driverId, 'Name:', driverFullName);
      
      // Query dispatches for this driver, then get the associated loads
      // Include both DELIVERED and ARCHIVED status (archived = sent to accounting)
      const dispatchesData = await fetchAllRows<any>(() => supabase
        .from('dispatches')
        .select(`
          id,
          load_id,
          driver_id,
          driver_pay,
          empty_miles,
          loads!dispatches_load_id_fkey(
            *,
            trucks(unit_number),
            drivers(first_name, last_name),
            load_stops!load_stops_load_id_fkey(
              stop_type,
              stop_order,
              city,
              state,
              date,
              time_from
            )
          )
        `)
        .eq('driver_id', driverId)
        .is('team_id', null)); // solo statement: never pull team loads onto an individual

      console.log('Dispatches for this driver:', dispatchesData);

      // Transform the data to match the expected Load structure and sort by delivery date
      const loadsData = (dispatchesData || [])
        .filter(dispatch => {
          // Filter out dispatches without loads
          if (!dispatch.loads) {
            console.log('Dispatch without load:', dispatch);
            return false;
          }
          // Include delivered/archived loads (selectable for billing) AND
          // dispatched loads (shown read-only so the user can see in-progress
          // work for the week; they are not selectable downstream).
          const status = (dispatch.loads as any).status?.toLowerCase();
          const isVisible = status === 'delivered' || status === 'archived' || status === 'dispatched';
          console.log('Load', (dispatch.loads as any).load_number, 'status:', status, 'included:', isVisible);
          return isVisible;
        })
        .map(dispatch => {
          const load = dispatch.loads as any;

          // Extract origin/destination from load_stops if main fields are empty
          let pickupCity = load.pickup_city;
          let pickupState = load.pickup_state;
          let pickupDate = load.pickup_date;
          let deliveryCity = load.delivery_city;
          let deliveryState = load.delivery_state;
          let deliveryDate = load.delivery_date;

          if (load.load_stops && load.load_stops.length > 0) {
            const sortedStops = [...load.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);

            // Get first pickup stop
            const pickupStop = sortedStops.find((s: any) => s.stop_type === 'shipper');
            if (pickupStop) {
              pickupCity = pickupStop.city;
              pickupState = pickupStop.state;
              // Use the pickup stop date if available
              if (pickupStop.date) {
                pickupDate = pickupStop.date;
              }
            }
            
            // Get last delivery stop
            const deliveryStops = sortedStops.filter((s: any) => s.stop_type === 'consignee');
            if (deliveryStops.length > 0) {
              const lastDelivery = deliveryStops[deliveryStops.length - 1];
              deliveryCity = lastDelivery.city;
              deliveryState = lastDelivery.state;
              // Use the last delivery stop date if available
              if (lastDelivery.date) {
                deliveryDate = lastDelivery.date;
              }
            }
          }
          
          return {
            ...load,
            pickup_city: pickupCity,
            pickup_state: pickupState,
            pickup_date: pickupDate,
            delivery_city: deliveryCity,
            delivery_state: deliveryState,
            delivery_date: deliveryDate,
            empty_miles: (dispatch as any).empty_miles || 0,
            dispatches: [{ driver_pay: dispatch.driver_pay }]
          };
        })
        .sort((a, b) => {
          // Sort by PICKUP date ascending (oldest first) — this is the date the
          // statement's Date column prints, so the two must always agree.
          // Delivery date is only a tiebreaker, never the primary key.
          const dateA = new Date(a.pickup_date || a.delivery_date || 0).getTime();
          const dateB = new Date(b.pickup_date || b.delivery_date || 0).getTime();
          if (dateA !== dateB) return dateA - dateB;
          const delA = new Date(a.delivery_date || 0).getTime();
          const delB = new Date(b.delivery_date || 0).getTime();
          if (delA !== delB) return delA - delB;
          return (a.load_number || '').localeCompare(b.load_number || '');
        });
      
      console.log('Loads for this driver:', loadsData);
      
      // If the driver has both a Contractor and a Company Driver scheduled deduction,
      // two separate statements are generated for them — show every load (including ones
      // already on another statement) so the user can split loads across the two.
      const { data: schedDedTypes } = await supabase
        .from('scheduled_deductions')
        .select('lease_or_rent')
        .ilike('driver_name', driverFullName.trim());
      // A driver with more than one scheduled-deduction profile (e.g. owner +
      // company_driver) gets a separate statement per profile, so their loads
      // must stay visible across all of them — even after one statement has
      // already claimed them. Single-profile drivers still hide already-billed loads.
      const hasMultipleProfiles = (schedDedTypes || []).length > 1;

      const visibleLoads = hasMultipleProfiles
        ? loadsData
        : loadsData.filter(load => !load.included_in_statement);

      console.log('Loaded loads for driver:', driverFullName, 'Total:', loadsData.length, 'Visible:', visibleLoads.length, 'Multi-profile:', hasMultipleProfiles);

      setAvailableLoads(visibleLoads);
      // Labels only — never gates selectability.
      void loadStatementRefsForLoads(visibleLoads.map(l => l.id));
      
      // The statement shows the driver's CURRENT truck assignment. The truck
      // that ran the most recent load is only a fallback for a driver with no
      // current assignment.
      if (driver.trucks?.unit_number) {
        setSelectedTruck(driver.trucks.unit_number);
      } else if (loadsData && loadsData.length > 0 && loadsData[0].trucks) {
        setSelectedTruck(loadsData[0].trucks.unit_number);
      } else {
        setSelectedTruck('Not assigned');
      }
    } catch (error) {
      console.error('Error loading loads:', error);
      setSelectedTruck('Not assigned');
    }
  };

  // Which statements already bill these loads? Read-only labelling for Step 3.
  // Scoped by item_id in chunks so it is never silently truncated by PostgREST's
  // 1000-row cap. Best-effort: on any failure the labels simply do not render —
  // this must never block or alter which loads are selectable.
  const loadStatementRefsForLoads = async (loadIds: string[]) => {
    if (!loadIds.length) { setLoadStatementRefs({}); return; }
    try {
      const items: any[] = [];
      for (let i = 0; i < loadIds.length; i += 100) {
        const { data, error } = await supabase
          .from('statement_line_items')
          .select('statement_id, item_id, metadata')
          .eq('item_type', 'load')
          .in('item_id', loadIds.slice(i, i + 100));
        if (error) throw error;
        if (data) items.push(...data);
      }

      // Resolve each statement to its week so the badge can say which week claimed it.
      const stmtIds = Array.from(new Set(items.map(i => i.statement_id).filter(Boolean)));
      const weekByStatement: Record<string, string | null> = {};
      for (let i = 0; i < stmtIds.length; i += 100) {
        const { data } = await supabase
          .from('driver_statements')
          .select('id, week_end_date')
          .in('id', stmtIds.slice(i, i + 100));
        (data || []).forEach((st: any) => { weekByStatement[st.id] = st.week_end_date ?? null; });
      }

      const refs: Record<string, { statementId: string | null; payMode: string | null; weekEnd: string | null }[]> = {};
      items.forEach((it: any) => {
        const lid = it.item_id;
        if (!lid) return;
        if (!refs[lid]) refs[lid] = [];
        refs[lid].push({
          statementId: it.statement_id ?? null,
          // 'cpm' = billed under a company-driver profile, 'percentage' = owner/contractor.
          payMode: (it.metadata as any)?.commission_type ?? null,
          weekEnd: weekByStatement[it.statement_id] ?? null,
        });
      });
      setLoadStatementRefs(refs);
    } catch (e) {
      console.error('Error loading statement refs for loads (labels only):', e);
      setLoadStatementRefs({});
    }
  };

  // Select a TEAM as the statement payee: one combined statement for both drivers.
  // Loads are gathered by dispatches.team_id (deduped by load), and selectedDriver
  // is set to the team label so the name-keyed deduction/credit/fuel loaders and
  // the team's scheduled_deduction (driver_name = label) resolve correctly.
  const handleTeamSelect = async (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return;

    setSelectedTeamId(teamId);
    setSelectedTeamName(team.label);
    setSelectedDriver(team.label);
    setTruckNumberOverride(null);

    try {
      const dispatchesData = await fetchAllRows<any>(() => supabase
        .from('dispatches')
        .select(`
          id,
          load_id,
          driver_id,
          team_id,
          driver_pay,
          empty_miles,
          loads!dispatches_load_id_fkey(
            *,
            trucks(unit_number),
            drivers(first_name, last_name),
            load_stops!load_stops_load_id_fkey(stop_type, stop_order, city, state, date, time_from)
          )
        `)
        .eq('team_id', teamId));

      // Dedup by load_id so a team load is only ever counted once.
      const seenLoadIds = new Set<string>();
      const loadsData = (dispatchesData || [])
        .filter(dispatch => {
          if (!dispatch.loads) return false;
          const status = (dispatch.loads as any).status?.toLowerCase();
          if (status !== 'delivered' && status !== 'archived' && status !== 'dispatched') return false;
          const lid = (dispatch.loads as any).id || dispatch.load_id;
          if (seenLoadIds.has(lid)) return false;
          seenLoadIds.add(lid);
          return true;
        })
        .map(dispatch => transformDispatchLoadRow(dispatch))
        .sort((a, b) => {
          // Pickup date first (matches the statement's Date column), delivery
          // date as tiebreaker, then load number.
          const dateA = new Date(a.pickup_date || a.delivery_date || 0).getTime();
          const dateB = new Date(b.pickup_date || b.delivery_date || 0).getTime();
          if (dateA !== dateB) return dateA - dateB;
          const delA = new Date(a.delivery_date || 0).getTime();
          const delB = new Date(b.delivery_date || 0).getTime();
          if (delA !== delB) return delA - delB;
          return (a.load_number || '').localeCompare(b.load_number || '');
        });

      const visibleLoads = loadsData.filter(load => !load.included_in_statement);
      setAvailableLoads(visibleLoads);

      // Same rule as solo drivers: the team's current truck wins over
      // whatever truck happened to run the most recent load.
      if (team.truckNumber) {
        setSelectedTruck(team.truckNumber);
      } else if (loadsData.length > 0 && loadsData[0].trucks) {
        setSelectedTruck(loadsData[0].trucks.unit_number);
      } else {
        setSelectedTruck('Not assigned');
      }
    } catch (error) {
      console.error('Error loading team loads:', error);
      setSelectedTruck(team.truckNumber || 'Not assigned');
    }
  };

  const loadDeductionsForDriver = async () => {
    try {
      // Get the selected driver's ID
      const driver = drivers.find(d => `${d.first_name} ${d.last_name}` === selectedDriver);
      if (!driver) {
        console.error('Driver not found:', selectedDriver);
        setAvailableDeductions([]);
        return;
      }

      const driverId = driver.id;
      console.log('Loading deductions for driver:', selectedDriver, 'ID:', driverId);

      // Fetch custom deductions from driver_deductions table (with remaining amount > 0, for this driver)
      const { data: customDeductions, error: customError } = await supabase
        .from('driver_deductions')
        .select(`
          id,
          driver_id,
          deduction_type,
          amount,
          charged_amount,
          remaining_amount,
          is_recurring,
          description,
          deduction_date,
          status,
          drivers (
            first_name,
            last_name
          )
        `)
        .eq('driver_id', driverId)
        .or('remaining_amount.gt.0,remaining_amount.is.null')
        .order('deduction_date', { ascending: false });

      if (customError) throw customError;

      // Fetch hotel reimbursements not yet paid (for this driver)
      const { data: hotelData, error: hotelError } = await supabase
        .from('hotel_reimbursements')
        .select('*')
        .ilike('driver_name', selectedDriver)
        .eq('driver_paid', false)
        .order('date', { ascending: false });

      if (hotelError) throw hotelError;

      // Fetch lyft reimbursements not yet paid (for this driver)
      const { data: lyftData, error: lyftError } = await supabase
        .from('lyft_reimbursements')
        .select('*')
        .ilike('driver_name', selectedDriver)
        .eq('driver_paid', false)
        .order('date', { ascending: false });

      if (lyftError) throw lyftError;

      // Fetch airplane reimbursements not yet paid (for this driver)
      const { data: airplaneData, error: airplaneError } = await supabase
        .from('airplane_reimbursements')
        .select('*')
        .ilike('driver_name', selectedDriver)
        .eq('driver_paid', false)
        .order('date', { ascending: false });

      if (airplaneError) throw airplaneError;

      // Aggregate prior partial payments for reimbursements from statement_line_items.
      // (Custom driver_deductions rows already carry charged_amount/remaining_amount on the row.)
      const reimbursementIds = [
        ...(hotelData || []).map((i: any) => i.id),
        ...(lyftData || []).map((i: any) => i.id),
        ...(airplaneData || []).map((i: any) => i.id),
      ];
      const paidByItemId = new Map<string, number>();
      if (reimbursementIds.length > 0) {
        const { data: priorLineItems } = await supabase
          .from('statement_line_items')
          .select('item_id, amount')
          .eq('item_type', 'deduction')
          .in('item_id', reimbursementIds);
        (priorLineItems || []).forEach((li: any) => {
          const prev = paidByItemId.get(li.item_id) || 0;
          paidByItemId.set(li.item_id, prev + Math.abs(li.amount || 0));
        });
      }

      // Combine all deductions
      const allDeductions: Deduction[] = [
        // Custom deductions from driver_deductions table
        ...(customDeductions || []).map((item: any) => ({
          id: `custom-${item.id}`,
          driver_id: item.driver_id,
          driver_name: item.drivers ? `${item.drivers.first_name} ${item.drivers.last_name}` : 'Unknown',
          deduction_type: item.description || 'Custom',
          description: item.description,
          amount: item.amount,
          charged_amount: item.charged_amount || 0,
          // One-time deductions: remaining = amount - charged, derived so an
          // edited amount is never capped by a stale stored remaining_amount
          // (e.g. Total $1,000,000 but Max $1.00). Recurring rows keep the
          // stored value, which tracks the total rather than the installment.
          remaining_amount: item.is_recurring
            ? (item.remaining_amount !== null ? item.remaining_amount : item.amount)
            : Math.max(0, (item.amount || 0) - (item.charged_amount || 0)),
          deduction_date: item.deduction_date,
          status: item.status || 'pending',
          paid_date: item.status === 'paid' ? item.paid_date : undefined,
        })),
        ...(hotelData || []).map((item: any) => {
          const paid = paidByItemId.get(item.id) || 0;
          return {
            id: `hotel-${item.id}`,
            driver_id: item.driver_id || '',
            driver_name: item.driver_name,
            deduction_type: 'Hotel',
            description: item.hotel_description,
            amount: item.amount,
            charged_amount: paid,
            remaining_amount: Math.max(0, item.amount - paid),
            deduction_date: item.date,
            status: item.driver_paid ? 'paid' : 'pending',
            paid_date: item.driver_paid ? new Date().toISOString() : undefined,
          };
        }),
        ...(lyftData || []).map((item: any) => {
          const paid = paidByItemId.get(item.id) || 0;
          return {
            id: `lyft-${item.id}`,
            driver_id: item.driver_id || '',
            driver_name: item.driver_name,
            deduction_type: 'Lyft',
            description: 'Lyft Ride',
            amount: item.amount,
            charged_amount: paid,
            remaining_amount: Math.max(0, item.amount - paid),
            deduction_date: item.date,
            status: item.driver_paid ? 'paid' : 'pending',
            paid_date: item.driver_paid ? new Date().toISOString() : undefined,
          };
        }),
        ...(airplaneData || []).map((item: any) => {
          const paid = paidByItemId.get(item.id) || 0;
          return {
            id: `airplane-${item.id}`,
            driver_id: item.driver_id || '',
            driver_name: item.driver_name,
            deduction_type: 'Airplane',
            description: item.airline_company,
            amount: item.amount,
            charged_amount: paid,
            remaining_amount: Math.max(0, item.amount - paid),
            deduction_date: item.date,
            status: item.driver_paid ? 'paid' : 'pending',
            paid_date: item.driver_paid ? new Date().toISOString() : undefined,
          };
        }),
      ].filter(d => (d.remaining_amount === undefined ? true : d.remaining_amount > 0));

      // Sort by date descending
      allDeductions.sort((a, b) => new Date(b.deduction_date).getTime() - new Date(a.deduction_date).getTime());
      
      console.log('Deductions loaded:', allDeductions);
      console.log('Number of deductions:', allDeductions.length);
      
      setAvailableDeductions(allDeductions);
    } catch (error) {
      console.error('Error loading deductions:', error);
    }
  };

  const handleAddDeduction = async () => {
    if (!newDeductionType || !newDeductionAmount || !newDeductionDate) {
      toast.error('Please fill in all fields');
      return;
    }

    const amount = parseFloat(newDeductionAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setAddingDeduction(true);

      if (editingDeductionId) {
        // Edit path. Mirrors DeductionsView.handleUpdateDeduction: keep
        // remaining_amount in step with the new amount so Step 4 never caps the
        // charge at a stale balance, and re-open a paid row if money is owed again.
        const { data: current, error: curErr } = await supabase
          .from('driver_deductions')
          .select('charged_amount, is_recurring, status')
          .eq('id', editingDeductionId)
          .single();
        if (curErr) throw curErr;

        const updateData: Record<string, unknown> = {
          deduction_date: newDeductionDate,
          amount,
          description: newDeductionType,
          deduction_type: newDeductionType,
        };
        let newRemaining: number | null = null;
        if (current && !current.is_recurring) {
          newRemaining = Math.max(0, amount - (current.charged_amount || 0));
          updateData.remaining_amount = newRemaining;
          if (newRemaining > 0 && current.status === 'paid') {
            updateData.status = 'pending';
            updateData.paid_date = null;
          }
        }

        const { error } = await supabase
          .from('driver_deductions')
          .update(updateData)
          .eq('id', editingDeductionId);
        if (error) throw error;

        // If this deduction is already ticked for the statement, re-seed its
        // charge amount from the new balance so the total stays consistent.
        const listId = `custom-${editingDeductionId}`;
        if (selectedDeductions.includes(listId)) {
          setDeductionChargeAmounts(prev => ({
            ...prev,
            [listId]: newRemaining !== null
              ? Math.min(prev[listId] ?? newRemaining, newRemaining)
              : amount,
          }));
        }

        toast.success('Deduction updated');
      } else {
        // Get the selected driver's ID
        const driver = drivers.find(d => `${d.first_name} ${d.last_name}` === selectedDriver);
        if (!driver) {
          toast.error('Driver not found');
          return;
        }

        // Insert the new deduction
        const { error } = await supabase
          .from('driver_deductions')
          .insert({
            driver_id: driver.id,
            deduction_type: newDeductionType,
            amount: amount,
            remaining_amount: amount,
            charged_amount: 0,
            description: newDeductionType,
            deduction_date: newDeductionDate,
            status: 'pending'
          });

        if (error) throw error;

        toast.success('Deduction added successfully');
      }
      
      // Reset form
      setNewDeductionType('');
      setNewDeductionAmount('');
      setNewDeductionDate(new Date().toISOString().split('T')[0]);
      setEditingDeductionId(null);
      setShowAddDeductionModal(false);

      // Reload deductions
      await loadDeductionsForDriver();
    } catch (error) {
      console.error('Error saving deduction:', error);
      toast.error(editingDeductionId ? 'Failed to update deduction' : 'Failed to add deduction');
    } finally {
      setAddingDeduction(false);
    }
  };

  const handleAddCredit = async () => {
    if (!newCreditReason || !newCreditAmount || !newCreditDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    const amount = parseFloat(newCreditAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setAddingCredit(true);

      // Get the selected driver's ID
      const driver = drivers.find(d => `${d.first_name} ${d.last_name}` === selectedDriver);
      if (!driver) {
        toast.error('Driver not found');
        return;
      }

      // Insert the new credit
      const { error } = await supabase
        .from('driver_credits')
        .insert({
          driver_name: selectedDriver,
          reason: newCreditReason,
          amount: amount,
          date: newCreditDate,
          status: newCreditStatus
        });

      if (error) throw error;

      toast.success('Credit added successfully');
      
      // Reset form
      setNewCreditReason('');
      setNewCreditAmount('');
      setNewCreditDate(new Date().toISOString().split('T')[0]);
      setNewCreditStatus('pending');
      setShowAddCreditModal(false);

      // Reload credits
      await loadCreditsForDriver();
    } catch (error) {
      console.error('Error adding credit:', error);
      toast.error('Failed to add credit');
    } finally {
      setAddingCredit(false);
    }
  };

  const loadMaintenanceDeductionsForDriver = async () => {
    try {
      const driver = drivers.find(d => `${d.first_name} ${d.last_name}` === selectedDriver);
      if (!driver) {
        console.error('Driver not found:', selectedDriver);
        setAvailableMaintenanceDeductions([]);
        return;
      }

      const driverId = driver.id;
      console.log('Loading maintenance deductions for driver:', selectedDriver, 'ID:', driverId);

      // First, check if there are ANY maintenance logs for this driver
      const { data: allDriverLogs, error: allError } = await supabase
        .from('maintenance_logs')
        .select('id, log_number, paid_by, driver_payment_status, driver_payment_amount, driver_id')
        .eq('driver_id', driverId);
      
      console.log('All maintenance logs for this driver:', allDriverLogs);

      // Fetch maintenance logs where driver is paying and has remaining amount
      // Note: This query works with both old schema (unpaid status) and new schema (remaining_amount)
      const { data: maintenanceData, error } = await supabase
        .from('maintenance_logs')
        .select(`
          id,
          log_number,
          maintenance_type,
          service_date,
          driver_payment_amount,
          driver_charged_amount,
          driver_remaining_amount,
          cost,
          company_cost_amount,
          description,
          driver_payment_status,
          trucks (unit_number),
          trailers (unit_number)
        `)
        .eq('driver_id', driverId)
        .eq('paid_by', 'driver')
        .eq('driver_payment_status', 'unpaid')
        .gt('driver_payment_amount', 0)
        .order('service_date', { ascending: false });

      if (error) throw error;

      console.log('Filtered maintenance deductions (paid_by=driver, status=unpaid, amount>0):', maintenanceData);
      setAvailableMaintenanceDeductions(maintenanceData || []);
    } catch (error) {
      console.error('Error loading maintenance deductions:', error);
      setAvailableMaintenanceDeductions([]);
    }
  };

  // Resolve loaded/empty/total miles for a load, honoring per-statement edits.
  // If user edits Loaded or Empty, Total = loaded + empty. If user edits Total
  // directly (without overriding loaded/empty), keep that as-is — loaded is
  // then derived from total - empty.
  const getLoadMiles = (load: Load): { loaded: number; empty: number; total: number } => {
    // `total_miles` actually stores the LOADED trip distance (origin →
    // destination, from the rate con). Empty/deadhead is stored separately on
    // the dispatch. Drivers are paid on loaded + empty, so the billed total is
    // loaded + empty (NOT total_miles alone).
    const defaultLoaded = load.total_miles ?? 0;
    const defaultEmpty = load.empty_miles ?? 0;
    const defaultTotal = defaultLoaded + defaultEmpty;
    const loadedEdit = editedLoadedMiles[load.id];
    const emptyEdit = editedEmptyMiles[load.id];
    const totalEdit = editedMileage[load.id];
    const empty = emptyEdit ?? defaultEmpty;
    if (loadedEdit !== undefined || emptyEdit !== undefined) {
      const loaded = loadedEdit ?? defaultLoaded;
      return { loaded, empty, total: loaded + empty };
    }
    // If the user typed a Total directly, honor it (loaded = total − empty);
    // otherwise the default total is loaded + empty.
    const total = totalEdit ?? defaultTotal;
    return { loaded: Math.max(0, total - empty), empty, total };
  };

  // The computed "ORIGIN - DESTINATION" text for a load — what the Description
  // column shows when accounting hasn't typed anything over it.
  const defaultLoadDescription = (load: Load): string => {
    const pickupLoc = load.pickup_city && load.pickup_state
      ? `${load.pickup_city}, ${load.pickup_state}`
      : (load.pickup_location || 'N/A');
    const deliveryLoc = load.delivery_city && load.delivery_state
      ? `${load.delivery_city}, ${load.delivery_state}`
      : (load.delivery_location || 'N/A');
    return `${pickupLoc} - ${deliveryLoc}`;
  };

  // What the editable Description cell displays: the per-statement override if
  // there is one, otherwise the computed default. Deliberately NOT trimmed —
  // the input is controlled by this, and trimming here would eat the space the
  // user just typed between two words.
  const getLoadDescriptionDraft = (load: Load): string =>
    editedDescription[load.id] ?? defaultLoadDescription(load);

  // The effective description — the one that gets printed and saved. Overrides
  // are free text that ends up in a PDF mailed to the driver, so trim and cap
  // them; a blank override falls back to the default rather than printing an
  // empty cell. Untouched rows are returned verbatim so existing statements
  // keep rendering exactly as before.
  const getLoadDescription = (load: Load): string => {
    const edit = editedDescription[load.id];
    if (edit === undefined) return defaultLoadDescription(load);
    return edit.trim().slice(0, LOAD_DESCRIPTION_MAX_CHARS) || defaultLoadDescription(load);
  };

  // Calculate driver amount based on commission type
  const calculateDriverAmount = (freightAmount: number, miles: number, commission: string): number => {
    if (!commission) return freightAmount;
    
    // Check if commission is a percentage (contains '%' or is > 100)
    const commissionValue = parseFloat(commission.replace('%', ''));
    
    if (commission.includes('%') || commissionValue > 100) {
      // Percentage-based: driver keeps (100 - commission)%
      return freightAmount * (1 - commissionValue / 100);
    } else {
      // Per-mile rate: value is stored as dollars/mile (e.g. 0.75 = $0.75/mile).
      // Legacy rows may still hold an integer like 75 for 75¢/mile — normalize those.
      const perMile = commissionValue >= 10 ? commissionValue / 100 : commissionValue;
      return miles * perMile;
    }
  };

  // Truck shown in the generate-modal steps. Past statements derive it from
  // the trucks that drove the selected loads (all distinct units); regular
  // statements show the profile/current-assignment unit. Must stay in sync
  // with the statementTruckNumber computed at generation time.
  // A load is "fully billed" once it already sits on as many statements as this
  // payee is expected to produce: 1 for a normal driver, 2 for a driver with both a
  // company-driver and an owner/contractor scheduled deduction. Those are hidden in
  // Step 3 behind a reveal toggle so the list shows work that still needs paying.
  //
  // Deliberately conservative — it hides ONLY on positive evidence:
  //   * not flagged included_in_statement      -> show (never billed)
  //   * statement history missing / load failed -> show (no evidence)
  //   * currently selected by the user          -> show (never hide something being billed)
  // Anything hidden stays one click away, so no load can become unpayable.
  const step3ProfileCount = Math.max(1, availableScheduledDeductions.length);
  const isLoadFullyBilled = (load: Load): boolean => {
    // If the profile list is empty we cannot know how many statements this payee
    // owes, and a dual-profile driver would look single-profile — hide nothing.
    if (availableScheduledDeductions.length === 0) return false;
    if (!load.included_in_statement) return false;
    if (selectedLoads.includes(load.id)) return false;
    const refs = loadStatementRefs[load.id];
    if (!refs || refs.length === 0) return false;
    const distinctStatements = new Set(refs.map(r => r.statementId).filter(Boolean)).size;
    if (distinctStatements === 0) return false;
    return distinctStatements >= step3ProfileCount;
  };
  const step3HiddenLoads = availableLoads.filter(isLoadFullyBilled);
  const step3VisibleLoads = showBilledLoads
    ? availableLoads
    : availableLoads.filter(l => !isLoadFullyBilled(l));

  // Per-load truck and driver, read from the load record itself (loads.truck_id /
  // loads.driver_id) rather than from the statement header, so a load that was
  // run under a different unit or reassigned driver shows up as such.
  type Step3LoadRefs = {
    trucks?: { unit_number?: string | null } | null;
    drivers?: { first_name?: string | null; last_name?: string | null } | null;
  };
  const step3LoadTruck = (load: Load): string => {
    const unit = (load as Load & Step3LoadRefs).trucks?.unit_number;
    return unit ? `#${unit}` : 'N/A';
  };
  const step3LoadDriver = (load: Load): string => {
    const d = (load as Load & Step3LoadRefs).drivers;
    const name = d ? `${d.first_name || ''} ${d.last_name || ''}`.trim() : '';
    return name || 'Unassigned';
  };
  // Grouping rule: when more than one driver appears, group by driver (and list
  // the trucks each one used). When it is a single driver — the usual case —
  // group by truck instead, so a driver who hopped units during the week sees
  // their loads split per unit. Groups keep order of first appearance (the list
  // is already sorted by pickup date, so groups follow the week's timeline).
  const step3GroupByTruck = new Set(step3VisibleLoads.map(step3LoadDriver)).size <= 1;
  const step3LoadGroups = (() => {
    const groups: { name: string; loads: Load[]; trucks: string[] }[] = [];
    step3VisibleLoads.forEach((load) => {
      const truck = step3LoadTruck(load);
      const name = step3GroupByTruck
        ? (truck === 'N/A' ? 'No truck on load' : `Truck ${truck}`)
        : step3LoadDriver(load);
      let g = groups.find(x => x.name === name);
      if (!g) { g = { name, loads: [], trucks: [] }; groups.push(g); }
      g.loads.push(load);
      if (truck !== 'N/A' && !g.trucks.includes(truck)) g.trucks.push(truck);
    });
    return groups;
  })();
  // Every distinct truck across the visible loads — drives the multi-truck notice.
  const step3DistinctTrucks = Array.from(new Set(
    step3VisibleLoads.map(step3LoadTruck).filter(t => t !== 'N/A')
  ));

  // The driver's (or team's) current truck assignment, read straight from the
  // equipment assignment rather than from selectedTruck, which later steps
  // overwrite.
  const currentAssignedUnit = (() => {
    if (selectedTeamId) {
      return teams.find(t => t.id === selectedTeamId)?.truckNumber || '';
    }
    const d = drivers.find(dr => `${dr.first_name} ${dr.last_name}` === selectedDriver);
    return d?.trucks?.unit_number || '';
  })();

  const displayTruck = (() => {
    // A unit typed at Step 8 wins over every derived value.
    if (truckNumberOverride !== null) return truckNumberOverride;
    if (!isPastStatementFlow) return selectedTruck;
    const units = Array.from(new Set(
      availableLoads
        .filter(l => selectedLoads.includes(l.id))
        .map(l => l.trucks?.unit_number)
        .filter((u): u is string => !!u)
    ));
    return units.length > 0 ? units.join(', ') : selectedTruck;
  })();

  // Step 3 group order: the driver's CURRENT truck (the one the statement header
  // will print) comes first, then the other units in order of first appearance.
  const step3OrderedGroups = (() => {
    if (!step3GroupByTruck) return step3LoadGroups;
    const current = `Truck #${String(displayTruck || '').replace(/^#/, '').trim()}`;
    const first = step3LoadGroups.filter(g => g.name === current);
    const rest = step3LoadGroups.filter(g => g.name !== current);
    return [...first, ...rest];
  })();

  const loadScheduledDeductionsForDriver = async () => {
    try {
      // For a team, the commission/deduction profile is linked by team_id (the
      // 'team' Lease-or-Rent row), not by name. For a solo driver, match by name.
      const query = supabase.from('scheduled_deductions').select('*');
      const { data, error } = selectedTeamId
        ? await query.eq('team_id', selectedTeamId)
        : await query.ilike('driver_name', selectedDriver.trim());

      if (error) throw error;
      
      // Fetch last charged dates for each scheduled deduction
      if (data && data.length > 0) {
        const deductionsWithHistory = await Promise.all(
          data.map(async (deduction) => {
            // Get last charged dates for each monthly deduction type
            const { data: history } = await supabase
              .from('monthly_deduction_history')
              .select('deduction_type, charged_date')
              .eq('scheduled_deduction_id', deduction.id)
              .order('charged_date', { ascending: false });
            
            const lastChargedDates: { [key: string]: string } = {};
            if (history) {
              history.forEach((h: any) => {
                if (!lastChargedDates[h.deduction_type]) {
                  lastChargedDates[h.deduction_type] = h.charged_date;
                }
              });
            }
            
            return {
              ...deduction,
              last_charged_dates: lastChargedDates
            };
          })
        );
        
        setAvailableScheduledDeductions(deductionsWithHistory);

        // Store commission rate from the first scheduled deduction that actually has one set
        const withCpm = deductionsWithHistory.find(
          (d: any) => d.commission_cpm !== null && d.commission_cpm !== undefined && String(d.commission_cpm).trim() !== ''
        );
        if (withCpm) {
          setDriverCommission(String(withCpm.commission_cpm));
        } else {
          setDriverCommission('');
        }
      } else {
        setAvailableScheduledDeductions([]);
      }
    } catch (error) {
      console.error('Error loading scheduled deductions:', error);
    }
  };

  const loadCreditsForDriver = async () => {
    try {
      const { data, error } = await supabase
        .from('driver_credits')
        .select('*')
        .ilike('driver_name', selectedDriver.trim())
        .eq('status', 'pending');

      if (error) throw error;
      setAvailableCredits(data || []);
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  };

  const loadFuelForDriver = async () => {
    try {
      console.log('Loading fuel for driver:', selectedDriver, 'Truck:', selectedTruck, 'Week:', weekStartDate, 'to', weekEndDate);

      // Query fuel data by truck/unit number (vehicle_id) AND by driver name as fallback
      // This matches fuel to the truck the driver is using, regardless of who fueled it
      let allData: any[] = [];

      // Primary: match by truck/unit number (vehicle_id field) — all unbilled (no date filter)
      if (selectedTruck && selectedTruck !== 'Not assigned') {
        const { data: truckData, error: truckError } = await supabase
          .from('pilot_fuel_imports')
          .select('*')
          .eq('vehicle_id', selectedTruck)
          .is('statement_id', null)
          .order('transaction_date', { ascending: true });

        if (truckError) {
          console.error('Fuel query by truck error:', truckError);
        } else {
          allData = truckData || [];
        }
      }

      // Fallback: also match by driver name in case vehicle_id wasn't set — all unbilled
      const { data: driverData, error: driverError } = await supabase
        .from('pilot_fuel_imports')
        .select('*')
        .ilike('driver_name', selectedDriver)
        .is('statement_id', null)
        .order('transaction_date', { ascending: true });

      if (driverError) {
        console.error('Fuel query by driver error:', driverError);
      } else {
        // Merge without duplicates
        const existingIds = new Set(allData.map(d => d.id));
        (driverData || []).forEach(fuel => {
          if (!existingIds.has(fuel.id)) {
            allData.push(fuel);
          }
        });
      }

      // Parse location to extract city and state if not already present
      const parsedData = allData.map(fuel => {
        if (!fuel.city && !fuel.state && fuel.location) {
          const locationParts = fuel.location.replace(/^\d+\s*/, '').trim().split(/\s+/);
          if (locationParts.length >= 2) {
            const state = locationParts[locationParts.length - 1];
            const city = locationParts.slice(0, -1).join(' ');
            return { ...fuel, city, state };
          }
        }
        return fuel;
      });

      console.log('Fuel data found:', parsedData?.length || 0, 'transactions (by truck + driver name)');
      setFuelData(parsedData || []);
      // Don't auto-select — let user choose which fuel to include
      setSelectedFuel([]);
    } catch (error) {
      console.error('Error loading fuel data:', error);
      toast.error('Failed to load fuel data');
    }
  };

  const nextStep = async () => {
    if (currentStep === 1 && !selectedDriver) {
      toast.error('Please select a driver');
      return;
    }
    
    if (currentStep === 1) {
      await loadScheduledDeductionsForDriver();
    } else if (currentStep === 3) {
      await loadDeductionsForDriver();
    } else if (currentStep === 4) {
      await loadMaintenanceDeductionsForDriver();
    } else if (currentStep === 5) {
      await loadCreditsForDriver();
    } else if (currentStep === 6) {
      await loadFuelForDriver();
    }
    
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Bucket a raw driver_type string into the three filter categories.
  // "Owner" covers owner_operator, owner, rent, lease, buy per product rules.
  const bucketDriverType = (rawType: string | null | undefined): 'company_driver' | 'contractor' | 'owner' | 'other' => {
    const t = (rawType || '').toLowerCase();
    if (t === 'company_driver') return 'company_driver';
    if (t === 'contractor') return 'contractor';
    if (['owner_operator', 'owner', 'rent', 'lease', 'buy'].includes(t)) return 'owner';
    return 'other';
  };

  const getStatementBucket = (statement: Statement): 'company_driver' | 'contractor' | 'owner' | 'other' => {
    // Authoritative: the scheduled deduction actually recorded on this statement's
    // line items. Owner-type deductions also carry a contractor snapshot, so the
    // statement's contractor_name field alone can't be used to bucket.
    const lease = statementLeaseMap[statement.id];
    if (lease) {
      const bucket = bucketDriverType(lease);
      if (bucket !== 'other') return bucket;
    }
    // Loads stamped 'cpm' come from selecting a company-driver (per-mile) profile,
    // so this is a company-driver statement even if it recorded no deduction rows.
    if (statementCommissionTypeMap[statement.id] === 'cpm') return 'company_driver';
    // Per-driver fallback for statements whose line items aren't loaded or
    // were generated without a scheduled deduction.
    const leaseInfo = driverLeaseTypeMap[(statement.driver_name || '').trim().toLowerCase()];
    if (leaseInfo) {
      const bucket = bucketDriverType(leaseInfo.lease_or_rent);
      if (bucket !== 'other') return bucket;
    }
    // Last resort: the drivers table. Treat presence of a contractor snapshot as
    // a hint only when nothing else identifies the driver.
    const driver = drivers.find(d => `${d.first_name} ${d.last_name}` === statement.driver_name);
    const fromDriver = bucketDriverType(driver?.driver_type);
    if (fromDriver !== 'other') return fromDriver;
    if (statement.contractor_name || statement.contractor_address) return 'contractor';
    return 'other';
  };

  // PDF filename convention: owner checks are named by truck ("Truck 119 07-27-26"),
  // driver checks by name ("John Doe 07-27-26") — so a dual-profile driver's two
  // checks for the same week get distinct filenames. Date is the Monday the
  // statement is paid through (same date printed on the check).
  const statementPdfFilename = (
    statement: Statement,
    bucketOverride?: 'company_driver' | 'contractor' | 'owner' | 'other',
  ): string => {
    const monday = mondayThrough(statement.week_end_date);
    const mm = String(monday.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(monday.getUTCDate()).padStart(2, '0');
    const yy = String(monday.getUTCFullYear() % 100).padStart(2, '0');
    const dateLabel = `${mm}-${dd}-${yy}`;
    const bucket = bucketOverride && bucketOverride !== 'other'
      ? bucketOverride
      : getStatementBucket(statement);
    return bucket === 'owner' && statement.truck_number
      ? `Truck ${statement.truck_number} ${dateLabel}.pdf`
      : `${statement.driver_name} ${dateLabel}.pdf`;
  };

  // Small profile badge shown on each statement card, driven by the
  // scheduled-deduction profile actually used to generate that statement.
  // Lets a driver's owner vs company statements be told apart at a glance.
  const renderProfileBadge = (statement: Statement) => {
    const cfg: Record<string, { label: string; cls: string }> = {
      owner: { label: 'OWNER', cls: 'bg-amber-100 text-amber-800' },
      company_driver: { label: 'COMPANY', cls: 'bg-emerald-100 text-emerald-800' },
      contractor: { label: 'CONTRACTOR', cls: 'bg-indigo-100 text-indigo-800' },
    };
    const c = cfg[getStatementBucket(statement)];
    if (!c) return null;
    return (
      <span className={`flex-shrink-0 mr-3 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${c.cls}`}>
        {c.label}
      </span>
    );
  };

  // Map each driver to the company they belong to, so statements can be
  // filtered by company. Built from the drivers list (which carries company_id
  // and the joined companies row). Keyed by BOTH driver id and normalized
  // "first last" name, because driver_statements rows don't always carry a
  // driver_id — many are keyed by driver_name only.
  const normName = (s: string) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const driverCompanyById = new Map<string, { id: string; name: string }>();
  const driverCompanyByName = new Map<string, { id: string; name: string }>();
  drivers.forEach((d: any) => {
    if (!d.company_id) return;
    const company = { id: d.company_id, name: d.companies?.name || 'Unknown Company' };
    driverCompanyById.set(d.id, company);
    const nameKey = normName(`${d.first_name || ''} ${d.last_name || ''}`);
    if (nameKey) driverCompanyByName.set(nameKey, company);
  });
  const companyForStatement = (statement: any) =>
    driverCompanyById.get(statement.driver_id) ||
    driverCompanyByName.get(normName(statement.driver_name)) ||
    null;

  // Distinct companies (only those that actually have drivers), for the dropdown.
  const companyOptions = Array.from(
    new Map(
      drivers
        .filter((d: any) => d.company_id)
        .map((d: any) => [d.company_id, d.companies?.name || 'Unknown Company'])
    ).entries()
  )
    .map(([id, name]) => ({ id: id as string, name: name as string }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const filteredStatements = statements.filter(statement => {
    const searchLower = searchTerm.toLowerCase();
    const contractorFromSd = driverLeaseTypeMap[(statement.driver_name || '').trim().toLowerCase()]?.contractor_name;
    const matchesSearch = !searchLower || (
      statement.driver_name.toLowerCase().includes(searchLower) ||
      statement.truck_number.toLowerCase().includes(searchLower) ||
      (statement.contractor_name || '').toLowerCase().includes(searchLower) ||
      (contractorFromSd || '').toLowerCase().includes(searchLower)
    );
    if (!matchesSearch) return false;

    if (companyFilter !== 'all') {
      const company = companyForStatement(statement);
      if (!company || company.id !== companyFilter) return false;
    }

    if (driverTypeFilter === 'all') return true;
    return getStatementBucket(statement) === driverTypeFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-none border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Driver Statements</h1>
            <p className="text-sm text-gray-500 mt-1">Generate and manage weekly driver statements</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => handleSendToReview()}
              disabled={selectedStatements.size === 0}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              title={selectedStatements.size === 0 ? 'Select at least one statement' : `Send ${selectedStatements.size} to review`}
            >
              <Send className="w-5 h-5" />
              Send to Review {selectedStatements.size > 0 ? `(${selectedStatements.size})` : ''}
            </button>
            <button
              onClick={() => handleSendToDrivers()}
              disabled={selectedStatements.size === 0}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              title={selectedStatements.size === 0 ? 'Select at least one statement' : `Send ${selectedStatements.size} to drivers`}
            >
              <Mail className="w-5 h-5" />
              Send to Drivers {selectedStatements.size > 0 ? `(${selectedStatements.size})` : ''}
            </button>
            <button
              onClick={() => { setDraftReviewEmail(reviewEmail); setShowEmailSettings(true); }}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              title="Email Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={openGenerateModal}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Generate Statement
            </button>
            <button
              onClick={openPastStatementModal}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Calendar className="w-5 h-5" />
              Generate Past Statement
            </button>
          </div>
        </div>

        {/* Search on its own line */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by driver name, truck number, or contractor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* All filters on one line: Company → Statement type → By Driver/Week */}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setCompanyFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                companyFilter === 'all'
                  ? 'bg-white text-blue-700 shadow-sm font-semibold'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All Companies
            </button>
            {companyOptions.map(c => (
              <button
                key={c.id}
                onClick={() => setCompanyFilter(c.id)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  companyFilter === c.id
                    ? 'bg-white text-blue-700 shadow-sm font-semibold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {([
              { v: 'all', label: 'All' },
              { v: 'company_driver', label: 'Company' },
              { v: 'contractor', label: 'Contractor' },
              { v: 'owner', label: 'Owner' },
            ] as const).map(opt => (
              <button
                key={opt.v}
                onClick={() => setDriverTypeFilter(opt.v)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  driverTypeFilter === opt.v
                    ? 'bg-white text-blue-700 shadow-sm font-semibold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {([
              { v: 'by_driver', label: 'By Driver' },
              { v: 'by_week', label: 'By Week' },
            ] as const).map(opt => (
              <button
                key={opt.v}
                onClick={() => setViewMode(opt.v)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  viewMode === opt.v
                    ? 'bg-white text-blue-700 shadow-sm font-semibold'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Statements Grouped by Driver */}
      <div className="flex-1 overflow-auto space-y-4 p-4">
        {filteredStatements.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-lg font-medium">No statements found</p>
            <p className="text-sm mt-1">Generate your first statement to get started</p>
          </div>
        ) : viewMode === 'by_driver' ? (
          (() => {
            // Group statements by driver
            const groupedByDriver = filteredStatements.reduce((acc, statement) => {
              if (!acc[statement.driver_name]) {
                acc[statement.driver_name] = [];
              }
              acc[statement.driver_name].push(statement);
              return acc;
            }, {} as Record<string, Statement[]>);

            return Object.entries(groupedByDriver).map(([driverName, driverStatements]) => {
              const isExpanded = expandedDrivers.has(driverName);
              
              // Group driver's statements by month
              const groupedByMonth = driverStatements.reduce((acc, statement) => {
                const monthKey = fmtDate(statement.week_start_date, { year: 'numeric', month: 'long' });
                if (!acc[monthKey]) {
                  acc[monthKey] = [];
                }
                acc[monthKey].push(statement);
                return acc;
              }, {} as Record<string, Statement[]>);

              const totalStatements = driverStatements.length;
              const totalNetPay = driverStatements.reduce((sum, s) => sum + s.net_pay, 0);

              // Determine bucket and any contractor snapshot present on recent statements.
              const bucket = getStatementBucket(driverStatements[0]);
              const latestContractor = driverStatements
                .slice()
                .sort((a, b) => new Date(b.week_end_date).getTime() - new Date(a.week_end_date).getTime())
                .find(s => s.contractor_name);
              // Fall back to the scheduled-deduction contractor name when no statement snapshot exists yet.
              const contractorNameForHeader = latestContractor?.contractor_name
                || driverLeaseTypeMap[driverName.trim().toLowerCase()]?.contractor_name;

              const bucketBadge =
                bucket === 'contractor'
                  ? { label: 'CONTRACTOR', className: 'bg-amber-100 text-amber-800' }
                  : bucket === 'company_driver'
                    ? { label: 'COMPANY DRIVER', className: 'bg-emerald-100 text-emerald-800' }
                    : bucket === 'owner'
                      ? { label: 'OWNER', className: 'bg-indigo-100 text-indigo-800' }
                      : null;

              return (
                <div key={driverName} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Driver Header */}
                  <div
                    className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
                    onClick={() => {
                      const newExpanded = new Set(expandedDrivers);
                      if (isExpanded) {
                        newExpanded.delete(driverName);
                      } else {
                        newExpanded.add(driverName);
                      }
                      setExpandedDrivers(newExpanded);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
                        <User className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-gray-900">{driverName}</h3>
                            {bucketBadge && (
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${bucketBadge.className}`}>
                                {bucketBadge.label}
                              </span>
                            )}
                          </div>
                          {bucket === 'contractor' && contractorNameForHeader && (
                            <p className="text-sm text-gray-700 mt-0.5">
                              <span className="text-gray-500">Pay to:</span> <span className="font-medium">{contractorNameForHeader}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-sm text-gray-600">{totalStatements} statement{totalStatements !== 1 ? 's' : ''}</p>
                          <p className="text-lg font-bold text-blue-600">${totalNetPay.toFixed(2)} total</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Statements by Month */}
                  {isExpanded && (
                    <div className="divide-y divide-gray-200">
                      {Object.entries(groupedByMonth)
                        .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
                        .map(([month, monthStatements]) => (
                        <div key={month} className="p-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {month}
                          </h4>
                          <div className="space-y-2">
                            {monthStatements
                              .sort((a, b) => new Date(b.week_start_date).getTime() - new Date(a.week_start_date).getTime())
                              .map((statement) => {
                                const isSelected = selectedStatements.has(statement.id);
                                return (
                              <div key={statement.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                                <div className="flex items-center justify-between">
                                  <button
                                    onClick={() => toggleStatementSelection(statement.id)}
                                    className="flex-shrink-0 mr-3"
                                    title={isSelected ? 'Deselect' : 'Select'}
                                  >
                                    {isSelected
                                      ? <CheckSquare className="w-5 h-5 text-blue-600" />
                                      : <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />}
                                  </button>
                                  {renderProfileBadge(statement)}
                                  <div className="flex-1 grid grid-cols-6 gap-4">
                                    <div>
                                      <p className="text-xs text-gray-500">Truck</p>
                                      <p className="text-sm font-medium text-gray-900">{statement.truck_number}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Week</p>
                                      <p className="text-sm text-gray-700">
                                        {fmtDate(statement.week_start_date, { month: 'short', day: 'numeric' })} - {fmtDate(statement.week_end_date, { month: 'short', day: 'numeric' })}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Revenue</p>
                                      <p className="text-sm font-medium text-gray-900">${statement.total_revenue.toFixed(2)}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Deductions</p>
                                      <p className="text-sm font-medium text-red-600">-${statement.total_deductions.toFixed(2)}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Fuel</p>
                                      <p className="text-sm font-medium text-red-600">-${statement.total_fuel.toFixed(2)}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Net Pay</p>
                                      <p className="text-sm font-bold text-blue-600">${statement.net_pay.toFixed(2)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 ml-4">
                                    <button
                                      onClick={() => handlePreviewStatement(statement)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="Preview Statement"
                                    >
                                      <Eye className="w-5 h-5" />
                                    </button>
                                    <button
                                      onClick={() => handleDownloadStatement(statement)}
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title="Download Statement"
                                    >
                                      <Download className="w-5 h-5" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setStatementToDelete(statement);
                                        setShowDeleteModal(true);
                                      }}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Delete Statement"
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                                );
                              })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            });
          })()
        ) : (
          (() => {
            // Group statements by week (week_start_date|week_end_date)
            const groupedByWeek = filteredStatements.reduce((acc, statement) => {
              const key = `${statement.week_start_date}|${statement.week_end_date}`;
              if (!acc[key]) acc[key] = [];
              acc[key].push(statement);
              return acc;
            }, {} as Record<string, Statement[]>);

            // ISO week number from a YYYY-MM-DD string (UTC components — no TZ shift).
            const isoWeek = (dateStr: string): number => {
              const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
              if (!m) return 0;
              const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
              d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
              const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
              return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
            };

            const sortedWeeks = Object.entries(groupedByWeek).sort(([a], [b]) => {
              const aStart = a.split('|')[0];
              const bStart = b.split('|')[0];
              return bStart.localeCompare(aStart);
            });

            return sortedWeeks.map(([key, weekStatements]) => {
              const [weekStart, weekEnd] = key.split('|');
              const totalRevenue = weekStatements.reduce((s, st) => s + st.total_revenue, 0);
              const totalNetPay = weekStatements.reduce((s, st) => s + st.net_pay, 0);
              const yearVal = (/^(\d{4})/.exec(weekStart) || [])[1] || '';
              const weekNum = isoWeek(weekStart);

              const isCollapsed = collapsedWeeks.has(key);

              return (
                <div key={key} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 cursor-pointer hover:from-orange-100 hover:to-amber-100 transition-colors"
                    onClick={() => {
                      const next = new Set(collapsedWeeks);
                      if (isCollapsed) next.delete(key);
                      else next.add(key);
                      setCollapsedWeeks(next);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isCollapsed ? <ChevronDown className="w-5 h-5 text-gray-600" /> : <ChevronUp className="w-5 h-5 text-gray-600" />}
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            {fmtDate(weekStart, { month: 'short', day: 'numeric' })} - {fmtDate(weekEnd, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </h3>
                          <p className="text-xs text-gray-500">Week #{weekNum} · {yearVal}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Statements / Revenue</p>
                          <p className="text-sm font-medium text-gray-900">{weekStatements.length} · ${totalRevenue.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Total Net Pay</p>
                          <p className="text-lg font-bold text-blue-600">${totalNetPay.toFixed(2)}</p>
                        </div>
                        {(() => {
                          const ids = weekStatements.map(s => s.id);
                          const allSelected = ids.length > 0 && ids.every(id => selectedStatements.has(id));
                          return (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleGroupSelection(ids); }}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                              {allSelected
                                ? <CheckSquare className="w-4 h-4 text-blue-600" />
                                : <Square className="w-4 h-4 text-gray-400" />}
                              {allSelected ? 'Deselect All in Week' : 'Select All in Week'}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {!isCollapsed && (
                  <div className="divide-y divide-gray-100">
                    {weekStatements
                      .slice()
                      .sort((a, b) => a.driver_name.localeCompare(b.driver_name))
                      .map(statement => {
                        const isSelected = selectedStatements.has(statement.id);
                        return (
                        <div key={statement.id} className="px-6 py-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => toggleStatementSelection(statement.id)}
                              className="flex-shrink-0 mr-3"
                              title={isSelected ? 'Deselect' : 'Select'}
                            >
                              {isSelected
                                ? <CheckSquare className="w-5 h-5 text-blue-600" />
                                : <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />}
                            </button>
                            {renderProfileBadge(statement)}
                            <div className="flex-1 grid grid-cols-6 gap-4">
                              <div>
                                <p className="text-xs text-gray-500">Driver</p>
                                <p className="text-sm font-medium text-gray-900">{statement.driver_name}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Truck</p>
                                <p className="text-sm font-medium text-gray-900">{statement.truck_number}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Revenue</p>
                                <p className="text-sm font-medium text-gray-900">${statement.total_revenue.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Deductions</p>
                                <p className="text-sm font-medium text-red-600">-${statement.total_deductions.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Fuel</p>
                                <p className="text-sm font-medium text-red-600">-${statement.total_fuel.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Net Pay</p>
                                <p className="text-sm font-bold text-blue-600">${statement.net_pay.toFixed(2)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => handlePreviewStatement(statement)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Preview Statement"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDownloadStatement(statement)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Download Statement"
                              >
                                <Download className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => {
                                  setStatementToDelete(statement);
                                  setShowDeleteModal(true);
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete Statement"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                  </div>
                  )}
                </div>
              );
            });
          })()
        )}
      </div>

      {/* Generate Statement Modal - Multi-step */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Generate Driver Statement for week {fmtDate(weekStartDate)} - {fmtDate(weekEndDate)}
                </h2>
                <p className="text-sm text-gray-500 mt-1">Step {currentStep} of 8</p>
              </div>
              <div className="flex items-center gap-4">
                {/* Running Total */}
                <div className="text-right">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Statement Total</p>
                  <p className={`text-2xl font-bold ${(() => {
                    // Calculate total from all selected items
                    let total = 0;
                    
                    // Add loads (positive)
                    selectedLoads.forEach(loadId => {
                      const load = availableLoads.find(l => l.id === loadId);
                      if (load) {
                        const totalMiles = getLoadMiles(load).total;
                        const driverPay = load.dispatches?.[0]?.driver_pay || 0;
                        const freightAmount = editedFreightAmounts[loadId] ?? (driverPay || load.total_rate || 0);
                        total += calculateDriverAmount(freightAmount, totalMiles, driverCommission);
                      }
                    });

                    // Subtract deductions (negative)
                    selectedDeductions.forEach(deductionId => {
                      const chargeAmount = deductionChargeAmounts[deductionId] || 0;
                      total -= chargeAmount;
                    });

                    // Subtract maintenance deductions (negative)
                    selectedMaintenanceDeductions.forEach(deductionId => {
                      const chargeAmount = maintenanceChargeAmounts[deductionId] || 0;
                      total -= chargeAmount;
                    });

                    // Subtract scheduled deductions (negative)
                    availableScheduledDeductions.filter(sd => selectedScheduledDeductions.includes(sd.id)).forEach(sd => {
                      total -= getScheduledDeductionTotal(sd);
                    });

                    // Subtract selected fuel (negative)
                    fuelData.filter(f => selectedFuel.includes(f.id)).forEach(fuel => {
                      total -= Math.abs(getFuelAmount(fuel));
                    });

                    // Add credits (positive)
                    selectedCredits.forEach(creditId => {
                      const credit = availableCredits.find(c => c.id === creditId);
                      if (credit) {
                        total += credit.amount;
                      }
                    });

                    return total >= 0 ? 'text-green-600' : 'text-red-600';
                  })()}`}>
                    ${(() => {
                      // Calculate total from all selected items
                      let total = 0;

                      // Add loads (positive)
                      selectedLoads.forEach(loadId => {
                        const load = availableLoads.find(l => l.id === loadId);
                        if (load) {
                          const totalMiles = getLoadMiles(load).total;
                          const driverPay = load.dispatches?.[0]?.driver_pay || 0;
                          const freightAmount = editedFreightAmounts[loadId] ?? (driverPay || load.total_rate || 0);
                          total += calculateDriverAmount(freightAmount, totalMiles, driverCommission);
                        }
                      });

                      // Subtract deductions (negative)
                      selectedDeductions.forEach(deductionId => {
                        const chargeAmount = deductionChargeAmounts[deductionId] || 0;
                        total -= chargeAmount;
                      });

                      // Subtract maintenance deductions (negative)
                      selectedMaintenanceDeductions.forEach(deductionId => {
                        const chargeAmount = maintenanceChargeAmounts[deductionId] || 0;
                        total -= chargeAmount;
                      });

                      // Subtract scheduled deductions (negative)
                      availableScheduledDeductions.filter(sd => selectedScheduledDeductions.includes(sd.id)).forEach(sd => {
                        total -= getScheduledDeductionTotal(sd);
                      });

                      // Subtract selected fuel (negative)
                      fuelData.filter(f => selectedFuel.includes(f.id)).forEach(fuel => {
                        total -= Math.abs(getFuelAmount(fuel));
                      });

                      // Add credits (positive)
                      selectedCredits.forEach(creditId => {
                        const credit = availableCredits.find(c => c.id === creditId);
                        if (credit) {
                          total += credit.amount;
                        }
                      });

                      return total.toFixed(2);
                    })()}
                  </p>
                </div>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Step 1: Select Driver */}
              {currentStep === 1 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Select Driver</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Statement period: {fmtDate(weekStartDate)} - {fmtDate(weekEndDate)}
                  </p>
                  
                  {/* Search Bar */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by driver name or truck number..."
                      value={driverSearchTerm}
                      onChange={(e) => setDriverSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {/* Teams (one combined statement for both drivers) */}
                    {teams.filter(team => {
                      const searchLower = driverSearchTerm.toLowerCase();
                      return team.label.toLowerCase().includes(searchLower)
                        || (team.truckNumber || '').toLowerCase().includes(searchLower);
                    }).map((team) => (
                      <div
                        key={team.id}
                        onClick={() => handleTeamSelect(team.id)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedTeamId === team.id
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 flex items-center gap-2">
                              <span className="text-[10px] font-bold text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded">TEAM</span>
                              {team.label}
                            </p>
                            {team.truckNumber && (
                              <p className="text-sm text-gray-500">Truck: {team.truckNumber}</p>
                            )}
                          </div>
                          {selectedTeamId === team.id && (
                            <div className="w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {drivers.filter(driver => {
                      const searchLower = driverSearchTerm.toLowerCase();
                      const driverName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
                      const truckNumber = driver.trucks?.unit_number?.toLowerCase() || '';
                      return driverName.includes(searchLower) || truckNumber.includes(searchLower);
                    }).map((driver) => (
                      <div
                        key={driver.id}
                        onClick={() => handleDriverSelect(driver.id)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedDriver === `${driver.first_name} ${driver.last_name}`
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{driver.first_name} {driver.last_name}</p>
                            {driver.trucks?.unit_number && (
                              <p className="text-sm text-gray-500">Truck: {driver.trucks.unit_number}</p>
                            )}
                          </div>
                          {selectedDriver === `${driver.first_name} ${driver.last_name}` && (
                            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Select Loads */}
              {currentStep === 3 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Select Completed Loads</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Driver: <span className="font-medium">{selectedDriver}</span> | Truck: <span className="font-medium">{displayTruck}</span>
                  </p>
                  {(() => {
                    // Dual-profile driver notice. Such a driver gets one statement per
                    // scheduled-deduction profile, which is why already-billed loads stay
                    // in this list. Spell that out and show which profile is still pending.
                    if (availableScheduledDeductions.length < 2) return null;
                    const sameWeek = (a: string | null, b: string | null) =>
                      !!a && !!b && String(a).slice(0, 10) === String(b).slice(0, 10);
                    const allRefs = Object.values(loadStatementRefs).flat();
                    const profiles = availableScheduledDeductions.map(sd => {
                      const bucket = bucketDriverType(sd.lease_or_rent);
                      const payMode = bucket === 'company_driver' ? 'cpm' : 'percentage';
                      return {
                        id: sd.id,
                        label: (sd.lease_or_rent || 'unknown').replace(/_/g, ' ').toUpperCase(),
                        // "Generated" = a statement for THIS week already billed a load
                        // under this profile's pay mode.
                        generated: allRefs.some(r => r.payMode === payMode && sameWeek(r.weekEnd, weekEndDate)),
                      };
                    });
                    const pending = profiles.filter(p => !p.generated).length;
                    return (
                      <div className="mb-4 p-3 rounded-lg border border-amber-300 bg-amber-50">
                        <p className="text-sm font-medium text-amber-900">
                          This driver has {availableScheduledDeductions.length} scheduled deductions — one statement is expected per profile.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {profiles.map(pr => (
                            <span
                              key={pr.id}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                pr.generated ? 'bg-green-600 text-white' : 'bg-white text-amber-900 border border-amber-400'
                              }`}
                            >
                              {pr.label} — {pr.generated ? 'statement generated' : 'not generated yet'}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-amber-800 mt-2">
                          {pending === 0
                            ? 'Both statements for this week already exist. Loads below stay listed so you can still correct them.'
                            : 'Loads already billed on one statement stay listed on purpose, so they can go on the other profile\u2019s statement too. Check the badges before selecting.'}
                        </p>
                      </div>
                    );
                  })()}
                  {step3DistinctTrucks.length > 1 && (
                    <div className="mb-4 p-3 rounded-lg border border-blue-300 bg-blue-50">
                      <p className="text-sm font-medium text-blue-900">
                        Multiple trucks on this driver's loads — {step3DistinctTrucks.length} different units: {step3DistinctTrucks.join(', ')}.
                      </p>
                      <p className="text-xs text-blue-800 mt-1">
                        The statement header prints one truck (currently <span className="font-semibold">#{displayTruck}</span>). Loads below are grouped per unit so you can check each one before selecting.
                      </p>
                    </div>
                  )}
                  {step3HiddenLoads.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowBilledLoads(!showBilledLoads)}
                      className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline"
                    >
                      {showBilledLoads
                        ? `Hide ${step3HiddenLoads.length} already-billed load${step3HiddenLoads.length === 1 ? '' : 's'}`
                        : `Show ${step3HiddenLoads.length} already-billed load${step3HiddenLoads.length === 1 ? '' : 's'}`}
                    </button>
                  )}
                  {step3VisibleLoads.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      {step3HiddenLoads.length > 0 ? (
                        <p className="mb-2">
                          All {step3HiddenLoads.length} completed load{step3HiddenLoads.length === 1 ? ' is' : 's are'} already on a statement — use the button above to see them.
                        </p>
                      ) : (
                        <>
                      <p className="mb-2">No completed loads found for this driver</p>
                      <p className="text-sm">This may be because loads don't have driver_id set in the database.</p>
                      <p className="text-sm">You can skip this step and continue to deductions.</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {step3OrderedGroups.map((group) => (
                      <div key={group.name}>
                      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-3 py-1.5 mb-2 rounded-md bg-gray-100 border border-gray-200">
                        <p className="text-sm font-semibold text-gray-800">{group.name}</p>
                        <p className="text-xs text-gray-600">
                          {group.loads.length} load{group.loads.length === 1 ? '' : 's'}
                          {!step3GroupByTruck && group.trucks.length > 0 && (
                            <> · Truck{group.trucks.length === 1 ? '' : 's'} {group.trucks.join(', ')}</>
                          )}
                        </p>
                      </div>
                      <div className="space-y-2">
                      {group.loads.map((load) => {
                        const isArchived = load.sent_to_accounting === true || load.status?.toUpperCase() === 'ARCHIVED';
                        const isInvoiced = load.invoice_sent === true;
                        // Dispatched (in-progress) loads are shown for context but cannot
                        // be billed yet — render them read-only / non-selectable.
                        const isDispatched = load.status?.toUpperCase() === 'DISPATCHED';
                        const pickupLoc = load.pickup_city && load.pickup_state
                          ? `${load.pickup_city}, ${load.pickup_state}`
                          : (load.pickup_location || 'N/A');
                        const deliveryLoc = load.delivery_city && load.delivery_state
                          ? `${load.delivery_city}, ${load.delivery_state}`
                          : (load.delivery_location || 'N/A');

                        return (
                          <div
                            key={load.id}
                            onClick={() => {
                              if (isDispatched) return;
                              if (selectedLoads.includes(load.id)) {
                                setSelectedLoads(selectedLoads.filter(id => id !== load.id));
                              } else {
                                setSelectedLoads([...selectedLoads, load.id]);
                              }
                            }}
                            className={`p-4 border-2 rounded-lg transition-colors ${
                              isDispatched
                                ? 'border-gray-200 bg-gray-100 opacity-70'
                                : isInvoiced
                                  ? 'border-purple-300 bg-purple-50 cursor-pointer'
                                  : isArchived
                                    ? 'border-green-300 bg-green-50 cursor-pointer'
                                    : 'border-gray-200 bg-white cursor-pointer hover:border-gray-300'
                            } ${!isDispatched && selectedLoads.includes(load.id) ? 'ring-2 ring-blue-400' : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium text-gray-900">Load #{load.load_number}</p>
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-slate-800 text-white tracking-wide"
                                    title="Truck on this load"
                                  >
                                    TRUCK {step3LoadTruck(load)}
                                  </span>
                                  {isDispatched ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-400 text-white">
                                      Dispatched — not delivered yet
                                    </span>
                                  ) : isInvoiced ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-600 text-white">
                                      Invoiced
                                    </span>
                                  ) : isArchived && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white">
                                      Ready for Billing
                                    </span>
                                  )}
                                  {(() => {
                                    // Already-billed marker. Purely informational: these loads
                                    // remain selectable because a dual-profile driver's load
                                    // legitimately appears on both of their statements.
                                    const refs = loadStatementRefs[load.id] || [];
                                    if (!refs.length) return null;
                                    // Name the profile exactly as the banner chip above does.
                                    // Deriving it from the driver's own scheduled deductions
                                    // avoids calling an OWNER/LEASE/RENT profile "contractor" —
                                    // this app treats Owner and Contractor as distinct categories.
                                    const nameForMode = (mode: string | null): string | null => {
                                      if (mode !== 'cpm' && mode !== 'percentage') return null;
                                      const wantCompany = mode === 'cpm';
                                      const names = Array.from(new Set(
                                        availableScheduledDeductions
                                          .filter(sd => (bucketDriverType(sd.lease_or_rent) === 'company_driver') === wantCompany)
                                          .map(sd => (sd.lease_or_rent || '').replace(/_/g, ' ').toUpperCase())
                                          .filter(Boolean)
                                      ));
                                      // Only name it when it is unambiguous; otherwise stay neutral.
                                      if (names.length === 1) return names[0];
                                      return wantCompany ? 'COMPANY DRIVER' : 'PERCENTAGE-PAY';
                                    };
                                    const modes = Array.from(new Set(
                                      refs.map(r => nameForMode(r.payMode)).filter(Boolean) as string[]
                                    ));
                                    const weeks = Array.from(new Set(
                                      refs.map(r => r.weekEnd).filter(Boolean) as string[]
                                    ));
                                    return (
                                      <span
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500 text-white"
                                        title={weeks.length ? `Statement week ending ${weeks.map(w => fmtDate(w)).join(', ')}` : undefined}
                                      >
                                        {modes.length ? `Already on ${modes.join(' + ')} statement` : 'Already on a statement'}
                                        {weeks.length === 1 ? ` (wk ending ${fmtDate(weeks[0])})` : ''}
                                      </span>
                                    );
                                  })()}
                                </div>
                                <p className="text-sm text-gray-500">
                                  Pickup: {load.pickup_date ? fmtDate(load.pickup_date) : 'N/A'} | Delivered: {load.delivery_date ? fmtDate(load.delivery_date) : 'N/A'} | Driver Pay: ${(load.dispatches?.[0]?.driver_pay || 0).toFixed(2)}
                                </p>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  Driver: <span className="font-medium text-gray-700">{step3LoadDriver(load)}</span>
                                </p>
                                <p className="text-sm text-gray-600 mt-0.5">
                                  <span className="font-medium">{pickupLoc}</span> <span className="text-gray-400">→</span> <span className="font-medium">{deliveryLoc}</span>
                                </p>
                                {load.billing_note && (
                                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                    <p className="text-xs font-medium text-blue-900">Billing Note:</p>
                                    <p className="text-xs text-blue-800">{load.billing_note}</p>
                                  </div>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                checked={!isDispatched && selectedLoads.includes(load.id)}
                                disabled={isDispatched}
                                title={isDispatched ? 'This load is still dispatched and cannot be billed until it is delivered' : undefined}
                                onChange={() => {}}
                                className={`w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                                  isDispatched ? 'cursor-not-allowed opacity-50' : ''
                                }`}
                              />
                            </div>
                          </div>
                        );
                      })}
                      </div>
                      </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Select Deductions */}
              {currentStep === 4 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">Select Deductions</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Driver: <span className="font-medium">{selectedDriver}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => { setEditingDeductionId(null); setShowAddDeductionModal(true); }}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Deduction
                    </button>
                  </div>
                  
                  {/* Search Bar */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search deductions by driver name, reason, or amount..."
                      value={deductionSearchTerm}
                      onChange={(e) => setDeductionSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {availableDeductions.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No unpaid deductions found</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {availableDeductions.filter(deduction => {
                        const searchLower = deductionSearchTerm.toLowerCase();
                        const driverName = deduction.driver_name?.toLowerCase() || '';
                        const description = deduction.description?.toLowerCase() || '';
                        const deductionType = deduction.deduction_type?.toLowerCase() || '';
                        const amount = deduction.amount.toString();
                        return driverName.includes(searchLower) || description.includes(searchLower) || deductionType.includes(searchLower) || amount.includes(searchLower);
                      }).map((deduction) => {
                        const isSelected = selectedDeductions.includes(deduction.id);
                        const remainingAmount = deduction.remaining_amount !== undefined ? deduction.remaining_amount : deduction.amount;
                        const chargeAmount = deductionChargeAmounts[deduction.id] || remainingAmount || 0;
                        const toggleDeduction = () => {
                          if (isSelected) {
                            setSelectedDeductions(selectedDeductions.filter(id => id !== deduction.id));
                            // Remove charge amount
                            const newAmounts = { ...deductionChargeAmounts };
                            delete newAmounts[deduction.id];
                            setDeductionChargeAmounts(newAmounts);
                          } else {
                            setSelectedDeductions([...selectedDeductions, deduction.id]);
                            // Initialize with remaining amount
                            setDeductionChargeAmounts({
                              ...deductionChargeAmounts,
                              [deduction.id]: remainingAmount
                            });
                          }
                        };

                        return (
                          <div
                            key={deduction.id}
                            onClick={toggleDeduction}
                            className={`p-4 border-2 rounded-lg transition-colors cursor-pointer ${
                              isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start gap-4">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                              />
                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <p className="font-medium text-gray-900">{deduction.deduction_type}</p>
                                    <p className="text-sm text-gray-600">Driver: {deduction.driver_name || 'Unknown'}</p>
                                    <p className="text-sm text-gray-500">{deduction.description}</p>
                                    <p className="text-sm text-gray-500">
                                      Date: {fmtDate(deduction.deduction_date)}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    {deduction.id.startsWith('custom-') && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Pre-fill the Add Deduction form and switch it to edit mode.
                                          setEditingDeductionId(deduction.id.replace(/^custom-/, ''));
                                          setNewDeductionType(deduction.description || deduction.deduction_type || '');
                                          setNewDeductionAmount(String(deduction.amount ?? ''));
                                          setNewDeductionDate(String(deduction.deduction_date || '').slice(0, 10) || new Date().toISOString().split('T')[0]);
                                          setShowAddDeductionModal(true);
                                        }}
                                        title="Edit this deduction before selecting it"
                                        className="inline-flex items-center gap-1 mb-1 px-2 py-1 text-xs font-medium text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50"
                                      >
                                        <Pencil className="w-3 h-3" />
                                        Edit
                                      </button>
                                    )}
                                    <p className="text-sm font-medium text-gray-900">
                                      Total: ${deduction.amount.toFixed(2)}
                                    </p>
                                    {deduction.charged_amount !== undefined && deduction.charged_amount > 0 && (
                                      <>
                                        <p className="text-xs text-green-600">
                                          Paid: ${deduction.charged_amount.toFixed(2)}
                                        </p>
                                        <p className="text-xs text-orange-600 font-medium">
                                          Remaining: ${(deduction.remaining_amount || 0).toFixed(2)}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {isSelected && (() => {
                                  const remainingAfter = Math.max(0, (remainingAmount || 0) - (chargeAmount || 0));
                                  const isPartial = chargeAmount > 0 && chargeAmount < (remainingAmount || 0);
                                  const willClear = chargeAmount > 0 && remainingAfter === 0;
                                  return (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Amount to Charge This Statement
                                      </label>
                                      <input
                                        type="number"
                                        value={chargeAmount}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          const value = parseFloat(e.target.value) || 0;
                                          const maxAmount = remainingAmount || 0;
                                          const finalValue = Math.min(Math.max(0, value), maxAmount);
                                          setDeductionChargeAmounts({
                                            ...deductionChargeAmounts,
                                            [deduction.id]: finalValue
                                          });
                                        }}
                                        step="0.01"
                                        min="0"
                                        max={remainingAmount || 0}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter amount to charge"
                                      />
                                      <div className="mt-2 flex items-center justify-between text-xs">
                                        <span className="text-gray-500">
                                          Max: ${(remainingAmount || 0).toFixed(2)}
                                        </span>
                                        <span
                                          className={`font-semibold ${
                                            willClear
                                              ? 'text-green-700'
                                              : isPartial
                                                ? 'text-orange-600'
                                                : 'text-gray-500'
                                          }`}
                                        >
                                          Remaining after this statement: ${remainingAfter.toFixed(2)}
                                          {willClear && ' — will clear'}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Select Maintenance Deductions */}
              {currentStep === 5 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Select Maintenance Deductions</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Driver: <span className="font-medium">{selectedDriver}</span>
                  </p>
                  
                  {availableMaintenanceDeductions.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No unpaid maintenance deductions found</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {availableMaintenanceDeductions.map((deduction: any) => {
                        const isSelected = selectedMaintenanceDeductions.includes(deduction.id);
                        const chargeAmount = maintenanceChargeAmounts[deduction.id] || deduction.driver_remaining_amount || 0;
                        const toggleMaintenance = () => {
                          if (isSelected) {
                            setSelectedMaintenanceDeductions(selectedMaintenanceDeductions.filter(id => id !== deduction.id));
                            // Remove charge amount
                            const newAmounts = { ...maintenanceChargeAmounts };
                            delete newAmounts[deduction.id];
                            setMaintenanceChargeAmounts(newAmounts);
                          } else {
                            setSelectedMaintenanceDeductions([...selectedMaintenanceDeductions, deduction.id]);
                            // Initialize with remaining amount
                            setMaintenanceChargeAmounts({
                              ...maintenanceChargeAmounts,
                              [deduction.id]: deduction.driver_remaining_amount
                            });
                          }
                        };

                        return (
                          <div
                            key={deduction.id}
                            onClick={toggleMaintenance}
                            className={`p-4 border rounded-lg transition-colors cursor-pointer ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                          >
                            <div className="flex items-start gap-4">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                              />
                              
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{deduction.maintenance_type}</p>
                                <p className="text-sm text-gray-600">Log: {deduction.log_number}</p>
                                <p className="text-sm text-gray-500">
                                  Unit: {deduction.trucks?.unit_number || deduction.trailers?.unit_number || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Date: {fmtDate(deduction.service_date)}
                                </p>
                                
                                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                                  <div>
                                    <span className="text-gray-600">Total Amount:</span>
                                    <span className="ml-1 font-medium">${deduction.driver_payment_amount.toFixed(2)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Already Charged:</span>
                                    <span className="ml-1 font-medium text-orange-600">${(deduction.driver_charged_amount || 0).toFixed(2)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Remaining:</span>
                                    <span className="ml-1 font-medium text-green-600">${(deduction.driver_remaining_amount || 0).toFixed(2)}</span>
                                  </div>
                                </div>
                                
                                {isSelected && (
                                  <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Amount to Charge on This Statement
                                    </label>
                                    <input
                                      type="number"
                                      value={chargeAmount}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        const value = parseFloat(e.target.value) || 0;
                                        const maxAmount = deduction.driver_remaining_amount || 0;
                                        // Don't allow more than remaining amount
                                        const finalValue = Math.min(Math.max(0, value), maxAmount);
                                        setMaintenanceChargeAmounts({
                                          ...maintenanceChargeAmounts,
                                          [deduction.id]: finalValue
                                        });
                                      }}
                                      step="0.01"
                                      min="0"
                                      max={deduction.driver_remaining_amount || 0}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="Enter amount to charge"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      Max: ${(deduction.driver_remaining_amount || 0).toFixed(2)}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Select Scheduled Deductions */}
              {currentStep === 2 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Select Scheduled Deductions</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Driver: <span className="font-medium">{selectedDriver}</span>
                  </p>
                  {availableScheduledDeductions.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No scheduled deductions found for this driver</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {availableScheduledDeductions.map((deduction) => {
                        const formatFriday = (day: string | undefined) => {
                          if (!day) return '';
                          const map: { [key: string]: string } = {
                            '1st_friday': '1st Friday',
                            '2nd_friday': '2nd Friday',
                            '3rd_friday': '3rd Friday',
                            '4th_friday': '4th Friday'
                          };
                          return map[day] || day;
                        };

                        const isSelected = selectedScheduledDeductions.includes(deduction.id);
                        const rawCommission = (deduction.commission_cpm ?? '').toString().trim();
                        const commissionNum = parseFloat(rawCommission.replace('%', ''));
                        const isPercentage = rawCommission.includes('%') || (isFinite(commissionNum) && commissionNum > 100);
                        const driverType = (deduction.lease_or_rent || '').toLowerCase();
                        const isCompanyDriver = driverType === 'company_driver';
                        let commissionLabel: string | null = null;
                        if (rawCommission) {
                          if (isCompanyDriver && !isPercentage) {
                            const perMile = commissionNum >= 10 ? commissionNum / 100 : commissionNum;
                            commissionLabel = `CPM: $${perMile.toFixed(2)}/mile`;
                          } else if (!isCompanyDriver && isPercentage) {
                            commissionLabel = `Commission: ${commissionNum}% (driver keeps ${(100 - commissionNum).toFixed(0)}%)`;
                          } else {
                            commissionLabel = isPercentage
                              ? `Commission: ${commissionNum}%`
                              : `CPM: $${(commissionNum >= 10 ? commissionNum / 100 : commissionNum).toFixed(2)}/mile`;
                          }
                        }

                        return (
                          <div
                            key={deduction.id}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedScheduledDeductions([]);
                                setDriverCommission('');
                              } else {
                                setSelectedScheduledDeductions([deduction.id]);
                                // Pay mode follows the profile picked here: OWNER → "12%"
                                // (full freight on top + 12% company-fee deduction),
                                // COMPANY_DRIVER → "0.60" (per-mile). Without this, the rate
                                // stayed locked to whichever profile happened to load first.
                                setDriverCommission(rawCommission);
                                // The statement's unit is the driver's CURRENT truck
                                // assignment. The profile's own unit_number is free text
                                // on scheduled_deductions — never validated against the
                                // trucks table — so it can name a unit that no longer
                                // exists. Use it only when there is no current assignment.
                                if (currentAssignedUnit) {
                                  setSelectedTruck(currentAssignedUnit);
                                } else if (deduction.unit_number) {
                                  setSelectedTruck(deduction.unit_number);
                                }
                              }
                            }}
                            className={`p-4 border rounded-lg cursor-pointer transition ${
                              isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <p className="font-medium text-gray-900">{deduction.lease_or_rent.toUpperCase()}</p>
                                  {commissionLabel && (
                                    <span
                                      className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                        isCompanyDriver ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                                      }`}
                                    >
                                      {commissionLabel}
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500 mt-1 grid grid-cols-2 gap-2">
                                  {deduction.truck_weekly > 0 && <span>Truck Payment Weekly: ${deduction.truck_weekly.toFixed(2)}</span>}
                                  {deduction.truck_monthly > 0 && (
                                    <span className="flex flex-col">
                                      <span>Truck Payment Monthly: ${deduction.truck_monthly.toFixed(2)}</span>
                                      <span className="text-xs text-blue-600">Charges on: {formatFriday(deduction.truck_monthly_charge_day)}</span>
                                      {(deduction as any).last_charged_dates?.truck_monthly ? (
                                        <span className="text-xs text-gray-500">
                                          Last charged: {fmtDate((deduction as any).last_charged_dates.truck_monthly)}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-orange-600 font-medium">
                                          Last charged: Never
                                        </span>
                                      )}
                                    </span>
                                  )}
                                  {deduction.physical_weekly > 0 && <span>Insurance Phys.Dem. Weekly: ${deduction.physical_weekly.toFixed(2)}</span>}
                                  {deduction.physical_monthly > 0 && (
                                    <span className="flex flex-col">
                                      <span>Insurance Phys.Dem. Monthly: ${deduction.physical_monthly.toFixed(2)}</span>
                                      <span className="text-xs text-green-600">Charges on: {formatFriday(deduction.physical_monthly_charge_day)}</span>
                                      {(deduction as any).last_charged_dates?.physical_monthly ? (
                                        <span className="text-xs text-gray-500">
                                          Last charged: {fmtDate((deduction as any).last_charged_dates.physical_monthly)}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-orange-600 font-medium">
                                          Last charged: Never
                                        </span>
                                      )}
                                    </span>
                                  )}
                                  {deduction.trailer > 0 && <span>Trailer: ${deduction.trailer.toFixed(2)}</span>}
                                  {deduction.cargo > 0 && <span>Cargo: ${deduction.cargo.toFixed(2)}</span>}
                                  {(deduction.escrow ?? 0) > 0 && <span>Escrow: ${deduction.escrow.toFixed(2)}</span>}
                                  {deduction.fuel_card_fee > 0 && <span>Fuel Card: ${deduction.fuel_card_fee.toFixed(2)}</span>}
                                  {deduction.eld_equipment_fee > 0 && <span>ELD Equipment Fee: ${deduction.eld_equipment_fee.toFixed(2)}</span>}
                                  {deduction.eld_weekly > 0 && <span>ELD Weekly Fee: ${deduction.eld_weekly.toFixed(2)}</span>}
                                  {deduction.prepass_weekly > 0 && <span>Prepass Weekly: ${deduction.prepass_weekly.toFixed(2)}</span>}
                                  {deduction.occupational_weekly > 0 && <span>Occup. Weekly: ${deduction.occupational_weekly.toFixed(2)}</span>}
                                  {deduction.occupational_monthly > 0 && (
                                    <span className="flex flex-col">
                                      <span>Occup. Monthly: ${deduction.occupational_monthly.toFixed(2)}</span>
                                      <span className="text-xs text-purple-600">Charges on: {formatFriday(deduction.occupational_monthly_charge_day)}</span>
                                      {(deduction as any).last_charged_dates?.occupational_monthly ? (
                                        <span className="text-xs text-gray-500">
                                          Last charged: {fmtDate((deduction as any).last_charged_dates.occupational_monthly)}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-orange-600 font-medium">
                                          Last charged: Never
                                        </span>
                                      )}
                                    </span>
                                  )}
                                  {deduction.highway_registration_weekly > 0 && <span>Highway Weekly: ${deduction.highway_registration_weekly.toFixed(2)}</span>}
                                </div>
                              </div>
                              <input
                                type="radio"
                                name="scheduled-deduction"
                                checked={isSelected}
                                readOnly
                                onClick={(e) => e.stopPropagation()}
                                className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Step 6: Select Credits */}
              {currentStep === 6 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">Select Credits</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Driver: <span className="font-medium">{selectedDriver}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAddCreditModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Credit
                    </button>
                  </div>
                  {availableCredits.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No unused credits found for this driver</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {availableCredits.map((credit) => (
                        <div
                          key={credit.id}
                          onClick={() => {
                            if (selectedCredits.includes(credit.id)) {
                              setSelectedCredits(selectedCredits.filter(id => id !== credit.id));
                            } else {
                              setSelectedCredits([...selectedCredits, credit.id]);
                            }
                          }}
                          className={`p-4 border rounded-lg transition-colors cursor-pointer ${
                            selectedCredits.includes(credit.id)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{credit.reason}</p>
                              <p className="text-sm text-gray-500">
                                Date: {fmtDate(credit.date)} | Amount: ${credit.amount.toFixed(2)}
                              </p>
                            </div>
                            <input
                              type="checkbox"
                              checked={selectedCredits.includes(credit.id)}
                              onChange={() => {}}
                              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 7: Fuel Data */}
              {currentStep === 7 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">Fuel Transactions</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Driver: <span className="font-medium">{selectedDriver}</span> | Truck: <span className="font-medium">{displayTruck}</span>
                      </p>
                    </div>
                    {fuelData.length > 0 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedFuel(fuelData.map(f => f.id))}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Select All
                        </button>
                        <button
                          onClick={() => setSelectedFuel([])}
                          className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                          Deselect All
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Select which fuel transactions to include in this statement. Unselected transactions will remain available for future statements.</p>
                  {fuelData.length > 0 && (() => {
                    const mode = getFuelPricingMode();
                    const missing = mode.discounted ? fuelData.filter(fuelMissingInvoice).length : 0;
                    return (
                      <div className={`mb-3 p-3 rounded-lg border text-xs ${missing > 0 ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                        <p>
                          Fuel price: <span className="font-semibold">{mode.discounted ? 'Discounted (invoice)' : 'Retail'}</span> — {mode.reason}
                        </p>
                        {missing > 0 && (
                          <p className="mt-1 font-semibold">
                            ⚠️ {missing} of {fuelData.length} fuel transactions were imported without a discounted (invoice) amount, so they are charged at RETAIL. Re-check the fuel file's "Total" column and re-import before generating this statement.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  {fuelData.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No unbilled fuel transactions found</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {fuelData.map((fuel) => (
                        <div
                          key={fuel.id}
                          onClick={() => {
                            if (selectedFuel.includes(fuel.id)) {
                              setSelectedFuel(selectedFuel.filter(id => id !== fuel.id));
                            } else {
                              setSelectedFuel([...selectedFuel, fuel.id]);
                            }
                          }}
                          className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedFuel.includes(fuel.id)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedFuel.includes(fuel.id)}
                              onChange={() => {}}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{fuel.location}</p>
                              <p className="text-sm text-gray-500">
                                Date: {fmtDate(fuel.transaction_date)} | Gallons: {fuel.gallons.toFixed(2)} | Amount: ${(getFuelAmount(fuel)).toFixed(2)}
                                {getFuelPricingMode().discounted && fuelMissingInvoice(fuel) && (
                                  <span className="ml-2 text-amber-700 font-semibold">(retail — no discounted amount on import)</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center">
                        <p className="text-sm font-medium text-blue-900">
                          Selected: {selectedFuel.length} of {fuelData.length} transactions
                        </p>
                        <p className="text-sm font-bold text-blue-900">
                          Total Fuel: ${fuelData.filter(f => selectedFuel.includes(f.id)).reduce((sum, f) => sum + (getFuelAmount(f)), 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 8: Preview & Edit */}
              {currentStep === 8 && (() => {
                // Any selected scheduled deduction with a pay-to company set
                // (contractor, owner, lease, rent) makes this a "Pay To" statement.
                const contractorDeduction = availableScheduledDeductions.find(
                  sd => selectedScheduledDeductions.includes(sd.id)
                    && (sd.contractor_name || sd.contractor_address)
                );
                // Only Company Driver statements show Mileage. Prefer the scheduled
                // deduction the user actually picked at Step 2 — for drivers with multiple
                // scheduled deductions (e.g. Contractor + Company Driver) this is the only
                // signal that distinguishes which type of statement is being made.
                const driverBucket = (() => {
                  const selectedSd = availableScheduledDeductions.find(sd => selectedScheduledDeductions.includes(sd.id));
                  if (selectedSd?.lease_or_rent) {
                    const b = bucketDriverType(selectedSd.lease_or_rent);
                    if (b !== 'other') return b;
                  }
                  const leaseInfo = driverLeaseTypeMap[(selectedDriver || '').trim().toLowerCase()];
                  if (leaseInfo) {
                    const b = bucketDriverType(leaseInfo.lease_or_rent);
                    if (b !== 'other') return b;
                  }
                  const driver = drivers.find(d => `${d.first_name} ${d.last_name}` === selectedDriver);
                  return bucketDriverType(driver?.driver_type);
                })();
                const hideMileage = driverBucket !== 'company_driver';
                return (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Statement Preview</h3>

                  {/* Unit override. Defaults to the driver's current truck
                      assignment; anything typed here applies to this statement
                      only and is what gets saved and printed. */}
                  <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 print:hidden">
                    <label htmlFor="statement-truck-number" className="text-sm font-medium text-gray-700">
                      Truck / Unit #
                    </label>
                    <input
                      id="statement-truck-number"
                      type="text"
                      value={displayTruck}
                      onChange={(e) => setTruckNumberOverride(e.target.value)}
                      placeholder="Unit #"
                      className="w-40 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {truckNumberOverride !== null && (
                      <button
                        type="button"
                        onClick={() => setTruckNumberOverride(null)}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        Reset to assigned ({selectedTruck || 'Not assigned'})
                      </button>
                    )}
                    <span className="text-xs text-gray-500">Applies to this statement only.</span>
                  </div>

                  <div className="space-y-6 bg-white p-4">
                    {/* Statement Header */}
                    <div className="flex items-start justify-between gap-6 mb-6 pb-4 border-b-2 border-gray-300">
                      {/* Pay-to block (contractor) — only shown for contractor statements */}
                      <div className="flex-1">
                        {contractorDeduction ? (
                          <>
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Pay To</p>
                            {contractorDeduction.contractor_name && (
                              <p className="text-lg font-bold">{contractorDeduction.contractor_name}</p>
                            )}
                            {contractorDeduction.contractor_address && (
                              <p className="text-sm whitespace-pre-line">{contractorDeduction.contractor_address}</p>
                            )}
                            <p className="text-sm mt-2">
                              <span className="text-gray-500">Driver:</span> <span className="font-bold">{selectedDriver}</span>
                            </p>
                            <p className="text-sm">
                              <span className="text-gray-500">Unit:</span> <span className="font-bold">#{displayTruck}</span>
                            </p>
                          </>
                        ) : null}
                      </div>

                      {/* Statement Info — Statement #/date are de-emphasized;
                          driver and unit are the primary identifiers. */}
                      <div className="text-right">
                        <p className="text-sm text-gray-500 mb-1">Statement #{Math.floor(Math.random() * 90000) + 10000}</p>
                        <p className="text-xs text-gray-500">{fmtDate(mondayThrough(weekEndDate))}</p>
                        {!contractorDeduction && (
                          <>
                            <p className="text-base font-bold mt-2">Unit #{displayTruck}</p>
                            <p className="text-base font-bold">{selectedDriver}</p>
                            {!hideMileage && (() => {
                              // Company-driver pay rate under the name — mirrors
                              // the PDF's "PER MILE $0.65" header line.
                              const cv = parseFloat(driverCommission?.replace('%', '') || '0');
                              const isPct = driverCommission?.includes('%') || cv > 100;
                              const rate = !isPct && cv > 0 ? (cv >= 10 ? cv / 100 : cv) : null;
                              return rate !== null
                                ? <p className="text-sm font-bold">PER MILE ${rate.toFixed(2)}</p>
                                : null;
                            })()}
                          </>
                        )}
                      </div>
                    </div>
                    {/* Loads Table */}
                    <div>
                      <h4 className="text-base mb-2">Loads :</h4>
                      <table className="w-full border-collapse border border-gray-400 mt-2">
                        <thead className="bg-gray-200">
                          <tr>
                            <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Date</th>
                            <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Load No.</th>
                            <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                            {!hideMileage && (
                              <>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Loaded</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Empty</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Mileage</th>
                              </>
                            )}
                            {/* One rate-amount column either way: the editable freight
                                amount on percentage statements (the company's cut shows up
                                as a separate Deductions line), the calculated driver amount
                                otherwise. */}
                            <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-24">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableLoads.filter(l => selectedLoads.includes(l.id)).map(load => {
                            const { loaded: loadedMiles, empty: emptyMiles, total: totalMiles } = getLoadMiles(load);
                            const driverPay = load.dispatches?.[0]?.driver_pay || 0;
                            // Use edited freight amount if available, otherwise use driver_pay or total_rate
                            const freightAmount = editedFreightAmounts[load.id] ?? (driverPay || load.total_rate || 0);
                            // Apply commission to freight amount (uses total miles = loaded + empty for CPM calculation)
                            const driverAmount = calculateDriverAmount(freightAmount, totalMiles, driverCommission);
                            const commissionValue = parseFloat(driverCommission?.replace('%', '') || '0');
                            const isPercentage = driverCommission?.includes('%') || commissionValue > 100;

                            return (
                              <tr key={load.id}>
                                <td className="border border-gray-400 px-2 py-1 text-sm">{load.pickup_date ? fmtDate(load.pickup_date) : 'N/A'}</td>
                                <td className="border border-gray-400 px-2 py-1 text-sm">{load.load_number}</td>
                                <td className="border border-gray-400 px-2 py-1 text-sm">
                                  <input
                                    type="text"
                                    value={getLoadDescriptionDraft(load).toUpperCase()}
                                    maxLength={LOAD_DESCRIPTION_MAX_CHARS}
                                    onChange={(e) => setEditedDescription({
                                      ...editedDescription,
                                      [load.id]: e.target.value.toUpperCase()
                                    })}
                                    className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1"
                                  />
                                </td>
                                {!hideMileage && (
                                  <>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                      <input
                                        type="number"
                                        value={loadedMiles}
                                        onChange={(e) => setEditedLoadedMiles({
                                          ...editedLoadedMiles,
                                          [load.id]: parseInt(e.target.value) || 0
                                        })}
                                        className="w-full text-right border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                      />
                                    </td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                      <input
                                        type="number"
                                        value={emptyMiles}
                                        onChange={(e) => setEditedEmptyMiles({
                                          ...editedEmptyMiles,
                                          [load.id]: parseInt(e.target.value) || 0
                                        })}
                                        className="w-full text-right border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                      />
                                    </td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right font-medium">
                                      <input
                                        type="number"
                                        value={totalMiles}
                                        onChange={(e) => {
                                          const v = parseInt(e.target.value) || 0;
                                          // Editing Total clears explicit loaded/empty overrides
                                          // so the row falls back to "total only" mode.
                                          const nextLoaded = { ...editedLoadedMiles };
                                          const nextEmpty = { ...editedEmptyMiles };
                                          delete nextLoaded[load.id];
                                          delete nextEmpty[load.id];
                                          setEditedLoadedMiles(nextLoaded);
                                          setEditedEmptyMiles(nextEmpty);
                                          setEditedMileage({ ...editedMileage, [load.id]: v });
                                        }}
                                        className="w-full text-right border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                      />
                                    </td>
                                  </>
                                )}
                                {isPercentage && (
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                    <input
                                      type="number"
                                      value={editedFreightAmounts[load.id] ?? (load.dispatches?.[0]?.driver_pay || load.total_rate || 0)}
                                      onChange={(e) => setEditedFreightAmounts({
                                        ...editedFreightAmounts,
                                        [load.id]: parseFloat(e.target.value) || 0
                                      })}
                                      className="w-full text-right border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1"
                                      step="0.01"
                                    />
                                  </td>
                                )}
                                {!isPercentage && (
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right font-semibold">${driverAmount.toFixed(2)}</td>
                                )}
                              </tr>
                            );
                          })}
                          <tr className="bg-gray-100">
                            <td colSpan={3} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                            {!hideMileage && (
                              <>
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                  {availableLoads.filter(l => selectedLoads.includes(l.id)).reduce((sum, l) => sum + getLoadMiles(l).loaded, 0)}
                                </td>
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                  {availableLoads.filter(l => selectedLoads.includes(l.id)).reduce((sum, l) => sum + getLoadMiles(l).empty, 0)}
                                </td>
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                  {availableLoads.filter(l => selectedLoads.includes(l.id)).reduce((sum, l) => sum + getLoadMiles(l).total, 0)}
                                </td>
                              </>
                            )}
                            {(() => {
                              const commissionValue = parseFloat(driverCommission?.replace('%', '') || '0');
                              const isPercentage = driverCommission?.includes('%') || commissionValue > 100;
                              return isPercentage && (
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                  ${availableLoads.filter(l => selectedLoads.includes(l.id)).reduce((sum, l) => {
                                    const driverPay = l.dispatches?.[0]?.driver_pay || 0;
                                    const freightAmount = editedFreightAmounts[l.id] ?? (driverPay || l.total_rate || 0);
                                    return sum + freightAmount;
                                  }, 0).toFixed(2)}
                                </td>
                              );
                            })()}
                            {(() => {
                              const commissionValue = parseFloat(driverCommission?.replace('%', '') || '0');
                              const isPercentage = driverCommission?.includes('%') || commissionValue > 100;
                              return !isPercentage && (
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                  ${availableLoads.filter(l => selectedLoads.includes(l.id)).reduce((sum, l) => {
                                    const totalMi = getLoadMiles(l).total;
                                    const driverPay = l.dispatches?.[0]?.driver_pay || 0;
                                    const freightAmount = editedFreightAmounts[l.id] ?? (driverPay || l.total_rate || 0);
                                    const driverAmount = calculateDriverAmount(freightAmount, totalMi, driverCommission);
                                    return sum + driverAmount;
                                  }, 0).toFixed(2)}
                                </td>
                              );
                            })()}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Deductions Table — scheduled deductions render first, then ad-hoc deductions and any service fee, single combined total */}
                    {(() => {
                      const commissionValue = parseFloat(driverCommission?.replace('%', '') || '0');
                      const isPercentage = driverCommission?.includes('%') || commissionValue > 100;
                      const hasServiceFee = isPercentage && commissionValue > 0 && selectedLoads.length > 0;
                      const hasScheduled = selectedScheduledDeductions.length > 0;
                      if (selectedDeductions.length === 0 && !hasServiceFee && !hasScheduled) return null;

                      // Company service fee: percent of freight taken by the company.
                      // The percent comes from the selected scheduled deduction's commission_cpm
                      // (e.g. "12%" → company keeps 12%, driver 88%).
                      const totalFreight = availableLoads
                        .filter(l => selectedLoads.includes(l.id))
                        .reduce((sum, l) => {
                          const driverPay = l.dispatches?.[0]?.driver_pay || 0;
                          const freightAmount = editedFreightAmounts[l.id] ?? (driverPay || l.total_rate || 0);
                          return sum + freightAmount;
                        }, 0);
                      const serviceFee = hasServiceFee ? totalFreight * (commissionValue / 100) : 0;

                      const customDeductionsTotal = availableDeductions
                        .filter(d => selectedDeductions.includes(d.id))
                        .reduce((sum, d) => sum + (deductionChargeAmounts[d.id] || d.amount), 0);

                      // Monthly-cadence helper for scheduled rows: only emit a monthly
                      // line if the configured charge-day Friday falls in the current week.
                      const getFridayOfMonth = (date: Date): string => {
                        const fridayNumber = Math.ceil(date.getDate() / 7);
                        const map: { [key: number]: string } = { 1: '1st_friday', 2: '2nd_friday', 3: '3rd_friday', 4: '4th_friday' };
                        return map[fridayNumber] || '4th_friday';
                      };
                      const shouldChargeMonthly = (chargeDay: string | undefined) => {
                        if (!chargeDay) return false;
                        const today = new Date();
                        const dayOfWeek = today.getDay();
                        let fridayThisWeek: Date;
                        if (dayOfWeek === 6) fridayThisWeek = new Date(today.getTime() - 86400000);
                        else if (dayOfWeek === 5) fridayThisWeek = today;
                        else if (dayOfWeek < 5) fridayThisWeek = new Date(today.getTime() + (5 - dayOfWeek) * 86400000);
                        else fridayThisWeek = new Date(today.getTime() + 5 * 86400000);
                        return getFridayOfMonth(fridayThisWeek) === chargeDay;
                      };

                      const scheduledTotal = availableScheduledDeductions
                        .filter(sd => selectedScheduledDeductions.includes(sd.id))
                        .reduce((sum, sd) => sum + getScheduledDeductionTotal(sd), 0);

                      const deductionsGrandTotal = customDeductionsTotal + serviceFee + scheduledTotal;

                      return (
                      <div>
                        <h4 className="text-base mb-2">Deductions :</h4>
                        <table className="w-full border-collapse border border-gray-400 mt-2">
                          <thead className="bg-gray-200">
                            <tr>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Date</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-24">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const rows: JSX.Element[] = [];
                              availableScheduledDeductions.filter(sd => selectedScheduledDeductions.includes(sd.id)).forEach(sd => {
                                // Pushed in the fixed display order (matches deductionSortRank):
                                // cargo, truck, ELD equipment, ELD weekly, escrow, then the rest.
                                if (sd.cargo > 0) rows.push(<tr key={`${sd.id}-cargo`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-cargo`] ?? 'Cargo & Liability insurance'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-cargo`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.cargo.toFixed(2)})</td></tr>);
                                if (sd.truck_weekly > 0) rows.push(<tr key={`${sd.id}-truck`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-truck`] ?? 'Truck payment'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-truck`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.truck_weekly.toFixed(2)})</td></tr>);
                                if (sd.truck_monthly > 0 && shouldChargeMonthly(sd.truck_monthly_charge_day)) rows.push(<tr key={`${sd.id}-truck-monthly`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-truck-monthly`] ?? `Truck monthly-${sd.lease_or_rent} for ${fmtDate(weekEndDate, { month: 'long' })}`} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-truck-monthly`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.truck_monthly.toFixed(2)})</td></tr>);
                                if (sd.eld_equipment_fee > 0) rows.push(<tr key={`${sd.id}-eld-equipment`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-eld-equipment`] ?? 'ELD equipment fee'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-eld-equipment`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.eld_equipment_fee.toFixed(2)})</td></tr>);
                                if (sd.eld_weekly > 0) rows.push(<tr key={`${sd.id}-eld`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-eld`] ?? 'ELD Weekly fee'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-eld`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.eld_weekly.toFixed(2)})</td></tr>);
                                if (sd.eld_monthly > 0 && shouldChargeMonthly(sd.eld_monthly_charge_day)) rows.push(<tr key={`${sd.id}-eld-monthly`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-eld-monthly`] ?? `ELD monthly for ${fmtDate(weekEndDate, { month: 'long' })}`} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-eld-monthly`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.eld_monthly.toFixed(2)})</td></tr>);
                                if ((sd.escrow ?? 0) > 0) rows.push(<tr key={`${sd.id}-escrow`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-escrow`] ?? 'Escrow'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-escrow`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.escrow.toFixed(2)})</td></tr>);
                                if (sd.trailer > 0) rows.push(<tr key={`${sd.id}-trailer`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-trailer`] ?? 'Trailer weekly rent'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-trailer`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.trailer.toFixed(2)})</td></tr>);
                                if (sd.physical_weekly > 0) rows.push(<tr key={`${sd.id}-physical`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-physical`] ?? 'Insurance Phys.Dem.'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-physical`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.physical_weekly.toFixed(2)})</td></tr>);
                                if (sd.physical_monthly > 0 && shouldChargeMonthly(sd.physical_monthly_charge_day)) rows.push(<tr key={`${sd.id}-physical-monthly`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-physical-monthly`] ?? `Physical for Truck monthly for ${fmtDate(weekEndDate, { month: 'long' })}`} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-physical-monthly`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.physical_monthly.toFixed(2)})</td></tr>);
                                if (sd.prepass_weekly > 0) rows.push(<tr key={`${sd.id}-prepass`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-prepass`] ?? 'Prepass Plus weekly'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-prepass`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.prepass_weekly.toFixed(2)})</td></tr>);
                                if (sd.prepass_monthly > 0 && shouldChargeMonthly(sd.prepass_monthly_charge_day)) rows.push(<tr key={`${sd.id}-prepass-monthly`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-prepass-monthly`] ?? `Prepass Plus monthly for ${fmtDate(weekEndDate, { month: 'long' })}`} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-prepass-monthly`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.prepass_monthly.toFixed(2)})</td></tr>);
                                if (sd.occupational_weekly > 0) rows.push(<tr key={`${sd.id}-occupational`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-occupational`] ?? 'Occupational insurance weekly'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-occupational`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.occupational_weekly.toFixed(2)})</td></tr>);
                                if (sd.occupational_monthly > 0 && shouldChargeMonthly(sd.occupational_monthly_charge_day)) rows.push(<tr key={`${sd.id}-occ-monthly`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-occ-monthly`] ?? `Occupational insurance monthly payment for ${fmtDate(weekEndDate, { month: 'long' })}`} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-occ-monthly`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.occupational_monthly.toFixed(2)})</td></tr>);
                                if (sd.highway_registration_weekly > 0) rows.push(<tr key={`${sd.id}-highway`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-highway`] ?? 'Highway tax weekly'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-highway`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.highway_registration_weekly.toFixed(2)})</td></tr>);
                                if (sd.fuel_card_fee > 0) rows.push(<tr key={`${sd.id}-fuel`}><td className="border border-gray-400 px-2 py-1 text-sm"><input type="text" value={editedScheduledDescriptions[`${sd.id}-fuel`] ?? 'Fuel Card fee'} onChange={(e) => setEditedScheduledDescriptions({...editedScheduledDescriptions, [`${sd.id}-fuel`]: e.target.value})} className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1" /></td><td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td><td className="border border-gray-400 px-2 py-1 text-sm text-right">(${sd.fuel_card_fee.toFixed(2)})</td></tr>);
                              });
                              return rows;
                            })()}
                            {hasServiceFee && (
                              <tr key="company-service-fee">
                                <td className="border border-gray-400 px-2 py-1 text-sm">
                                  Company service fee {commissionValue}%
                                </td>
                                <td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(weekEndDate)}</td>
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right">(${serviceFee.toFixed(2)})</td>
                              </tr>
                            )}
                            {availableDeductions.filter(d => selectedDeductions.includes(d.id)).map(deduction => {
                              const chargeAmount = deductionChargeAmounts[deduction.id] || deduction.amount;
                              return (
                                <tr key={deduction.id}>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">
                                    <input
                                      type="text"
                                      value={editedDeductionDescriptions[deduction.id] ?? deduction.description}
                                      onChange={(e) => setEditedDeductionDescriptions({
                                        ...editedDeductionDescriptions,
                                        [deduction.id]: e.target.value
                                      })}
                                      className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1"
                                    />
                                  </td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(deduction.deduction_date)}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">(${chargeAmount.toFixed(2)})</td>
                                </tr>
                              );
                            })}
                            <tr className="bg-gray-100">
                              <td colSpan={2} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                              <td className="border border-gray-400 px-2 py-1 text-sm text-right text-red-600">
                                -${deductionsGrandTotal.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      );
                    })()}

                    {/* Maintenance Deductions Table */}
                    {selectedMaintenanceDeductions.length > 0 && (
                      <div>
                        <h4 className="text-base mb-2">Maintenance Deductions :</h4>
                        <table className="w-full border-collapse border border-gray-400 mt-2">
                          <thead className="bg-gray-200">
                            <tr>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Date</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-24">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {availableMaintenanceDeductions.filter(md => selectedMaintenanceDeductions.includes(md.id)).map(deduction => {
                              const chargeAmount = maintenanceChargeAmounts[deduction.id] || deduction.driver_remaining_amount || 0;
                              return (
                                <tr key={deduction.id}>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">
                                    {deduction.maintenance_type} - {deduction.log_number}
                                  </td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(deduction.service_date)}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">(${chargeAmount.toFixed(2)})</td>
                                </tr>
                              );
                            })}
                            <tr className="bg-gray-100">
                              <td colSpan={2} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                              <td className="border border-gray-400 px-2 py-1 text-sm text-right text-red-600">
                                -${availableMaintenanceDeductions.filter(md => selectedMaintenanceDeductions.includes(md.id)).reduce((sum, md) => {
                                  const chargeAmount = maintenanceChargeAmounts[md.id] || md.driver_remaining_amount || 0;
                                  return sum + chargeAmount;
                                }, 0).toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Credits Table */}
                    {selectedCredits.length > 0 && (
                      <div>
                        <h4 className="text-base mb-2">Credits :</h4>
                        <table className="w-full border-collapse border border-gray-400 mt-2">
                          <thead className="bg-gray-200">
                            <tr>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Date</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-24">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {availableCredits.filter(c => selectedCredits.includes(c.id)).map(credit => (
                              <tr key={credit.id}>
                                <td className="border border-gray-400 px-2 py-1 text-sm">
                                  <input
                                    type="text"
                                    value={editedCreditDescriptions[credit.id] ?? credit.reason}
                                    onChange={(e) => setEditedCreditDescriptions({
                                      ...editedCreditDescriptions,
                                      [credit.id]: e.target.value
                                    })}
                                    className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-blue-500 rounded px-1"
                                  />
                                </td>
                                <td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(credit.date)}</td>
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right text-green-600">${credit.amount.toFixed(2)}</td>
                              </tr>
                            ))}
                            <tr className="bg-gray-100">
                              <td colSpan={2} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                              <td className="border border-gray-400 px-2 py-1 text-sm text-right text-green-600">
                                +${availableCredits.filter(c => selectedCredits.includes(c.id)).reduce((sum, c) => sum + c.amount, 0).toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Check Amount — closes the summary; the Fuel detail follows it */}
                    <div className="flex justify-end mt-6">
                      <div className="border-2 border-gray-400 p-2">
                        <div className="flex justify-between gap-8">
                          <span className="font-bold">Check Amount:</span>
                          <span className="text-lg font-bold">
                            ${(() => {
                              const weekEnd = new Date(weekEndDate);
                              const getFridayOfMonth = (date: Date): string => {
                                const fridayNumber = Math.ceil(date.getDate() / 7);
                                const map: { [key: number]: string } = { 1: '1st_friday', 2: '2nd_friday', 3: '3rd_friday', 4: '4th_friday' };
                                return map[fridayNumber] || '4th_friday';
                              };
                              const shouldChargeMonthly = (chargeDay: string | undefined) => {
                                if (!chargeDay) return false;
                                // Check which week of the month TODAY is (when generating the statement)
                                const today = new Date();
                                const dayOfWeek = today.getDay();
                                let fridayThisWeek: Date;
                                if (dayOfWeek === 6) {
                                  // Today is Saturday, Friday was yesterday
                                  fridayThisWeek = new Date(today.getTime() - 86400000);
                                } else if (dayOfWeek === 5) {
                                  // Today is Friday
                                  fridayThisWeek = today;
                                } else if (dayOfWeek < 5) {
                                  // Before Friday this week
                                  const daysUntilFriday = 5 - dayOfWeek;
                                  fridayThisWeek = new Date(today.getTime() + daysUntilFriday * 86400000);
                                } else {
                                  // Sunday (0) - use next Friday
                                  fridayThisWeek = new Date(today.getTime() + 5 * 86400000);
                                }
                                return getFridayOfMonth(fridayThisWeek) === chargeDay;
                              };
                              
                              const totalRevenue = availableLoads.filter(l => selectedLoads.includes(l.id)).reduce((sum, l) => {
                                const totalMi = getLoadMiles(l).total;
                                const driverPay = l.dispatches?.[0]?.driver_pay || 0;
                                const freightAmount = editedFreightAmounts[l.id] ?? (driverPay || l.total_rate || 0);
                                const driverAmount = calculateDriverAmount(freightAmount, totalMi, driverCommission);
                                return sum + driverAmount;
                              }, 0);
                              const totalDeductions = availableDeductions.filter(d => selectedDeductions.includes(d.id)).reduce((sum, d) => {
                                const chargeAmount = deductionChargeAmounts[d.id] || d.amount;
                                return sum + chargeAmount;
                              }, 0);
                              const totalMaintenanceDeductions = availableMaintenanceDeductions.filter(md => selectedMaintenanceDeductions.includes(md.id)).reduce((sum, md) => {
                                const chargeAmount = maintenanceChargeAmounts[md.id] || md.driver_remaining_amount || 0;
                                return sum + chargeAmount;
                              }, 0);
                              const totalScheduled = availableScheduledDeductions.filter(sd => selectedScheduledDeductions.includes(sd.id)).reduce((sum, sd) => {
                                let total = sd.truck_weekly + sd.physical_weekly + sd.trailer + sd.cargo + (sd.escrow || 0) + sd.fuel_card_fee +
                                  (sd.eld_equipment_fee || 0) + sd.eld_weekly + sd.prepass_weekly + sd.occupational_weekly + sd.highway_registration_weekly;
                                if (sd.truck_monthly > 0 && shouldChargeMonthly(sd.truck_monthly_charge_day)) total += sd.truck_monthly;
                                if (sd.physical_monthly > 0 && shouldChargeMonthly(sd.physical_monthly_charge_day)) total += sd.physical_monthly;
                                if (sd.occupational_monthly > 0 && shouldChargeMonthly(sd.occupational_monthly_charge_day)) total += sd.occupational_monthly;
                                    if (sd.eld_monthly > 0 && shouldChargeMonthly(sd.eld_monthly_charge_day)) total += sd.eld_monthly;
                                    if (sd.prepass_monthly > 0 && shouldChargeMonthly(sd.prepass_monthly_charge_day)) total += sd.prepass_monthly;
                                return sum + total;
                              }, 0);
                              const totalFuel = fuelData.filter(f => selectedFuel.includes(f.id)).reduce((sum, f) => sum + (getFuelAmount(f)), 0);
                              const totalCredits = availableCredits.filter(c => selectedCredits.includes(c.id)).reduce((sum, c) => sum + c.amount, 0);

                              return (totalRevenue - totalDeductions - totalMaintenanceDeductions - totalScheduled - totalFuel + totalCredits).toFixed(2);
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Fuel Table — the last block of the statement, under the Check Amount */}
                    {fuelData.filter(f => selectedFuel.includes(f.id)).length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-base mb-2">Fuel :</h4>
                        <table className="w-full border-collapse border border-gray-400 mt-2">
                          <thead className="bg-gray-200">
                            <tr>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">City</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">St.</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Gallons</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Fuel ($)</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Advance ($)</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Misc. ($)</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Date</th>
                              <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fuelData.filter(f => selectedFuel.includes(f.id)).map((fuel, idx) => {
                              const fuelAmount = Math.abs(getFuelAmount(fuel));
                              return (
                                <tr key={idx}>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{fuel.product || 'Fuel'}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{fuel.city || ''}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{fuel.state || ''}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">{fuel.gallons?.toFixed(2) || '0.00'}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">${fuelAmount.toFixed(2)}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">$0.00</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">$0.00</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{fuel.date ? fmtDate(fuel.date) : fmtDate(fuel.transaction_date)}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">(${fuelAmount.toFixed(2)})</td>
                                </tr>
                              );
                            })}
                            <tr className="bg-gray-100">
                              <td colSpan={8} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                              <td className="border border-gray-400 px-2 py-1 text-sm text-right text-red-600">
                                -${fuelData.filter(f => selectedFuel.includes(f.id)).reduce((sum, f) => sum + Math.abs(getFuelAmount(f)), 0).toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                  </div>
                </div>
                );
              })()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              {currentStep < 8 ? (
                <button
                  onClick={nextStep}
                  disabled={currentStep === 1 && !selectedDriver}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={async () => {
                    if (isGeneratingStatement) return;
                    setIsGeneratingStatement(true);
                    try {
                      // Helper function to determine which Friday of the month a date is
                      const getFridayOfMonth = (date: Date): string => {
                        const fridayNumber = Math.ceil(date.getDate() / 7);
                        const map: { [key: number]: string } = { 1: '1st_friday', 2: '2nd_friday', 3: '3rd_friday', 4: '4th_friday' };
                        return map[fridayNumber] || '4th_friday';
                      };
                      
                      // Helper function to check if monthly deduction should be charged
                      const shouldChargeMonthly = (chargeDay: string | undefined, statementDate: Date): boolean => {
                        if (!chargeDay) return false;
                        // Check which week of the month TODAY is (when generating the statement)
                        const today = new Date();
                        const dayOfWeek = today.getDay();
                        let fridayThisWeek: Date;
                        if (dayOfWeek === 6) {
                          // Today is Saturday, Friday was yesterday
                          fridayThisWeek = new Date(today.getTime() - 86400000);
                        } else if (dayOfWeek === 5) {
                          // Today is Friday
                          fridayThisWeek = today;
                        } else if (dayOfWeek < 5) {
                          // Before Friday this week
                          const daysUntilFriday = 5 - dayOfWeek;
                          fridayThisWeek = new Date(today.getTime() + daysUntilFriday * 86400000);
                        } else {
                          // Sunday (0) - use next Friday
                          fridayThisWeek = new Date(today.getTime() + 5 * 86400000);
                        }
                        return getFridayOfMonth(fridayThisWeek) === chargeDay;
                      };
                      
                      // Calculate which week of the month this is
                      const weekStart = new Date(weekStartDate);
                      const weekEnd = new Date(weekEndDate);
                      const currentFridayOfMonth = getFridayOfMonth(weekEnd);
                      
                      console.log('Statement week end:', weekEndDate, 'Friday of month:', currentFridayOfMonth);

                      // For percentage commissions the trip "amount" stored on each
                      // line item is the FULL freight, and the company's cut is
                      // saved as a separate "Company service fee" deduction. For
                      // CPM/fixed commissions the trip amount is the driver's
                      // calculated pay (legacy shape).
                      const commissionRaw = driverCommission || '';
                      const commissionValue = parseFloat(commissionRaw.replace('%', '') || '0');
                      const isPercentage = commissionRaw.includes('%') || commissionValue > 100;

                      const totalFreight = availableLoads.filter(l => selectedLoads.includes(l.id)).reduce((sum, l) => {
                        const driverPay = l.dispatches?.[0]?.driver_pay || 0;
                        const freightAmount = editedFreightAmounts[l.id] ?? (driverPay || l.total_rate || 0);
                        return sum + freightAmount;
                      }, 0);
                      const totalDriverFromLoads = availableLoads.filter(l => selectedLoads.includes(l.id)).reduce((sum, l) => {
                        const totalMi = getLoadMiles(l).total;
                        const driverPay = l.dispatches?.[0]?.driver_pay || 0;
                        const freightAmount = editedFreightAmounts[l.id] ?? (driverPay || l.total_rate || 0);
                        return sum + calculateDriverAmount(freightAmount, totalMi, driverCommission);
                      }, 0);
                      const serviceFee = isPercentage && commissionValue > 0 ? totalFreight - totalDriverFromLoads : 0;

                      const totalRevenue = isPercentage ? totalFreight : totalDriverFromLoads;
                      const totalDeductions = serviceFee + availableDeductions.filter(d => selectedDeductions.includes(d.id)).reduce((sum, d) => {
                        const chargeAmount = deductionChargeAmounts[d.id] || d.amount;
                        return sum + chargeAmount;
                      }, 0) +
                        availableMaintenanceDeductions.filter(md => selectedMaintenanceDeductions.includes(md.id)).reduce((sum, md) => {
                          const chargeAmount = maintenanceChargeAmounts[md.id] || md.driver_remaining_amount || 0;
                          return sum + chargeAmount;
                        }, 0) +
                        availableScheduledDeductions.filter(sd => selectedScheduledDeductions.includes(sd.id)).reduce((sum, sd) => {
                          // Always include weekly deductions
                          let total = sd.truck_weekly + sd.physical_weekly + sd.trailer + sd.cargo + (sd.escrow || 0) + sd.fuel_card_fee +
                            (sd.eld_equipment_fee || 0) + sd.eld_weekly + sd.prepass_weekly + sd.occupational_weekly + sd.highway_registration_weekly;
                          
                          // Include monthly deductions based on their charge day
                          if (sd.truck_monthly > 0 && shouldChargeMonthly(sd.truck_monthly_charge_day, weekEnd)) {
                            total += sd.truck_monthly;
                          }
                          if (sd.physical_monthly > 0 && shouldChargeMonthly(sd.physical_monthly_charge_day, weekEnd)) {
                            total += sd.physical_monthly;
                          }
                          if (sd.occupational_monthly > 0 && shouldChargeMonthly(sd.occupational_monthly_charge_day, weekEnd)) {
                            total += sd.occupational_monthly;
                          }
                          if (sd.eld_monthly > 0 && shouldChargeMonthly(sd.eld_monthly_charge_day, weekEnd)) {
                            total += sd.eld_monthly;
                          }
                          if (sd.prepass_monthly > 0 && shouldChargeMonthly(sd.prepass_monthly_charge_day, weekEnd)) {
                            total += sd.prepass_monthly;
                          }
                          
                          return sum + total;
                        }, 0);
                      const totalFuel = fuelData.filter(f => selectedFuel.includes(f.id)).reduce((sum, f) => sum + (getFuelAmount(f)), 0);
                      const totalCredits = availableCredits.filter(c => selectedCredits.includes(c.id)).reduce((sum, c) => sum + c.amount, 0);
                      const netPay = totalRevenue - totalDeductions - totalFuel + totalCredits;

                      // Snapshot pay-to company name/address if any selected scheduled
                      // deduction has them set (contractor, owner, lease, rent).
                      const contractorSd = availableScheduledDeductions.find(
                        sd => selectedScheduledDeductions.includes(sd.id)
                          && (sd.contractor_name || sd.contractor_address)
                      );

                      // A unit typed at Step 8 wins. Otherwise past statements record
                      // the truck(s) that actually drove the selected loads — all
                      // distinct units, comma-separated when the driver switched trucks
                      // mid-week — and regular statements use the driver's current
                      // assignment. displayTruck implements all three rules, so the
                      // saved value always matches what the preview showed.
                      const statementTruckNumber = displayTruck;

                      // Create statement and get the ID
                      const { data: statementData, error: statementError } = await supabase
                        .from('driver_statements')
                        .insert([{
                          driver_name: selectedDriver,
                          team_id: selectedTeamId,
                          team_name: selectedTeamId ? selectedTeamName : null,
                          truck_number: statementTruckNumber,
                          week_start_date: weekStartDate,
                          week_end_date: weekEndDate,
                          total_revenue: totalRevenue,
                          total_deductions: totalDeductions,
                          total_credits: totalCredits,
                          total_fuel: totalFuel,
                          net_pay: netPay,
                          contractor_name: contractorSd?.contractor_name || null,
                          contractor_address: contractorSd?.contractor_address || null,
                        }])
                        .select()
                        .single();

                      if (statementError) throw statementError;
                      
                      const statementId = statementData?.id;

                      // Save all line items with edited values for PDF generation
                      if (statementId) {
                        const lineItems: any[] = [];

                        // Add load line items with edited mileage and freight amounts
                        availableLoads.filter(l => selectedLoads.includes(l.id)).forEach(load => {
                          const { loaded: loadedMiles, empty: emptyMiles, total: totalMiles } = getLoadMiles(load);
                          const driverPay = load.dispatches?.[0]?.driver_pay || 0;
                          const freightAmount = editedFreightAmounts[load.id] ?? (driverPay || load.total_rate || 0);
                          const driverAmount = calculateDriverAmount(freightAmount, totalMiles, driverCommission);

                          lineItems.push({
                            statement_id: statementId,
                            item_type: 'load',
                            item_id: load.id,
                            // The load-number prefix is what the PDF and the saved-statement
                            // preview strip back off before printing the description.
                            description: `${load.load_number} - ${getLoadDescription(load)}`,
                            // Statement rows are dated by PICKUP, never delivery.
                            date: load.pickup_date || null,
                            // For percentage commissions the row's amount IS freight
                            // (commission is shown separately as a deduction); for
                            // CPM/fixed it's the calculated driver pay.
                            amount: isPercentage ? freightAmount : driverAmount,
                            quantity: totalMiles,
                            metadata: {
                              freight_amount: freightAmount,
                              load_number: load.load_number,
                              commission_type: isPercentage ? 'percentage' : 'cpm',
                              commission_value: commissionValue,
                              empty_miles: emptyMiles,
                              loaded_miles: loadedMiles,
                            },
                          });
                        });

                        // Company service fee — stored as a deduction so it shows
                        // in the Deductions table on every render path. item_id is
                        // null because it has no underlying deduction row.
                        if (serviceFee > 0) {
                          lineItems.push({
                            statement_id: statementId,
                            item_type: 'deduction',
                            item_id: null,
                            description: `Company service fee ${commissionValue}%`,
                            date: weekEndDate,
                            amount: -serviceFee,
                            quantity: null,
                            metadata: {
                              kind: 'company_service_fee',
                              commission_percent: commissionValue,
                            },
                          });
                        }

                        // Add deduction line items with edited descriptions and partial charge amounts
                        availableDeductions.filter(d => selectedDeductions.includes(d.id)).forEach(deduction => {
                          // Extract raw UUID from prefixed ID (e.g., "custom-uuid" -> "uuid")
                          const rawId = deduction.id.includes('-')
                            ? deduction.id.split('-').slice(1).join('-')
                            : deduction.id;
                          const remainingAmount = deduction.remaining_amount !== undefined
                            ? deduction.remaining_amount
                            : deduction.amount;
                          const chargeAmount = deductionChargeAmounts[deduction.id] ?? remainingAmount;

                          lineItems.push({
                            statement_id: statementId,
                            item_type: 'deduction',
                            item_id: rawId,
                            description: editedDeductionDescriptions[deduction.id] ?? deduction.description,
                            date: deduction.deduction_date,
                            amount: -chargeAmount,
                            quantity: null,
                            metadata: { original_amount: deduction.amount }
                          });
                        });

                        // Add maintenance deduction line items with custom charge amounts
                        availableMaintenanceDeductions.filter(md => selectedMaintenanceDeductions.includes(md.id)).forEach(md => {
                          const chargeAmount = maintenanceChargeAmounts[md.id] || md.driver_remaining_amount || 0;
                          lineItems.push({
                            statement_id: statementId,
                            item_type: 'maintenance_deduction',
                            item_id: md.id,
                            description: `${md.maintenance_type} - ${md.log_number}`,
                            date: md.service_date,
                            amount: -chargeAmount,
                            quantity: null,
                            metadata: { 
                              log_number: md.log_number, 
                              unit: md.trucks?.unit_number || md.trailers?.unit_number,
                              charge_amount: chargeAmount,
                              total_amount: md.driver_payment_amount,
                              previously_charged: md.driver_charged_amount || 0
                            }
                          });
                        });

                        // Add scheduled deduction line items with edited descriptions
                        availableScheduledDeductions.filter(sd => selectedScheduledDeductions.includes(sd.id)).forEach(sd => {
                          if (sd.cargo > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-cargo`] ?? 'Cargo & Liability insurance',
                            date: weekEndDate,
                            amount: -sd.cargo,
                            quantity: null,
                            metadata: { deduction_type: 'cargo' }
                          });
                          if ((sd.escrow || 0) > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-escrow`] ?? 'Escrow',
                            date: weekEndDate,
                            amount: -sd.escrow,
                            quantity: null,
                            metadata: { deduction_type: 'escrow' }
                          });
                          if (sd.eld_equipment_fee > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-eld-equipment`] ?? 'ELD equipment fee',
                            date: weekEndDate,
                            amount: -sd.eld_equipment_fee,
                            quantity: null,
                            metadata: { deduction_type: 'eld_equipment_fee' }
                          });
                          if (sd.eld_weekly > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-eld`] ?? 'ELD Weekly fee',
                            date: weekEndDate,
                            amount: -sd.eld_weekly,
                            quantity: null,
                            metadata: { deduction_type: 'eld_weekly' }
                          });
                          if (sd.eld_monthly > 0 && shouldChargeMonthly(sd.eld_monthly_charge_day, weekEnd)) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-eld-monthly`] ?? `ELD monthly for ${fmtDate(weekEndDate, { month: 'long' })}`,
                            date: weekEndDate,
                            amount: -sd.eld_monthly,
                            quantity: null,
                            metadata: { deduction_type: 'eld_monthly' }
                          });
                          if (sd.fuel_card_fee > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-fuel`] ?? 'Fuel Card fee',
                            date: weekEndDate,
                            amount: -sd.fuel_card_fee,
                            quantity: null,
                            metadata: { deduction_type: 'fuel_card_fee' }
                          });
                          if (sd.highway_registration_weekly > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-highway`] ?? 'Highway tax weekly',
                            date: weekEndDate,
                            amount: -sd.highway_registration_weekly,
                            quantity: null,
                            metadata: { deduction_type: 'highway_registration_weekly' }
                          });
                          if (sd.occupational_weekly > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-occupational`] ?? 'Occupational insurance weekly',
                            date: weekEndDate,
                            amount: -sd.occupational_weekly,
                            quantity: null,
                            metadata: { deduction_type: 'occupational_weekly' }
                          });
                          if (sd.physical_weekly > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-physical`] ?? 'Insurance Phys.Dem.',
                            date: weekEndDate,
                            amount: -sd.physical_weekly,
                            quantity: null,
                            metadata: { deduction_type: 'physical_weekly' }
                          });
                          if (sd.prepass_weekly > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-prepass`] ?? 'Prepass Plus weekly',
                            date: weekEndDate,
                            amount: -sd.prepass_weekly,
                            quantity: null,
                            metadata: { deduction_type: 'prepass_weekly' }
                          });
                          if (sd.prepass_monthly > 0 && shouldChargeMonthly(sd.prepass_monthly_charge_day, weekEnd)) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-prepass-monthly`] ?? `Prepass Plus monthly for ${fmtDate(weekEndDate, { month: 'long' })}`,
                            date: weekEndDate,
                            amount: -sd.prepass_monthly,
                            quantity: null,
                            metadata: { deduction_type: 'prepass_monthly' }
                          });
                          if (sd.trailer > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-trailer`] ?? 'Trailer weekly rent',
                            date: weekEndDate,
                            amount: -sd.trailer,
                            quantity: null,
                            metadata: { deduction_type: 'trailer' }
                          });
                          if (sd.truck_weekly > 0) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-truck`] ?? 'Truck payment',
                            date: weekEndDate,
                            amount: -sd.truck_weekly,
                            quantity: null,
                            metadata: { deduction_type: 'truck_weekly' }
                          });
                          if (sd.truck_monthly > 0 && shouldChargeMonthly(sd.truck_monthly_charge_day, weekEnd)) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-truck-monthly`] ?? `Truck monthly-${sd.lease_or_rent} for ${fmtDate(weekEndDate, { month: 'long' })}`,
                            date: weekEndDate,
                            amount: -sd.truck_monthly,
                            quantity: null,
                            metadata: { deduction_type: 'truck_monthly' }
                          });
                          if (sd.physical_monthly > 0 && shouldChargeMonthly(sd.physical_monthly_charge_day, weekEnd)) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-physical-monthly`] ?? `Physical for Truck monthly for ${fmtDate(weekEndDate, { month: 'long' })}`,
                            date: weekEndDate,
                            amount: -sd.physical_monthly,
                            quantity: null,
                            metadata: { deduction_type: 'physical_monthly' }
                          });
                          if (sd.occupational_monthly > 0 && shouldChargeMonthly(sd.occupational_monthly_charge_day, weekEnd)) lineItems.push({
                            statement_id: statementId,
                            item_type: 'scheduled_deduction',
                            item_id: sd.id,
                            description: editedScheduledDescriptions[`${sd.id}-occ-monthly`] ?? `Occupational insurance monthly payment for ${fmtDate(weekEndDate, { month: 'long' })}`,
                            date: weekEndDate,
                            amount: -sd.occupational_monthly,
                            quantity: null,
                            metadata: { deduction_type: 'occupational_monthly' }
                          });
                        });

                        // Record monthly deduction history for tracking
                        const monthlyDeductionHistory: any[] = [];
                        availableScheduledDeductions.filter(sd => selectedScheduledDeductions.includes(sd.id)).forEach(sd => {
                          if (sd.truck_monthly > 0 && shouldChargeMonthly(sd.truck_monthly_charge_day, weekEnd)) {
                            monthlyDeductionHistory.push({
                              scheduled_deduction_id: sd.id,
                              driver_name: selectedDriver,
                              deduction_type: 'truck_monthly',
                              amount: sd.truck_monthly,
                              charge_day: sd.truck_monthly_charge_day,
                              statement_id: statementId,
                              charged_date: weekEndDate
                            });
                          }
                          if (sd.physical_monthly > 0 && shouldChargeMonthly(sd.physical_monthly_charge_day, weekEnd)) {
                            monthlyDeductionHistory.push({
                              scheduled_deduction_id: sd.id,
                              driver_name: selectedDriver,
                              deduction_type: 'physical_monthly',
                              amount: sd.physical_monthly,
                              charge_day: sd.physical_monthly_charge_day,
                              statement_id: statementId,
                              charged_date: weekEndDate
                            });
                          }
                          if (sd.occupational_monthly > 0 && shouldChargeMonthly(sd.occupational_monthly_charge_day, weekEnd)) {
                            monthlyDeductionHistory.push({
                              scheduled_deduction_id: sd.id,
                              driver_name: selectedDriver,
                              deduction_type: 'occupational_monthly',
                              amount: sd.occupational_monthly,
                              charge_day: sd.occupational_monthly_charge_day,
                              statement_id: statementId,
                              charged_date: weekEndDate
                            });
                          }
                          if (sd.eld_monthly > 0 && shouldChargeMonthly(sd.eld_monthly_charge_day, weekEnd)) {
                            monthlyDeductionHistory.push({
                              scheduled_deduction_id: sd.id,
                              driver_name: selectedDriver,
                              deduction_type: 'eld_monthly',
                              amount: sd.eld_monthly,
                              charge_day: sd.eld_monthly_charge_day,
                              statement_id: statementId,
                              charged_date: weekEndDate
                            });
                          }
                          if (sd.prepass_monthly > 0 && shouldChargeMonthly(sd.prepass_monthly_charge_day, weekEnd)) {
                            monthlyDeductionHistory.push({
                              scheduled_deduction_id: sd.id,
                              driver_name: selectedDriver,
                              deduction_type: 'prepass_monthly',
                              amount: sd.prepass_monthly,
                              charge_day: sd.prepass_monthly_charge_day,
                              statement_id: statementId,
                              charged_date: weekEndDate
                            });
                          }
                        });

                        // Add credit line items with edited descriptions
                        availableCredits.filter(c => selectedCredits.includes(c.id)).forEach(credit => {
                          lineItems.push({
                            statement_id: statementId,
                            item_type: 'credit',
                            item_id: credit.id,
                            description: editedCreditDescriptions[credit.id] ?? credit.reason,
                            date: credit.date,
                            amount: credit.amount,
                            quantity: null,
                            metadata: null
                          });
                        });

                        // Add selected fuel line items
                        fuelData.filter(f => selectedFuel.includes(f.id)).forEach(fuel => {
                          lineItems.push({
                            statement_id: statementId,
                            item_type: 'fuel',
                            item_id: null,
                            description: fuel.product || 'Fuel',
                            date: fuel.date || fuel.transaction_date,
                            amount: -(getFuelAmount(fuel)),
                            quantity: fuel.gallons,
                            metadata: { 
                              city: fuel.city, 
                              state: fuel.state, 
                              location: fuel.location,
                              product: fuel.product,
                              gallons: fuel.gallons,
                              invoice_amount: getFuelAmount(fuel),
                              amount: fuel.amount
                            }
                          });
                        });

                        // Insert all line items
                        console.log('Total line items to insert:', lineItems.length);
                        console.log('Scheduled deduction line items:', lineItems.filter(item => item.item_type === 'scheduled_deduction'));
                        
                        const { error: lineItemsError } = await supabase
                          .from('statement_line_items')
                          .insert(lineItems);

                        if (lineItemsError) {
                          console.error('Error saving line items:', lineItemsError);
                          throw lineItemsError;
                        }
                        
                        console.log('Successfully inserted', lineItems.length, 'line items');

                        // Mark selected fuel transactions as included in statement
                        if (selectedFuel.length > 0) {
                          const fuelIds = selectedFuel.filter(id => id);
                          if (fuelIds.length > 0) {
                            const { error: fuelError } = await supabase
                              .from('pilot_fuel_imports')
                              .update({ statement_id: statementId })
                              .in('id', fuelIds);
                            
                            if (fuelError) {
                              console.error('Error marking fuel transactions:', fuelError);
                              // Don't throw - this is not critical
                            }
                          }
                        }

                        // Insert monthly deduction history records
                        if (monthlyDeductionHistory.length > 0) {
                          const { error: historyError } = await supabase
                            .from('monthly_deduction_history')
                            .insert(monthlyDeductionHistory);

                          if (historyError) {
                            console.error('Error saving monthly deduction history:', historyError);
                            // Statement itself is saved — don't abort, but surface it
                            toast.error('Statement saved, but monthly charge history could not be recorded');
                          }
                        }
                      }

                      // Mark selected loads as included in statement
                      if (selectedLoads.length > 0) {
                        const { error: loadsError } = await supabase
                          .from('loads')
                          .update({ included_in_statement: true })
                          .in('id', selectedLoads);
                        
                        if (loadsError) throw loadsError;
                      }

                      // Update deductions with partial payment tracking
                      if (selectedDeductions.length > 0 && statementId) {
                        // Only update custom deductions (from driver_deductions table)
                        const customDeductionIds = selectedDeductions
                          .filter(id => id.startsWith('custom-'))
                          .map(id => id.replace('custom-', '')); // Extract raw UUID
                        
                        if (customDeductionIds.length > 0) {
                          // Update each deduction individually with charged amount
                          for (const deductionId of customDeductionIds) {
                            const deduction = availableDeductions.find(d => d.id === `custom-${deductionId}`);
                            if (!deduction) continue;
                            
                            const chargeAmount = deductionChargeAmounts[`custom-${deductionId}`] || deduction.amount;
                            const previouslyCharged = deduction.charged_amount || 0;
                            const newChargedAmount = previouslyCharged + chargeAmount;
                            const newRemainingAmount = deduction.amount - newChargedAmount;
                            
                            const updateData: any = {
                              charged_amount: newChargedAmount,
                              remaining_amount: newRemainingAmount,
                              statement_id: statementId
                            };
                            
                            // Only mark as paid if fully charged
                            if (newRemainingAmount <= 0) {
                              updateData.status = 'paid';
                              updateData.paid_date = new Date().toISOString();
                            } else {
                              updateData.status = 'pending';
                            }
                            
                            const { error: deductionError } = await supabase
                              .from('driver_deductions')
                              .update(updateData)
                              .eq('id', deductionId);
                            
                            if (deductionError) {
                              console.error('Error updating deduction:', deductionError);
                              throw deductionError;
                            }
                          }
                        }

                        // Reimbursement tables (hotel/lyft/airplane) have no partial-payment columns.
                        // Only flip driver_paid=true when the user actually charged the FULL remaining amount —
                        // otherwise leave it unpaid so the untouched balance can be charged on a future statement.
                        // Paid-to-date for partial reimbursements is derived from statement_line_items in the loader.
                        const fullyPaidBySource = (prefix: string) => selectedDeductions
                          .filter(id => id.startsWith(prefix))
                          .map(id => {
                            const deduction = availableDeductions.find(d => d.id === id);
                            if (!deduction) return null;
                            const remaining = deduction.remaining_amount ?? deduction.amount;
                            const charged = deductionChargeAmounts[id] ?? remaining;
                            return charged >= remaining ? id.replace(prefix, '') : null;
                          })
                          .filter((x): x is string => x !== null);

                        const hotelIds = fullyPaidBySource('hotel-');
                        if (hotelIds.length > 0) {
                          const { error: hotelError } = await supabase
                            .from('hotel_reimbursements')
                            .update({ driver_paid: true, statement_id: statementId })
                            .in('id', hotelIds);
                          if (hotelError) throw hotelError;
                        }

                        const lyftIds = fullyPaidBySource('lyft-');
                        if (lyftIds.length > 0) {
                          const { error: lyftError } = await supabase
                            .from('lyft_reimbursements')
                            .update({ driver_paid: true, statement_id: statementId })
                            .in('id', lyftIds);
                          if (lyftError) throw lyftError;
                        }

                        const airplaneIds = fullyPaidBySource('airplane-');
                        if (airplaneIds.length > 0) {
                          const { error: airplaneError } = await supabase
                            .from('airplane_reimbursements')
                            .update({ driver_paid: true, statement_id: statementId })
                            .in('id', airplaneIds);
                          if (airplaneError) throw airplaneError;
                        }
                      }

                      // Mark selected credits as USED with statement reference
                      if (selectedCredits.length > 0 && statementId) {
                        const { error: creditsError } = await supabase
                          .from('driver_credits')
                          .update({ 
                            status: 'used',
                            paid_date: new Date().toISOString(),
                            statement_id: statementId
                          })
                          .in('id', selectedCredits);
                        
                        if (creditsError) throw creditsError;
                      }

                      // Update maintenance deductions with charged amounts (supports partial payments)
                      if (selectedMaintenanceDeductions.length > 0) {
                        console.log('Updating maintenance deductions with charged amounts:', selectedMaintenanceDeductions);
                        
                        // Update each maintenance log individually with its specific charge amount
                        for (const maintenanceId of selectedMaintenanceDeductions) {
                          const maintenance = availableMaintenanceDeductions.find(md => md.id === maintenanceId);
                          if (!maintenance) continue;
                          
                          const chargeAmount = maintenanceChargeAmounts[maintenanceId] || maintenance.driver_remaining_amount || 0;
                          const previouslyCharged = maintenance.driver_charged_amount || 0;
                          const newChargedAmount = previouslyCharged + chargeAmount;
                          const newRemainingAmount = maintenance.driver_payment_amount - newChargedAmount;
                          
                          const { error: maintenanceError } = await supabase
                            .from('maintenance_logs')
                            .update({ 
                              driver_charged_amount: newChargedAmount,
                              driver_remaining_amount: Math.max(0, newRemainingAmount)
                              // driver_payment_status will be automatically updated by trigger
                            })
                            .eq('id', maintenanceId);
                          
                          if (maintenanceError) {
                            console.error('Error updating maintenance deduction:', maintenanceError);
                            throw maintenanceError;
                          }
                          
                          console.log(`Updated maintenance ${maintenanceId}: charged ${chargeAmount}, total charged: ${newChargedAmount}, remaining: ${newRemainingAmount}`);
                        }
                      }

                      // Generate PDF and save to Company Documents
                      if (statementId && statementData) {
                        try {
                          // Fetch the line items we just created
                          const { data: savedLineItems, error: fetchLineItemsError } = await supabase
                            .from('statement_line_items')
                            .select('*')
                            .eq('statement_id', statementId)
                            .order('item_type');

                          if (fetchLineItemsError) throw fetchLineItemsError;

                          // Generate PDF as blob
                          const pdfBlob = await generateStatementPDF(statementData, savedLineItems || [], false, selectedScheduledDeductions);
                          
                          if (pdfBlob) {
                            // Find or create folder structure
                            const folderId = await findOrCreateStatementFolder(selectedDriver, weekEndDate);
                            
                            if (folderId) {
                              // Name the saved document by the profile actually
                              // selected in Step 2 — the statement maps behind
                              // getStatementBucket don't include this brand-new
                              // statement yet.
                              const selectedSd = availableScheduledDeductions.find(
                                sd => selectedScheduledDeductions.includes(sd.id)
                              );
                              const filename = statementPdfFilename(
                                statementData,
                                bucketDriverType(selectedSd?.lease_or_rent)
                              );
                              const fileExt = 'pdf';
                              const storageFileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
                              const filePath = `${folderId}/${storageFileName}`;

                              // Upload to storage
                              const { error: uploadError } = await supabase.storage
                                .from('company-documents')
                                .upload(filePath, pdfBlob, {
                                  contentType: 'application/pdf',
                                  upsert: false
                                });

                              if (uploadError) {
                                console.error('Error uploading PDF to storage:', uploadError);
                                throw uploadError;
                              }

                              // Save document metadata to database
                              const { data: documentData, error: dbError } = await supabase
                                .from('documents')
                                .insert({
                                  name: filename,
                                  file_name: filename,
                                  file_path: filePath,
                                  file_size: pdfBlob.size,
                                  file_type: fileExt,
                                  mime_type: 'application/pdf',
                                  folder_id: folderId,
                                })
                                .select('id')
                                .single();

                              if (dbError) {
                                console.error('Error saving document metadata:', dbError);
                                throw dbError;
                              }

                              // Link document to statement
                              if (documentData?.id) {
                                const { error: updateError } = await supabase
                                  .from('driver_statements')
                                  .update({ document_id: documentData.id })
                                  .eq('id', statementId);

                                if (updateError) {
                                  console.error('Error linking document to statement:', updateError);
                                }
                              }

                              console.log('Statement PDF saved to Company Documents successfully');
                            } else {
                              console.warn('Could not create folder structure for statement');
                            }
                          }
                        } catch (docError) {
                          console.error('Error saving statement to documents:', docError);
                          // Don't fail the whole statement generation if document save fails
                        }
                      }

                      toast.success('Statement generated successfully!');
                      setShowGenerateModal(false);
                      loadStatements();
                      
                      // Reset form
                      setCurrentStep(1);
                      setSelectedDriver('');
                      setSelectedTeamId(null);
                      setSelectedTeamName('');
                      setSelectedTruck('');
                      setTruckNumberOverride(null);
                      setSelectedLoads([]);
                      setSelectedDeductions([]);
                      setSelectedMaintenanceDeductions([]);
                      setSelectedScheduledDeductions([]);
                      setSelectedCredits([]);
                      setSelectedFuel([]);
                    } catch (error) {
                      console.error('Error generating statement:', error);
                      toast.error('Failed to generate statement');
                    } finally {
                      setIsGeneratingStatement(false);
                    }
                  }}
                  disabled={isGeneratingStatement}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileText className="w-4 h-4" />
                  {isGeneratingStatement ? 'Generating...' : 'Generate Statement'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Settings Modal */}
      {showEmailSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Email Settings</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Review Email Address</label>
                <input
                  type="email"
                  value={draftReviewEmail}
                  onChange={(e) => setDraftReviewEmail(e.target.value)}
                  placeholder="review@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Statements sent for review will be emailed to this address</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowEmailSettings(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveReviewEmail}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && statementToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Delete Statement</h2>
              <p className="text-sm text-gray-600 mt-2">
                Are you sure you want to delete this statement?
              </p>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm"><strong>Driver:</strong> {statementToDelete.driver_name}</p>
                <p className="text-sm"><strong>Truck:</strong> {statementToDelete.truck_number}</p>
                <p className="text-sm"><strong>Week:</strong> {fmtDate(statementToDelete.week_start_date)} - {fmtDate(statementToDelete.week_end_date)}</p>
                <p className="text-sm"><strong>Net Pay:</strong> ${statementToDelete.net_pay.toFixed(2)}</p>
              </div>
              <p className="text-sm text-red-600 mt-3 font-semibold">
                ⚠️ This action cannot be undone! This will also delete all associated line items.
              </p>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setStatementToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteStatement}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Statement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewStatement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Statement Preview</h2>
                  <p className="text-blue-100 text-sm mt-1">
                    {previewStatement.driver_name} | Truck: {previewStatement.truck_number}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPreviewModal(false);
                    setPreviewStatement(null);
                  }}
                  className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {previewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Period: {fmtDate(previewStatement.week_start_date)} - {fmtDate(mondayThrough(previewStatement.week_end_date))}
                    {(() => {
                      // Company-driver pay rate — mirrors the PDF's
                      // "PER MILE $0.65" line under the driver name.
                      if (getStatementBucket(previewStatement) !== 'company_driver') return null;
                      const rate = cpmRateFromLoads(previewLineItems.filter(i => i.item_type === 'load'));
                      return rate !== null
                        ? <span className="ml-3 font-bold text-gray-800">PER MILE ${rate.toFixed(2)}</span>
                        : null;
                    })()}
                  </p>

                  {(previewStatement.contractor_name || previewStatement.contractor_address) && (
                    <div className="mb-4 pb-4 border-b-2 border-gray-300">
                      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Pay To</p>
                      {previewStatement.contractor_name && (
                        <p className="text-lg font-bold">{previewStatement.contractor_name}</p>
                      )}
                      {previewStatement.contractor_address && (
                        <p className="text-sm whitespace-pre-line">{previewStatement.contractor_address}</p>
                      )}
                      <p className="text-sm mt-2">
                        <span className="text-gray-500">Driver:</span> <span className="font-bold">{previewStatement.driver_name}</span>
                      </p>
                      <p className="text-sm">
                        <span className="text-gray-500">Unit:</span> <span className="font-bold">#{previewStatement.truck_number}</span>
                      </p>
                    </div>
                  )}

                  <div className="space-y-6 bg-white">
                    {/* Loads Table */}
                    {(() => {
                      const loads = previewLineItems
                        .filter(item => item.item_type === 'load')
                        .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
                      if (loads.length === 0) return null;
                      const view = deriveStatementView(previewStatement, previewLineItems);
                      const { lastColLabel, lastColValue } = view;
                      const hideMileage = getStatementBucket(previewStatement) === 'owner';
                      return (
                        <div>
                          <h4 className="text-base mb-2">Loads:</h4>
                          <table className="w-full border-collapse border border-gray-400 mt-2">
                            <thead className="bg-gray-200">
                              <tr>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Date</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Load No.</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                                {!hideMileage && (
                                  <>
                                    <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Loaded</th>
                                    <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Empty</th>
                                    <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Mileage</th>
                                  </>
                                )}
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-24">{lastColLabel}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loads.map(item => {
                                // Remove load number prefix from description, uppercase
                                const description = item.description.replace(/^[^\s]+ - /, '').toUpperCase();
                                const total = item.quantity || 0;
                                const empty = item.metadata?.empty_miles ?? 0;
                                const loaded = item.metadata?.loaded_miles ?? Math.max(0, total - empty);
                                return (
                                  <tr key={item.id}>
                                    <td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(item.date)}</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm">{item.metadata?.load_number || 'N/A'}</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm">{description}</td>
                                    {!hideMileage && (
                                      <>
                                        <td className="border border-gray-400 px-2 py-1 text-sm text-right">{loaded}</td>
                                        <td className="border border-gray-400 px-2 py-1 text-sm text-right">{empty}</td>
                                        <td className="border border-gray-400 px-2 py-1 text-sm text-right">{total}</td>
                                      </>
                                    )}
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right font-semibold">${lastColValue(item).toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                              <tr className="bg-gray-100">
                                <td colSpan={3} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                                {!hideMileage && (
                                  <>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                      {loads.reduce((sum, item) => {
                                        const t = item.quantity || 0;
                                        const e = item.metadata?.empty_miles ?? 0;
                                        return sum + (item.metadata?.loaded_miles ?? Math.max(0, t - e));
                                      }, 0)}
                                    </td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                      {loads.reduce((sum, item) => sum + (item.metadata?.empty_miles ?? 0), 0)}
                                    </td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                      {loads.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                                    </td>
                                  </>
                                )}
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right">
                                  ${loads.reduce((sum, item) => sum + lastColValue(item), 0).toFixed(2)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Deductions Table — scheduled deductions first, then ad-hoc/synthesized deductions, single combined total */}
                    {(() => {
                      const { effectiveDeductions } = deriveStatementView(previewStatement, previewLineItems);
                      const scheduled = previewLineItems.filter(item => item.item_type === 'scheduled_deduction');
                      const combined = [...scheduled, ...effectiveDeductions]
                        .sort((a, b) => deductionSortRank(a.description) - deductionSortRank(b.description));
                      return combined.length > 0 && (
                        <div>
                          <h4 className="text-base mb-2">Deductions:</h4>
                          <table className="w-full border-collapse border border-gray-400 mt-2">
                            <thead className="bg-gray-200">
                              <tr>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Date</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-24">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {combined.map(item => (
                                <tr key={item.id}>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(item.date)}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{item.description}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">-${Math.abs(item.amount).toFixed(2)}</td>
                                </tr>
                              ))}
                              <tr className="bg-gray-100">
                                <td colSpan={2} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right text-red-600">
                                  -${combined.reduce((sum, item) => sum + Math.abs(item.amount), 0).toFixed(2)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Maintenance Deductions Table */}
                    {(() => {
                      const maintenanceDeductions = previewLineItems.filter(item => item.item_type === 'maintenance_deduction');
                      return maintenanceDeductions.length > 0 && (
                        <div>
                          <h4 className="text-base mb-2">Maintenance Deductions:</h4>
                          <table className="w-full border-collapse border border-gray-400 mt-2">
                            <thead className="bg-gray-200">
                              <tr>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Date</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-24">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {maintenanceDeductions.map(item => (
                                <tr key={item.id}>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(item.date)}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{item.description}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">-${Math.abs(item.amount).toFixed(2)}</td>
                                </tr>
                              ))}
                              <tr className="bg-gray-100">
                                <td colSpan={2} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right text-red-600">
                                  -${maintenanceDeductions.reduce((sum, item) => sum + Math.abs(item.amount), 0).toFixed(2)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Credits Table */}
                    {(() => {
                      const credits = previewLineItems.filter(item => item.item_type === 'credit');
                      return credits.length > 0 && (
                        <div>
                          <h4 className="text-base mb-2">Credits:</h4>
                          <table className="w-full border-collapse border border-gray-400 mt-2">
                            <thead className="bg-gray-200">
                              <tr>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-20">Date</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal w-24">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {credits.map(item => (
                                <tr key={item.id}>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(item.date)}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm">{item.description}</td>
                                  <td className="border border-gray-400 px-2 py-1 text-sm text-right">${item.amount.toFixed(2)}</td>
                                </tr>
                              ))}
                              <tr className="bg-gray-100">
                                <td colSpan={2} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right text-green-600">
                                  +${credits.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Fuel Table — printed before the Summary, matching the PDF */}
                    {(() => {
                      const fuel = previewLineItems.filter(item => item.item_type === 'fuel');
                      return fuel.length > 0 && (
                        <div className="mt-6">
                          <h4 className="text-base mb-2">Fuel:</h4>
                          <table className="w-full border-collapse border border-gray-400 mt-2">
                            <thead className="bg-gray-200">
                              <tr>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Description</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">City</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">St.</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Gallons</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Fuel ($)</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Advance ($)</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Misc. ($)</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Date</th>
                                <th className="border border-gray-400 px-2 py-1 text-sm font-normal">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {fuel.map(item => {
                                const fuelAmount = Math.abs(item.metadata?.invoice_amount || item.metadata?.amount || item.amount);
                                return (
                                  <tr key={item.id}>
                                    <td className="border border-gray-400 px-2 py-1 text-sm">{item.metadata?.product || item.description || 'Fuel'}</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm">{item.metadata?.city || ''}</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm">{item.metadata?.state || ''}</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">{item.metadata?.gallons?.toFixed(2) || '0.00'}</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">${fuelAmount.toFixed(2)}</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">$0.00</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">$0.00</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm">{fmtDate(item.date)}</td>
                                    <td className="border border-gray-400 px-2 py-1 text-sm text-right">(${fuelAmount.toFixed(2)})</td>
                                  </tr>
                                );
                              })}
                              <tr className="bg-gray-100">
                                <td colSpan={8} className="border border-gray-400 px-2 py-1 text-sm text-right">Total:</td>
                                <td className="border border-gray-400 px-2 py-1 text-sm text-right text-red-600">
                                  -${fuel.reduce((sum, item) => sum + Math.abs(item.metadata?.invoice_amount || item.metadata?.amount || item.amount), 0).toFixed(2)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}

                    {/* Summary — the last block, after the Fuel detail, matching the PDF */}
                    <div className="flex justify-end mt-6">
                      <div className="border-2 border-gray-400 p-4 min-w-[300px]">
                        {(() => {
                          const { displayTotalRevenue, displayTotalDeductions } = deriveStatementView(previewStatement, previewLineItems);
                          return (
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="font-semibold">Total Revenue:</span>
                            <span className="font-semibold">${displayTotalRevenue.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-red-600">
                            <span className="font-semibold">Total Deductions:</span>
                            <span className="font-semibold">-${displayTotalDeductions.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span className="font-semibold">Total Credits:</span>
                            <span className="font-semibold">+${previewStatement.total_credits.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-red-600">
                            <span className="font-semibold">Total Fuel:</span>
                            <span className="font-semibold">-${previewStatement.total_fuel.toFixed(2)}</span>
                          </div>
                          <div className="border-t-2 border-gray-400 pt-2 mt-2">
                            <div className="flex justify-between">
                              <span className="text-lg font-bold">Check Amount:</span>
                              <span className={`text-lg font-bold ${previewStatement.net_pay >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                ${previewStatement.net_pay.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                          );
                        })()}
                      </div>
                    </div>

                  </div>

                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      onClick={() => {
                        setShowPreviewModal(false);
                        setPreviewStatement(null);
                        setPreviewLineItems([]);
                      }}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => {
                        if (previewStatement) {
                          handleDownloadStatement(previewStatement);
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Past Statement Week Selection Modal */}
      {showPastStatementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Select Week for Past Statement</h2>
              <button
                onClick={() => setShowPastStatementModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Select a week from the past 12 weeks to generate a statement. The dropdown shows Monday-Sunday week ranges.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Week <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={`${customWeekStart}|${customWeekEnd}`}
                    onChange={(e) => {
                      const [start, end] = e.target.value.split('|');
                      setCustomWeekStart(start);
                      setCustomWeekEnd(end);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {getPastWeeks().map((week, index) => (
                      <option key={index} value={`${week.start}|${week.end}`}>
                        {week.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Selected: {fmtDate(customWeekStart)} - {fmtDate(customWeekEnd)}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowPastStatementModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={startPastStatementGeneration}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Continue to Steps
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Deduction Modal */}
      {showAddDeductionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{editingDeductionId ? 'Edit Deduction' : 'Add Deduction'}</h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Driver (pre-filled, read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Driver
                </label>
                <input
                  type="text"
                  value={selectedDriver}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Deduction Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deduction Type
                </label>
                <input
                  type="text"
                  value={newDeductionType}
                  onChange={(e) => setNewDeductionType(e.target.value)}
                  placeholder="e.g., Hotel, Lyft, Airplane, Fuel, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount ($)
                </label>
                <input
                  type="number"
                  value={newDeductionAmount}
                  onChange={(e) => setNewDeductionAmount(e.target.value)}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={newDeductionDate}
                  onChange={(e) => setNewDeductionDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowAddDeductionModal(false);
                  setEditingDeductionId(null);
                  setNewDeductionType('');
                  setNewDeductionAmount('');
                  setNewDeductionDate(new Date().toISOString().split('T')[0]);
                }}
                disabled={addingDeduction}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDeduction}
                disabled={addingDeduction}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingDeduction ? 'Saving...' : editingDeductionId ? 'Save Changes' : 'Add Deduction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Credit Modal */}
      {showAddCreditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Credit</h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Driver (pre-filled, read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Driver Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={selectedDriver}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={newCreditDate}
                  onChange={(e) => setNewCreditDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newCreditReason}
                  onChange={(e) => setNewCreditReason(e.target.value)}
                  placeholder="e.g., Bonus, Referral, Adjustment"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={newCreditAmount}
                  onChange={(e) => setNewCreditAmount(e.target.value)}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={newCreditStatus}
                  onChange={(e) => setNewCreditStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowAddCreditModal(false);
                  setNewCreditReason('');
                  setNewCreditAmount('');
                  setNewCreditDate(new Date().toISOString().split('T')[0]);
                  setNewCreditStatus('pending');
                }}
                disabled={addingCredit}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCredit}
                disabled={addingCredit}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingCredit ? 'Adding...' : 'Add Credit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
